import type { TraitScores } from "@/lib/scoring";
import { CAREER_EXPANSION_SEEDS } from "./careersExpansion";
import { CAREER_EXPANSION_V2_SEEDS } from "./careersExpansion2";

export type MatchLabel = "Strong match" | "Good fit" | "Worth exploring";
export type Difficulty = "Low" | "Medium" | "High";
export type Competition = "Low" | "Moderate" | "High" | "Extremely High";

export interface Specialisation {
  title: string;
  shortDescription: string;
  typicalEntryRoute: string;
  salaryRange: string;
}

export type Category =
  | "Technology"
  | "Design and Creative"
  | "Marketing and Media"
  | "Business, Finance and Legal"
  | "Sales, Retail and Customer Service"
  | "Education, Science and Research"
  | "Healthcare and Care"
  | "Sport, Fitness and Wellbeing"
  | "Hospitality and Food"
  | "Skilled Trades"
  | "Engineering, Construction and Infrastructure"
  | "Transport and Logistics"
  | "Public Sector and Emergency Services"
  | "Self-Employed and Business Owner"
  // ---- 2026 expansion categories ----
  | "Aviation and Aerospace"
  | "Mental Health and Therapy"
  | "Green Energy and Sustainability"
  | "Logistics and Supply Chain"
  | "AI, Data and Automation"
  | "Beauty and Aesthetics"
  | "Maritime and Offshore";

/**
 * Tailored content shown on careers that can be done solo.
 * Used by both pure self-employment careers and hybrid careers
 * (employed OR self-employed) to surface the realistic numbers.
 */
export interface SoloMeta {
  /** Realistic upfront cost to start (tools, kit, marketing, registration). */
  startupCostGBP: string;
  /** How long until first paying client/customer is realistic. */
  timeToFirstClient: string;
  /** Honest income range in month one. Often £0 - low. */
  monthOneIncome: string;
  /** Realistic year-one full-time income range. */
  yearOneIncome: string;
  /** What you can earn once established and scaled. */
  scaledIncome: string;
  /** 3-5 honest gotchas / common ways people fail. */
  commonPitfalls: string[];
  /** 5 concrete actions to land your first paying clients. */
  firstFiveClients: string[];
  /** Tools, software, kit you'll actually need to buy. */
  toolsAndCosts: string[];
  /** UK legal/admin basics: UTR, insurance, VAT threshold, etc. */
  legalBasics: string[];
}

export interface Career {
  id: number;
  name: string;
  matchPercentage: number;
  matchLabel?: MatchLabel;
  category?: Category;
  shortDescription: string;
  whyItFits: string;
  stepByStep: string[];
  skillsNeeded: string[];
  incomePotential: string;
  risks: string;
  startingSalary?: string;
  midSalary?: string;
  seniorSalary?: string;
  timeToEntry?: string;
  degreeRequired?: string;
  difficulty?: Difficulty;
  competitionLevel?: Competition;
  workEnvironment?: string;
  stability?: string;
  specialisations?: Specialisation[];
  isAlternativePath?: boolean;
  /** Career can realistically be done as a self-employed/freelance route. Shows a "Can go solo" badge. */
  selfEmployable?: boolean;
  /** Career exists ONLY as a self-employed/business-owner path (no employed equivalent). */
  selfEmploymentOnly?: boolean;
  /** Tailored "going solo" content - startup cost, first clients, pitfalls. */
  soloMeta?: SoloMeta;
  /** Raw trait weights from the seed (people, creativity, income, freedom, structure). */
  traitWeights?: Partial<Record<"people" | "creativity" | "income" | "freedom" | "structure", number>>;
  /** Same-cluster careers a user might also enjoy (career names, resolved to slugs at render time). */
  similarCareers?: string[];
  /** Roles further up the ladder with higher earning potential. */
  higherProgressionCareers?: string[];
  /** Easier-to-enter alternatives that lead toward this career. */
  entryLevelAlternatives?: string[];
}

// =============================================================
// Trait weights: people, creativity, income, freedom, structure
// Values roughly -3..+3. Score = 50 + sum(traitScore * weight * 0.12)
// Final clamped 45..88.
// =============================================================
type TraitKey = "people" | "creativity" | "income" | "freedom" | "structure";
type Weights = Partial<Record<TraitKey, number>>;

export interface CareerSeed {
  name: string;
  category: Category;
  shortDescription: string;
  whyItFits: string;
  startingSalary: string;
  midSalary: string;
  seniorSalary: string;
  timeToEntry: string;
  degreeRequired: string;
  difficulty: Difficulty;
  competitionLevel: Competition;
  workEnvironment: string;
  stability: string;
  weights: Weights;
  specialisations: Specialisation[];
  skillsNeeded: string[];
  isAlternativePath?: boolean;
  selfEmployable?: boolean;
  selfEmploymentOnly?: boolean;
  soloMeta?: SoloMeta;
  similarCareers?: string[];
  higherProgressionCareers?: string[];
  entryLevelAlternatives?: string[];
}

export const careerSeeds: CareerSeed[] = [
  // ---------------- Technology ----------------
  {
    name: "Software Engineer",
    category: "Technology",
    shortDescription: "Build the apps, websites, and systems millions of people use every day.",
    whyItFits: "You like solving problems, thinking logically, and building things that work.",
    startingSalary: "£28,000 - £40,000",
    midSalary: "£50,000 - £75,000",
    seniorSalary: "£80,000 - £130,000+",
    timeToEntry: "1 - 4 years",
    degreeRequired: "Helpful but not required",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Office or remote, mostly desk-based",
    stability: "Strong demand across industries",
    weights: { structure: 2, creativity: 1, income: 2, freedom: 1, people: -1 },
    skillsNeeded: ["Logical thinking", "Problem solving", "Patience", "Continuous learning"],
    specialisations: [
      { title: "Frontend Developer", shortDescription: "Build the part of websites and apps users see and interact with.", typicalEntryRoute: "Self-taught + portfolio, bootcamp, or CS degree", salaryRange: "£28,000 - £80,000" },
      { title: "Backend Developer", shortDescription: "Build the servers, APIs, and databases that power apps behind the scenes.", typicalEntryRoute: "CS degree or bootcamp + projects", salaryRange: "£32,000 - £90,000" },
      { title: "Mobile Developer", shortDescription: "Build iOS and Android apps used on phones and tablets.", typicalEntryRoute: "Self-taught + published apps or CS degree", salaryRange: "£30,000 - £85,000" },
      { title: "Full Stack Developer", shortDescription: "Work across both frontend and backend - the whole stack.", typicalEntryRoute: "Bootcamp + portfolio or CS degree", salaryRange: "£32,000 - £90,000" },
      { title: "DevOps Engineer", shortDescription: "Automate deployment, infrastructure, and reliability of software systems.", typicalEntryRoute: "Move from dev or sysadmin role + cloud certifications", salaryRange: "£45,000 - £110,000" },
    ],
    selfEmployable: true,
  },
  {
    name: "Product Manager",
    category: "Technology",
    shortDescription: "Decide what a product should do, why, and when - working with designers, engineers, and customers.",
    whyItFits: "You can hold the big picture, talk to people, and make decisions under uncertainty.",
    startingSalary: "£40,000 - £55,000",
    midSalary: "£65,000 - £95,000",
    seniorSalary: "£100,000 - £160,000+",
    timeToEntry: "2 - 5 years (usually after another role)",
    degreeRequired: "Often expected, not strict",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Office or remote, lots of meetings",
    stability: "Strong in tech but cyclical",
    weights: { people: 3, structure: 2, income: 2, creativity: 1, freedom: 0 },
    skillsNeeded: ["Communication", "Prioritisation", "User empathy", "Data literacy"],
    specialisations: [
      { title: "Associate Product Manager", shortDescription: "Entry-level PM role, usually at larger tech companies.", typicalEntryRoute: "APM grad scheme or internal move", salaryRange: "£40,000 - £60,000" },
      { title: "Growth Product Manager", shortDescription: "Focus on acquisition, activation, and retention metrics.", typicalEntryRoute: "PM + analytics background", salaryRange: "£60,000 - £120,000" },
      { title: "Technical Product Manager", shortDescription: "PM for developer tools, APIs, and infrastructure products.", typicalEntryRoute: "Engineering background + PM skills", salaryRange: "£70,000 - £140,000" },
      { title: "Platform Product Manager", shortDescription: "Manage internal platforms used by other product teams.", typicalEntryRoute: "Senior PM with technical depth", salaryRange: "£80,000 - £150,000" },
    ],
  },
  {
    name: "Data Analyst",
    category: "Technology",
    shortDescription: "Find patterns in data and turn them into clear answers that help businesses decide.",
    whyItFits: "You enjoy digging into details and explaining what numbers actually mean.",
    startingSalary: "£26,000 - £35,000",
    midSalary: "£40,000 - £60,000",
    seniorSalary: "£70,000 - £100,000+",
    timeToEntry: "6 months - 2 years",
    degreeRequired: "Helpful, not required",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office or remote, desk-based",
    stability: "Growing demand across industries",
    weights: { structure: 3, income: 1, creativity: 0, people: 0, freedom: 0 },
    skillsNeeded: ["SQL", "Spreadsheets", "Critical thinking", "Clear communication"],
    specialisations: [
      { title: "Business Intelligence Analyst", shortDescription: "Build dashboards and reports for business teams.", typicalEntryRoute: "SQL + BI tool (Power BI / Tableau)", salaryRange: "£30,000 - £60,000" },
      { title: "Marketing Analyst", shortDescription: "Measure campaign performance and customer behaviour.", typicalEntryRoute: "Marketing or analytics background", salaryRange: "£28,000 - £55,000" },
      { title: "Product Analyst", shortDescription: "Help product teams understand how users behave.", typicalEntryRoute: "SQL + product context", salaryRange: "£35,000 - £70,000" },
      { title: "Data Scientist", shortDescription: "Build statistical and ML models to predict and explain.", typicalEntryRoute: "Stats / CS degree + Python", salaryRange: "£45,000 - £100,000" },
    ],
  },
  {
    name: "Cybersecurity Analyst",
    category: "Technology",
    shortDescription: "Protect organisations from cyber attacks by monitoring, investigating, and responding to threats.",
    whyItFits: "You're analytical, persistent, and like puzzles where the stakes are real.",
    startingSalary: "£28,000 - £40,000",
    midSalary: "£45,000 - £70,000",
    seniorSalary: "£80,000 - £140,000+",
    timeToEntry: "1 - 3 years (often via certifications)",
    degreeRequired: "Helpful, certifications matter more",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Office, remote, or SOC; sometimes shifts",
    stability: "Strong long-term demand",
    weights: { structure: 2, creativity: 1, income: 2, freedom: 1, people: -1 },
    skillsNeeded: ["Networking basics", "Curiosity", "Attention to detail", "Persistence"],
    specialisations: [
      { title: "SOC Analyst", shortDescription: "Monitor systems and respond to security alerts in a team.", typicalEntryRoute: "Entry certs (Sec+, CySA+)", salaryRange: "£28,000 - £55,000" },
      { title: "Penetration Tester", shortDescription: "Get paid to legally hack systems and find weaknesses.", typicalEntryRoute: "OSCP / hands-on labs", salaryRange: "£40,000 - £90,000" },
      { title: "Security Engineer", shortDescription: "Design and build secure systems and infrastructure.", typicalEntryRoute: "Move from dev or sysadmin", salaryRange: "£55,000 - £110,000" },
    ],
  },
  {
    name: "IT Support Technician",
    category: "Technology",
    shortDescription: "Help people fix laptops, software, and network issues - keeping a workplace running.",
    whyItFits: "You like solving practical problems quickly and helping people who are stuck.",
    startingSalary: "£20,000 - £26,000",
    midSalary: "£28,000 - £40,000",
    seniorSalary: "£45,000 - £60,000",
    timeToEntry: "0 - 12 months",
    degreeRequired: "Not required",
    difficulty: "Low",
    competitionLevel: "Low",
    workEnvironment: "Office, on-site, or remote helpdesk",
    stability: "Steady, broad demand",
    weights: { people: 1, structure: 2, income: 0, creativity: 0, freedom: 0 },
    skillsNeeded: ["Troubleshooting", "Patience", "Clear communication", "Basic networking"],
    specialisations: [
      { title: "1st Line Support", shortDescription: "First point of contact - tickets, password resets, basic fixes.", typicalEntryRoute: "Direct entry, CompTIA A+ helpful", salaryRange: "£20,000 - £30,000" },
      { title: "2nd / 3rd Line Support", shortDescription: "Handle escalated and more complex IT issues.", typicalEntryRoute: "1-3 years experience + certifications", salaryRange: "£28,000 - £45,000" },
      { title: "Systems Administrator", shortDescription: "Maintain servers, networks, and core IT infrastructure.", typicalEntryRoute: "Support background + Microsoft / Linux certs", salaryRange: "£35,000 - £60,000" },
    ],
  },

  // ---------------- Design and Creative ----------------
  {
    name: "UX Designer",
    category: "Design and Creative",
    shortDescription: "Design how users navigate apps and websites, working with product and engineering teams to improve usability.",
    whyItFits: "You enjoy structured problem-solving and care about how people experience things.",
    startingSalary: "£28,000 - £38,000",
    midSalary: "£45,000 - £65,000",
    seniorSalary: "£70,000 - £100,000+",
    timeToEntry: "1 - 2 years",
    degreeRequired: "Not required, portfolio matters most",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Office or remote, collaborative",
    stability: "Growing in tech and digital teams",
    weights: { creativity: 3, people: 2, structure: 1, freedom: 1, income: 0 },
    skillsNeeded: ["Figma", "User research", "Visual thinking", "Communication"],
    specialisations: [
      { title: "Product Designer", shortDescription: "Own the full design of a product feature end-to-end.", typicalEntryRoute: "UX background + portfolio", salaryRange: "£35,000 - £85,000" },
      { title: "UX Researcher", shortDescription: "Run interviews and tests to understand what users need.", typicalEntryRoute: "Psychology / HCI background helpful", salaryRange: "£35,000 - £75,000" },
      { title: "UI Designer", shortDescription: "Focus on the visual layer - look, feel, and components.", typicalEntryRoute: "Visual design portfolio", salaryRange: "£28,000 - £65,000" },
      { title: "Service Designer", shortDescription: "Design end-to-end experiences across digital and physical touchpoints.", typicalEntryRoute: "Senior design background", salaryRange: "£45,000 - £85,000" },
    ],
    selfEmployable: true,
  },
  {
    name: "Graphic Designer",
    category: "Design and Creative",
    shortDescription: "Create visual identity, layouts, and marketing materials for brands and publications.",
    whyItFits: "You think visually, like making things look right, and care about craft.",
    startingSalary: "£22,000 - £28,000",
    midSalary: "£30,000 - £45,000",
    seniorSalary: "£50,000 - £70,000",
    timeToEntry: "1 - 2 years",
    degreeRequired: "Not required, portfolio matters",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Studio, agency, in-house, or freelance",
    stability: "Competitive but broad demand",
    weights: { creativity: 3, freedom: 1, people: 0, structure: 0, income: -1 },
    skillsNeeded: ["Adobe Creative Suite", "Typography", "Layout", "Visual storytelling"],
    specialisations: [
      { title: "Brand Designer", shortDescription: "Build the visual identity of a brand - logos, systems, guidelines.", typicalEntryRoute: "Strong portfolio + agency or in-house", salaryRange: "£28,000 - £60,000" },
      { title: "Motion Designer", shortDescription: "Animate graphics for video, ads, and product UIs.", typicalEntryRoute: "After Effects portfolio", salaryRange: "£28,000 - £65,000" },
      { title: "Editorial Designer", shortDescription: "Design magazines, books, and long-form publications.", typicalEntryRoute: "Print design portfolio", salaryRange: "£25,000 - £50,000" },
    ],
    selfEmployable: true,
    soloMeta: {
      startupCostGBP: "£300 - £1,500 (laptop + Adobe + portfolio site)",
      timeToFirstClient: "2 - 8 weeks",
      monthOneIncome: "£300 - £1,800",
      yearOneIncome: "£20,000 - £40,000",
      scaledIncome: "£50,000 - £90,000+ with niche (brand identity, packaging, SaaS)",
      commonPitfalls: ["No niche - competing with the entire planet on Fiverr", "Endless free revisions", "Pricing per hour instead of per project / outcome"],
      firstFiveClients: ["Rebrand a friend's business for free in exchange for a case study", "Cold DM small businesses with weak branding", "Post on LinkedIn with before/after rebrands", "Join a Slack/Discord for founders", "Subcontract for a small agency"],
      toolsAndCosts: ["Adobe CC £30/mo", "Figma free", "Domain + portfolio £15/mo"],
      legalBasics: ["Sole trader or Ltd", "Professional indemnity insurance", "Written contracts with IP transfer clauses"],
    },
  },
  {
    name: "Video Editor",
    category: "Design and Creative",
    shortDescription: "Cut and craft footage into films, ads, social videos, and YouTube content.",
    whyItFits: "You're patient, story-minded, and enjoy hands-on creative work.",
    startingSalary: "£20,000 - £28,000",
    midSalary: "£30,000 - £50,000",
    seniorSalary: "£55,000 - £80,000",
    timeToEntry: "0 - 2 years",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Studio, agency, freelance, or remote",
    stability: "Project-based, growing in social/video",
    weights: { creativity: 3, freedom: 2, people: 0, structure: 0, income: -1 },
    skillsNeeded: ["Premiere Pro / DaVinci", "Pacing", "Sound design basics", "Storytelling"],
    specialisations: [
      { title: "Social Media Video Editor", shortDescription: "Cut short-form vertical videos for TikTok, Reels, Shorts.", typicalEntryRoute: "Build a reel + freelance", salaryRange: "£22,000 - £50,000" },
      { title: "Documentary Editor", shortDescription: "Shape long-form interview and observational footage.", typicalEntryRoute: "Assistant editor route", salaryRange: "£28,000 - £60,000" },
      { title: "Commercial Editor", shortDescription: "Edit ads and branded content for agencies and brands.", typicalEntryRoute: "Agency or post-house junior role", salaryRange: "£30,000 - £75,000" },
    ],
    selfEmployable: true,
    soloMeta: {
      startupCostGBP: "£800 - £3,000 (decent laptop + software)",
      timeToFirstClient: "2 - 8 weeks",
      monthOneIncome: "£400 - £2,000",
      yearOneIncome: "£22,000 - £45,000",
      scaledIncome: "£55,000 - £100,000+ editing for YouTubers, ads, or running a small studio",
      commonPitfalls: ["Editing for anyone instead of one niche (YT, ads, podcasts)", "No retainer clients - feast and famine", "Quoting per video without scoping revisions"],
      firstFiveClients: ["Edit one viral-ready clip for a creator for free as a sample", "Cold DM mid-tier YouTubers (10k-100k subs)", "Post breakdowns of your edits on Twitter/LinkedIn", "Join editor-for-hire Slack groups", "Subcontract for a busier editor"],
      toolsAndCosts: ["Premiere/DaVinci £20-50/mo", "External SSD £100", "Frame.io for review £15/mo"],
      legalBasics: ["Sole trader or Ltd", "Written contract per project", "Professional indemnity insurance"],
    },
  },
  {
    name: "Architect",
    category: "Design and Creative",
    shortDescription: "Design buildings and spaces, balancing aesthetics, function, regulations, and budget.",
    whyItFits: "You blend creativity with careful planning and long-term thinking.",
    startingSalary: "£24,000 - £32,000 (Part 1)",
    midSalary: "£38,000 - £55,000 (qualified)",
    seniorSalary: "£60,000 - £100,000+",
    timeToEntry: "7 years (qualification path)",
    degreeRequired: "Yes - RIBA Parts 1, 2, 3",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Studio + site visits",
    stability: "Steady but tied to construction cycles",
    weights: { creativity: 3, structure: 2, people: 1, income: 0, freedom: 0 },
    skillsNeeded: ["Drawing / CAD", "Spatial thinking", "Patience", "Project management"],
    specialisations: [
      { title: "Residential Architect", shortDescription: "Design homes, extensions, and housing developments.", typicalEntryRoute: "RIBA qualified + practice experience", salaryRange: "£32,000 - £70,000" },
      { title: "Commercial Architect", shortDescription: "Design offices, retail, and large public buildings.", typicalEntryRoute: "Large practice route", salaryRange: "£38,000 - £85,000" },
      { title: "Interior Architect", shortDescription: "Focus on internal spaces, materials, and finishes.", typicalEntryRoute: "Architecture or interior design degree", salaryRange: "£28,000 - £60,000" },
    ],
    selfEmployable: true,
  },

  // ---------------- Marketing and Media ----------------
  {
    name: "Marketing Executive",
    category: "Marketing and Media",
    shortDescription: "Plan and run campaigns that bring customers to a brand across digital and traditional channels.",
    whyItFits: "You're creative, curious about people, and like seeing measurable results.",
    startingSalary: "£24,000 - £32,000",
    midSalary: "£35,000 - £55,000",
    seniorSalary: "£60,000 - £90,000",
    timeToEntry: "0 - 2 years",
    degreeRequired: "Often expected",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Agency, in-house, or remote",
    stability: "Broad demand, cyclical with business spend",
    weights: { creativity: 2, people: 2, income: 1, structure: 1, freedom: 0 },
    skillsNeeded: ["Copywriting", "Analytics", "Project management", "Brand thinking"],
    specialisations: [
      { title: "Performance Marketer", shortDescription: "Run paid ads on Google, Meta, and TikTok to hit growth targets.", typicalEntryRoute: "Junior PPC role + certifications", salaryRange: "£28,000 - £80,000" },
      { title: "SEO Specialist", shortDescription: "Help websites rank in Google through content and technical fixes.", typicalEntryRoute: "Self-taught + agency role", salaryRange: "£28,000 - £70,000" },
      { title: "Content Marketer", shortDescription: "Plan and produce blog, social, and video content that attracts customers.", typicalEntryRoute: "Writing portfolio + junior role", salaryRange: "£26,000 - £60,000" },
      { title: "Email Marketer", shortDescription: "Build and run email programmes that drive sales and retention.", typicalEntryRoute: "Marketing role + ESP experience", salaryRange: "£28,000 - £65,000" },
    ],
  },
  {
    name: "Brand Manager",
    category: "Marketing and Media",
    shortDescription: "Own how a brand looks, sounds, and behaves across every customer touchpoint.",
    whyItFits: "You think strategically, care about story, and can lead cross-team work.",
    startingSalary: "£32,000 - £42,000",
    midSalary: "£50,000 - £75,000",
    seniorSalary: "£80,000 - £120,000+",
    timeToEntry: "3 - 5 Years (after marketing roles)",
    degreeRequired: "Often expected",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "In-house, often FMCG or tech",
    stability: "Steady at established brands",
    weights: { creativity: 2, people: 2, income: 2, structure: 1, freedom: 0 },
    skillsNeeded: ["Strategy", "Storytelling", "Leadership", "Commercial awareness"],
    specialisations: [
      { title: "Junior Brand Manager", shortDescription: "Support a senior on a specific brand or product line.", typicalEntryRoute: "Grad scheme route", salaryRange: "£30,000 - £45,000" },
      { title: "Senior Brand Manager", shortDescription: "Own a brand's P&L, positioning, and campaigns.", typicalEntryRoute: "5+ years marketing experience", salaryRange: "£55,000 - £95,000" },
    ],
  },
  {
    name: "Social Media Manager",
    category: "Marketing and Media",
    shortDescription: "Run a brand's social channels - strategy, content, and community.",
    whyItFits: "You spot trends fast, write well, and understand what makes people share.",
    startingSalary: "£24,000 - £30,000",
    midSalary: "£32,000 - £50,000",
    seniorSalary: "£55,000 - £80,000",
    timeToEntry: "0 - 2 years",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Agency, in-house, or remote",
    stability: "Growing demand, content-heavy",
    weights: { creativity: 3, people: 2, freedom: 1, income: 0, structure: 0 },
    skillsNeeded: ["Copywriting", "Trend awareness", "Editing", "Community management"],
    specialisations: [
      { title: "Community Manager", shortDescription: "Engage and grow online communities for a brand or product.", typicalEntryRoute: "Direct entry + portfolio", salaryRange: "£24,000 - £45,000" },
      { title: "Influencer Marketing Manager", shortDescription: "Plan and run partnerships with creators.", typicalEntryRoute: "Social or PR background", salaryRange: "£30,000 - £60,000" },
    ],
    selfEmployable: true,
    soloMeta: {
      startupCostGBP: "£200 - £1,500 (laptop, Canva Pro, scheduler)",
      timeToFirstClient: "2 - 8 weeks",
      monthOneIncome: "£0 - £2,000",
      yearOneIncome: "£18,000 - £45,000",
      scaledIncome: "£40,000 - £100,000+ with retainers or productised offers",
      commonPitfalls: [
        "Charging hourly - the most underpaid corner of freelancing",
        "Promising follower growth - vanity metrics lose clients in month 3",
        "Taking 8 small clients on £400/mo each instead of 3 on £1,500 - admin kills you",
        "Posting and ghosting - clients pay for community management too",
      ],
      firstFiveClients: [
        "Build YOUR OWN account in your target niche to 5k followers as live proof",
        "Productise one offer ('TikTok content engine, £1,500/mo, 12 posts + 3 reels')",
        "Cold-email 50 small businesses with personalised content audits",
        "Reach out to 10 marketing agencies for overflow social capacity",
        "Run a free 7-day social audit for one local business to seed first case study",
      ],
      toolsAndCosts: [
        "Canva Pro: £11/month",
        "Later, Buffer or Metricool: £15-45/month",
        "Capcut: free",
        "Notion or Trello: free-£10/month",
      ],
      legalBasics: [
        "Sole trader or Ltd",
        "Written contracts: minimum 3-month engagement, kill fee for early exit",
        "Account access via delegated permissions, never shared logins",
        "GDPR: never store client login credentials in plain text",
      ],
    },
  },
  {
    name: "PR Officer",
    category: "Marketing and Media",
    shortDescription: "Shape how a brand or person is talked about in press, podcasts, and online.",
    whyItFits: "You write clearly, network easily, and stay calm under pressure.",
    startingSalary: "£22,000 - £28,000",
    midSalary: "£32,000 - £50,000",
    seniorSalary: "£55,000 - £85,000",
    timeToEntry: "0 - 2 years",
    degreeRequired: "Often expected",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Agency or in-house, deadline-driven",
    stability: "Steady at large agencies",
    weights: { creativity: 2, people: 3, income: 1, structure: 0, freedom: 0 },
    skillsNeeded: ["Writing", "Relationship building", "Storytelling", "Resilience"],
    specialisations: [
      { title: "Consumer PR", shortDescription: "Promote brands and products to the public through media.", typicalEntryRoute: "Agency junior role", salaryRange: "£24,000 - £55,000" },
      { title: "Corporate Comms", shortDescription: "Manage internal and external communications for a company.", typicalEntryRoute: "PR or journalism background", salaryRange: "£32,000 - £80,000" },
    ],
  },
  {
    name: "Journalist",
    category: "Marketing and Media",
    shortDescription: "Research, interview, and write stories for newspapers, websites, or broadcasters.",
    whyItFits: "You're curious, like writing, and can chase a story with persistence.",
    startingSalary: "£20,000 - £28,000",
    midSalary: "£30,000 - £45,000",
    seniorSalary: "£50,000 - £80,000",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Often expected (NCTJ helpful)",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Newsroom, remote, or out reporting",
    stability: "Industry under pressure but specialist niches strong",
    weights: { creativity: 2, people: 2, freedom: 1, structure: 0, income: -1 },
    skillsNeeded: ["Writing", "Research", "Interviewing", "Speed under deadline"],
    specialisations: [
      { title: "News Reporter", shortDescription: "Cover daily news for a publication or broadcaster.", typicalEntryRoute: "Local news role + NCTJ", salaryRange: "£20,000 - £45,000" },
      { title: "Feature Writer", shortDescription: "Write longer, in-depth pieces.", typicalEntryRoute: "Build clips + pitch editors", salaryRange: "£25,000 - £55,000" },
      { title: "Sports Journalist", shortDescription: "Report on sport across print, online, or broadcast.", typicalEntryRoute: "Sports desk junior + NCTJ", salaryRange: "£22,000 - £60,000" },
    ],
    selfEmployable: true,
  },

  // ---------------- Business and Operations ----------------
  {
    name: "Management Consultant",
    category: "Business, Finance and Legal",
    shortDescription: "Help organisations solve business problems - strategy, operations, transformation.",
    whyItFits: "You think structurally, present well, and like complex problems.",
    startingSalary: "£35,000 - £50,000",
    midSalary: "£60,000 - £90,000",
    seniorSalary: "£100,000 - £200,000+",
    timeToEntry: "Grad scheme to 3 years",
    degreeRequired: "Usually expected",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Client sites + travel + office",
    stability: "Strong but demanding hours",
    weights: { income: 3, structure: 2, people: 2, creativity: 0, freedom: -1 },
    skillsNeeded: ["Structured thinking", "Presenting", "Excel modelling", "Stakeholder management"],
    specialisations: [
      { title: "Strategy Consultant", shortDescription: "Advise leadership on big-picture business decisions.", typicalEntryRoute: "Top-tier firm grad scheme", salaryRange: "£45,000 - £200,000" },
      { title: "Operations Consultant", shortDescription: "Improve how businesses actually run day to day.", typicalEntryRoute: "Big 4 grad scheme", salaryRange: "£35,000 - £120,000" },
      { title: "Tech Consultant", shortDescription: "Help businesses adopt and use new technology.", typicalEntryRoute: "Tech or business background", salaryRange: "£35,000 - £130,000" },
    ],
    selfEmployable: true,
  },
  {
    name: "Operations Manager",
    category: "Business, Finance and Legal",
    shortDescription: "Keep a team, site, or function running smoothly day to day.",
    whyItFits: "You're organised, calm under pressure, and good at making processes work.",
    startingSalary: "£28,000 - £38,000",
    midSalary: "£45,000 - £70,000",
    seniorSalary: "£80,000 - £130,000+",
    timeToEntry: "3 - 7 years (often via team lead route)",
    degreeRequired: "Helpful, not required",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office, warehouse, or site - varies",
    stability: "Strong, present in every industry",
    weights: { structure: 3, people: 2, income: 1, creativity: 0, freedom: -1 },
    skillsNeeded: ["Planning", "People management", "Process improvement", "Problem solving"],
    specialisations: [
      { title: "Logistics Manager", shortDescription: "Plan and oversee how goods move through a business.", typicalEntryRoute: "Move up from supervisor / coordinator", salaryRange: "£32,000 - £65,000" },
      { title: "Site / Branch Manager", shortDescription: "Run a single location end-to-end.", typicalEntryRoute: "Internal promotion", salaryRange: "£30,000 - £60,000" },
    ],
  },
  {
    name: "Project Manager",
    category: "Business, Finance and Legal",
    shortDescription: "Plan, run, and deliver projects on time and on budget across teams.",
    whyItFits: "You're organised, decisive, and like turning chaos into a plan.",
    startingSalary: "£28,000 - £38,000",
    midSalary: "£42,000 - £65,000",
    seniorSalary: "£70,000 - £100,000+",
    timeToEntry: "2 - 4 years",
    degreeRequired: "Helpful, certifications matter",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office, hybrid, or on-site",
    stability: "Strong across industries",
    weights: { structure: 3, people: 2, income: 1, creativity: 0, freedom: 0 },
    skillsNeeded: ["Planning", "Stakeholder management", "Risk management", "Communication"],
    specialisations: [
      { title: "Construction Project Manager", shortDescription: "Run building and infrastructure projects.", typicalEntryRoute: "Site experience + qualifications", salaryRange: "£40,000 - £85,000" },
      { title: "IT Project Manager", shortDescription: "Lead technology delivery projects.", typicalEntryRoute: "Tech background + PRINCE2 / Agile", salaryRange: "£40,000 - £85,000" },
      { title: "Programme Manager", shortDescription: "Oversee multiple linked projects.", typicalEntryRoute: "Senior PM with track record", salaryRange: "£60,000 - £120,000" },
    ],
  },
  {
    name: "HR Business Partner",
    category: "Business, Finance and Legal",
    shortDescription: "Support managers and employees on hiring, performance, culture, and people issues.",
    whyItFits: "You care about people, are fair-minded, and can have difficult conversations.",
    startingSalary: "£35,000 - £45,000",
    midSalary: "£50,000 - £70,000",
    seniorSalary: "£80,000 - £120,000",
    timeToEntry: "4 - 7 years (often via HR generalist)",
    degreeRequired: "Helpful (CIPD highly valued)",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office, hybrid",
    stability: "Strong across most industries",
    weights: { people: 3, structure: 2, income: 0, creativity: 0, freedom: -1 },
    skillsNeeded: ["Empathy", "Discretion", "Employment law basics", "Coaching"],
    specialisations: [
      { title: "Recruiter", shortDescription: "Find and hire people for a company or clients.", typicalEntryRoute: "Direct entry, agency or in-house", salaryRange: "£25,000 - £70,000" },
      { title: "L&D Specialist", shortDescription: "Design and run learning programmes for staff.", typicalEntryRoute: "HR or training background", salaryRange: "£32,000 - £65,000" },
    ],
  },

  // ---------------- Sales and Customer-Facing ----------------
  {
    name: "Sales Executive",
    category: "Sales, Retail and Customer Service",
    shortDescription: "Find new customers, build relationships, and close deals - often with bonus on top of salary.",
    whyItFits: "You're driven, persuasive, and energised by hitting targets.",
    startingSalary: "£24,000 - £32,000 (+ commission)",
    midSalary: "£40,000 - £70,000 (+ commission)",
    seniorSalary: "£80,000 - £200,000+ OTE",
    timeToEntry: "0 - 1 year",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office, field, or remote",
    stability: "Always in demand",
    weights: { people: 3, income: 3, freedom: 1, structure: 0, creativity: 0 },
    skillsNeeded: ["Communication", "Resilience", "Listening", "Negotiation"],
    specialisations: [
      { title: "SDR / BDR", shortDescription: "Entry-level sales role focused on booking meetings.", typicalEntryRoute: "Direct entry", salaryRange: "£25,000 - £55,000 OTE" },
      { title: "Account Executive", shortDescription: "Close deals with new customers in B2B sales.", typicalEntryRoute: "Promote from SDR", salaryRange: "£50,000 - £150,000 OTE" },
      { title: "Account Manager", shortDescription: "Look after existing clients and grow their spend.", typicalEntryRoute: "Sales or service background", salaryRange: "£35,000 - £90,000" },
      { title: "Field Sales Rep", shortDescription: "Visit customers in person, often regional patches.", typicalEntryRoute: "Direct entry, often with car", salaryRange: "£28,000 - £65,000 OTE" },
    ],
  },
  {
    name: "Customer Service Advisor",
    category: "Sales, Retail and Customer Service",
    shortDescription: "Help customers by phone, email, or chat - solving problems and answering questions.",
    whyItFits: "You're patient, calm, and good at making people feel heard.",
    startingSalary: "£20,000 - £24,000",
    midSalary: "£25,000 - £32,000",
    seniorSalary: "£35,000 - £45,000 (team lead)",
    timeToEntry: "0 - 3 months",
    degreeRequired: "Not required",
    difficulty: "Low",
    competitionLevel: "Low",
    workEnvironment: "Office, remote, or contact centre",
    stability: "Steady, broad demand",
    weights: { people: 3, structure: 1, freedom: 0, creativity: 0, income: -1 },
    skillsNeeded: ["Patience", "Listening", "Clear communication", "Problem solving"],
    specialisations: [
      { title: "Contact Centre Advisor", shortDescription: "Phone-based customer support, often in shifts.", typicalEntryRoute: "Direct entry", salaryRange: "£20,000 - £28,000" },
      { title: "Customer Success Manager", shortDescription: "Help B2B customers get value from a product over time.", typicalEntryRoute: "Service or sales background", salaryRange: "£35,000 - £80,000" },
    ],
  },
  {
    name: "Estate Agent",
    category: "Sales, Retail and Customer Service",
    shortDescription: "Help people buy, sell, and rent homes - viewings, valuations, and negotiation.",
    whyItFits: "You're outgoing, target-driven, and enjoy meeting new people.",
    startingSalary: "£18,000 - £24,000 (+ commission)",
    midSalary: "£35,000 - £60,000 OTE",
    seniorSalary: "£80,000 - £200,000+ OTE (manager / director)",
    timeToEntry: "0 - 6 months",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office + lots of driving and viewings",
    stability: "Tied to housing market",
    weights: { people: 3, income: 2, freedom: 1, structure: 0, creativity: 0 },
    skillsNeeded: ["Persuasion", "Relationship building", "Local knowledge", "Resilience"],
    specialisations: [
      { title: "Lettings Negotiator", shortDescription: "Match tenants with rental properties.", typicalEntryRoute: "Direct entry", salaryRange: "£20,000 - £45,000 OTE" },
      { title: "Sales Negotiator", shortDescription: "Manage property sales from viewing to completion.", typicalEntryRoute: "Direct entry", salaryRange: "£22,000 - £60,000 OTE" },
    ],
  },

  // ---------------- Finance and Legal ----------------
  {
    name: "Accountant",
    category: "Business, Finance and Legal",
    shortDescription: "Prepare accounts, advise on tax, and help businesses or people manage money correctly.",
    whyItFits: "You're detail-oriented, comfortable with numbers, and like clear right-or-wrong answers.",
    startingSalary: "£24,000 - £32,000 (trainee)",
    midSalary: "£45,000 - £70,000 (qualified)",
    seniorSalary: "£90,000 - £200,000+ (partner / CFO)",
    timeToEntry: "3 - 5 Years (ACA / ACCA / CIMA)",
    degreeRequired: "Not strict (ACA / ACCA / CIMA matter)",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Office or remote, busy at year-end",
    stability: "Very strong, every business needs one",
    weights: { structure: 3, income: 2, people: 0, creativity: -1, freedom: -1 },
    skillsNeeded: ["Attention to detail", "Maths", "Software (Xero/Sage)", "Communication"],
    specialisations: [
      { title: "Audit Associate", shortDescription: "Check company accounts for accuracy and compliance.", typicalEntryRoute: "Big 4 grad scheme", salaryRange: "£28,000 - £65,000" },
      { title: "Tax Advisor", shortDescription: "Help individuals and businesses minimise tax legally.", typicalEntryRoute: "ATT / CTA route", salaryRange: "£30,000 - £90,000" },
      { title: "Management Accountant", shortDescription: "Provide internal financial reporting and forecasting.", typicalEntryRoute: "CIMA route", salaryRange: "£35,000 - £80,000" },
      { title: "Forensic Accountant", shortDescription: "Investigate fraud and financial disputes.", typicalEntryRoute: "Qualified + specialism", salaryRange: "£45,000 - £100,000" },
    ],
    selfEmployable: true,
  },
  {
    name: "Financial Analyst",
    category: "Business, Finance and Legal",
    shortDescription: "Analyse financial data to help businesses or investors make decisions.",
    whyItFits: "You like numbers, modelling, and having strong opinions backed by data.",
    startingSalary: "£28,000 - £40,000",
    midSalary: "£50,000 - £80,000",
    seniorSalary: "£90,000 - £150,000+",
    timeToEntry: "0 - 2 years",
    degreeRequired: "Usually expected",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Office, often London-based",
    stability: "Strong, especially in financial services",
    weights: { structure: 3, income: 3, people: 0, creativity: 0, freedom: -1 },
    skillsNeeded: ["Excel modelling", "Statistics", "Commercial awareness", "Communication"],
    specialisations: [
      { title: "FP&A Analyst", shortDescription: "Forecast and plan a company's financial future.", typicalEntryRoute: "Finance grad scheme", salaryRange: "£35,000 - £80,000" },
      { title: "Investment Analyst", shortDescription: "Research companies and assets for investors.", typicalEntryRoute: "Top firm + CFA", salaryRange: "£40,000 - £150,000" },
      { title: "Equity Research Analyst", shortDescription: "Cover specific stocks for investment banks or funds.", typicalEntryRoute: "Top firm grad scheme", salaryRange: "£50,000 - £200,000" },
    ],
  },
  {
    name: "Solicitor",
    category: "Business, Finance and Legal",
    shortDescription: "Advise clients on the law and represent them in legal matters.",
    whyItFits: "You think analytically, write precisely, and care about getting things right.",
    startingSalary: "£28,000 - £50,000 (trainee / NQ)",
    midSalary: "£60,000 - £100,000",
    seniorSalary: "£120,000 - £300,000+ (partner, top firms)",
    timeToEntry: "5 - 7 years (incl. SQE)",
    degreeRequired: "Yes (law or conversion + SQE)",
    difficulty: "High",
    competitionLevel: "Extremely High",
    workEnvironment: "Office, demanding hours at top firms",
    stability: "Strong, varies by area of law",
    weights: { structure: 3, income: 3, people: 1, creativity: 0, freedom: -1 },
    skillsNeeded: ["Analytical thinking", "Writing", "Attention to detail", "Resilience"],
    specialisations: [
      { title: "Corporate Solicitor", shortDescription: "Advise companies on deals, contracts, and governance.", typicalEntryRoute: "Top firm training contract", salaryRange: "£50,000 - £250,000" },
      { title: "Family Solicitor", shortDescription: "Handle divorce, custody, and family disputes.", typicalEntryRoute: "Smaller firm route", salaryRange: "£35,000 - £90,000" },
      { title: "Criminal Solicitor", shortDescription: "Represent people charged with criminal offences.", typicalEntryRoute: "Legal aid or boutique firm", salaryRange: "£28,000 - £70,000" },
    ],
    selfEmployable: true,
  },
  {
    name: "Paralegal",
    category: "Business, Finance and Legal",
    shortDescription: "Support solicitors with research, drafting, and case management.",
    whyItFits: "You're organised and want to enter law without going through full qualification straight away.",
    startingSalary: "£20,000 - £28,000",
    midSalary: "£30,000 - £45,000",
    seniorSalary: "£50,000 - £65,000",
    timeToEntry: "0 - 1 year",
    degreeRequired: "Helpful, not strict",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office or hybrid",
    stability: "Steady demand",
    weights: { structure: 3, people: 1, income: 0, creativity: 0, freedom: -1 },
    skillsNeeded: ["Research", "Writing", "Attention to detail", "Organisation"],
    specialisations: [],
  },

  // ---------------- Education and Training ----------------
  {
    name: "Teacher",
    category: "Education, Science and Research",
    shortDescription: "Plan and deliver lessons, mark work, and help young people develop.",
    whyItFits: "You like explaining things, you're patient, and you want work that matters.",
    startingSalary: "£30,000 - £33,000 (UK, NQT)",
    midSalary: "£36,000 - £45,000",
    seniorSalary: "£50,000 - £75,000 (leadership)",
    timeToEntry: "1 - 4 years",
    degreeRequired: "Yes + PGCE or equivalent",
    difficulty: "High",
    competitionLevel: "Low",
    workEnvironment: "School, lots of contact time + planning",
    stability: "Very strong, ongoing demand",
    weights: { people: 3, structure: 2, creativity: 1, income: -1, freedom: -1 },
    skillsNeeded: ["Communication", "Patience", "Subject knowledge", "Classroom management"],
    specialisations: [
      { title: "Primary Teacher", shortDescription: "Teach all subjects to children aged 5-11.", typicalEntryRoute: "PGCE Primary or undergraduate degree with QTS", salaryRange: "£30,000 - £55,000" },
      { title: "Secondary Teacher", shortDescription: "Teach a specialist subject to ages 11-18.", typicalEntryRoute: "Subject degree + PGCE", salaryRange: "£30,000 - £55,000" },
      { title: "SEN Teacher", shortDescription: "Teach pupils with special educational needs.", typicalEntryRoute: "QTS + SEN training", salaryRange: "£32,000 - £55,000" },
    ],
  },
  {
    name: "Teaching Assistant",
    category: "Education, Science and Research",
    shortDescription: "Support teachers in classrooms and work with pupils who need extra help.",
    whyItFits: "You enjoy working with children and want to enter education without full teacher training.",
    startingSalary: "£18,000 - £22,000",
    midSalary: "£22,000 - £27,000",
    seniorSalary: "£28,000 - £33,000 (HLTA)",
    timeToEntry: "0 - 6 months",
    degreeRequired: "Not required",
    difficulty: "Low",
    competitionLevel: "Moderate",
    workEnvironment: "School, term-time only",
    stability: "Steady demand, often part-time",
    weights: { people: 3, structure: 1, income: -2, creativity: 0, freedom: -1 },
    skillsNeeded: ["Patience", "Empathy", "Communication", "Adaptability"],
    specialisations: [],
  },
  {
    name: "Lecturer",
    category: "Education, Science and Research",
    shortDescription: "Teach and research at university or further education level.",
    whyItFits: "You love a subject deeply and want to share and explore it.",
    startingSalary: "£36,000 - £45,000",
    midSalary: "£50,000 - £65,000",
    seniorSalary: "£70,000 - £100,000+ (professor)",
    timeToEntry: "5 - 10 years (PhD route)",
    degreeRequired: "Yes (PhD usually required)",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "University, mix of teaching and research",
    stability: "Limited permanent posts",
    weights: { creativity: 2, structure: 2, people: 2, income: 0, freedom: 1 },
    skillsNeeded: ["Subject expertise", "Writing", "Public speaking", "Research"],
    specialisations: [],
  },

  // ---------------- Healthcare and Care ----------------
  {
    name: "Nurse",
    category: "Healthcare and Care",
    shortDescription: "Care for patients in hospitals, GP surgeries, or community settings.",
    whyItFits: "You care about people, can stay calm under pressure, and want hands-on impact.",
    startingSalary: "£28,000 - £32,000 (NHS Band 5)",
    midSalary: "£35,000 - £45,000",
    seniorSalary: "£50,000 - £70,000",
    timeToEntry: "3 years",
    degreeRequired: "Yes (or apprenticeship route)",
    difficulty: "High",
    competitionLevel: "Low",
    workEnvironment: "Hospital, community, or clinic; shifts",
    stability: "Very strong, persistent demand",
    weights: { people: 3, structure: 2, creativity: 0, income: 0, freedom: -1 },
    skillsNeeded: ["Compassion", "Resilience", "Clinical judgement", "Teamwork"],
    specialisations: [
      { title: "A&E Nurse", shortDescription: "Work in emergency departments handling acute cases.", typicalEntryRoute: "RN + A&E rotation", salaryRange: "£32,000 - £55,000" },
      { title: "Mental Health Nurse", shortDescription: "Support people with mental health conditions.", typicalEntryRoute: "Specialist nursing degree", salaryRange: "£28,000 - £50,000" },
      { title: "Paediatric Nurse", shortDescription: "Care for babies, children, and young people.", typicalEntryRoute: "Children's nursing degree", salaryRange: "£28,000 - £50,000" },
      { title: "District Nurse", shortDescription: "Provide nursing care in patients' homes.", typicalEntryRoute: "RN + community experience", salaryRange: "£35,000 - £55,000" },
    ],
  },
  {
    name: "Doctor",
    category: "Healthcare and Care",
    shortDescription: "Diagnose and treat patients in general practice, the front door of healthcare.",
    whyItFits: "You're academically strong, calm, and want to make a difference daily.",
    startingSalary: "£36,000 - £45,000 (FY1)",
    midSalary: "£70,000 - £100,000",
    seniorSalary: "£100,000 - £150,000+ (partner)",
    timeToEntry: "10 years",
    degreeRequired: "Yes (medicine + GP training)",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "GP surgery, mostly clinic-based",
    stability: "Very strong",
    weights: { people: 3, structure: 2, income: 2, creativity: 0, freedom: 0 },
    skillsNeeded: ["Clinical knowledge", "Communication", "Decision making", "Empathy"],
    specialisations: [],
  },
  {
    name: "Physiotherapist",
    category: "Healthcare and Care",
    shortDescription: "Help people recover movement and reduce pain through exercise and hands-on treatment.",
    whyItFits: "You like science and people, and want active, hands-on work.",
    startingSalary: "£28,000 - £32,000",
    midSalary: "£35,000 - £45,000",
    seniorSalary: "£50,000 - £65,000",
    timeToEntry: "3 - 4 years",
    degreeRequired: "Yes",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Hospital, clinic, sports, or home visits",
    stability: "Strong demand",
    weights: { people: 3, structure: 2, creativity: 1, income: 0, freedom: 0 },
    skillsNeeded: ["Anatomy", "Hands-on technique", "Communication", "Patience"],
    specialisations: [
      { title: "Sports Physiotherapist", shortDescription: "Work with athletes on injury and performance.", typicalEntryRoute: "Physio degree + sports specialism", salaryRange: "£30,000 - £60,000" },
      { title: "Musculoskeletal Physio", shortDescription: "Treat back, neck, and joint issues - common in NHS and private.", typicalEntryRoute: "Physio degree", salaryRange: "£30,000 - £55,000" },
    ],
  },
  {
    name: "Care Worker",
    category: "Healthcare and Care",
    shortDescription: "Support older or disabled people with daily life - in their homes or care settings.",
    whyItFits: "You're kind, patient, and want work that genuinely helps others.",
    startingSalary: "£20,000 - £24,000",
    midSalary: "£24,000 - £28,000",
    seniorSalary: "£28,000 - £35,000 (senior carer)",
    timeToEntry: "0 - 3 months",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Care home or service users' homes; shifts",
    stability: "Very strong demand",
    weights: { people: 3, structure: 1, income: -2, creativity: 0, freedom: -1 },
    skillsNeeded: ["Compassion", "Patience", "Reliability", "Practical skills"],
    specialisations: [
      { title: "Domiciliary Carer", shortDescription: "Visit people in their own homes to provide care.", typicalEntryRoute: "Direct entry + Care Certificate", salaryRange: "£20,000 - £26,000" },
      { title: "Care Home Worker", shortDescription: "Provide round-the-clock care in residential settings.", typicalEntryRoute: "Direct entry", salaryRange: "£20,000 - £27,000" },
    ],
  },
  {
    name: "Pharmacist",
    category: "Healthcare and Care",
    shortDescription: "Dispense medicines and advise patients on safe and effective use.",
    whyItFits: "You're scientifically minded, careful, and like helping people directly.",
    startingSalary: "£35,000 - £45,000",
    midSalary: "£45,000 - £60,000",
    seniorSalary: "£65,000 - £90,000",
    timeToEntry: "5 years (MPharm + foundation year)",
    degreeRequired: "Yes (MPharm)",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Community pharmacy, hospital, or industry",
    stability: "Strong demand",
    weights: { structure: 3, people: 2, income: 1, creativity: 0, freedom: -1 },
    skillsNeeded: ["Chemistry", "Attention to detail", "Communication", "Decision making"],
    specialisations: [],
  },

  // ---------------- Fitness and Wellbeing ----------------
  {
    name: "Personal Trainer",
    category: "Sport, Fitness and Wellbeing",
    shortDescription: "Coach individuals or groups on fitness, training, and lifestyle.",
    whyItFits: "You love fitness, enjoy people, and want flexible, hands-on work.",
    startingSalary: "£15,000 - £25,000 (building base)",
    midSalary: "£30,000 - £55,000",
    seniorSalary: "£60,000 - £120,000+ (online + premium)",
    timeToEntry: "3 - 6 months (Level 2 + 3)",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Gym, online, or clients' homes",
    stability: "Variable - depends on client base",
    weights: { people: 3, freedom: 2, creativity: 1, income: 0, structure: 0 },
    skillsNeeded: ["Motivation", "Anatomy basics", "Communication", "Self-promotion"],
    specialisations: [
      { title: "Gym Instructor", shortDescription: "Run inductions and classes in a gym.", typicalEntryRoute: "Level 2 Gym Instructor", salaryRange: "£18,000 - £25,000" },
      { title: "Strength Coach", shortDescription: "Specialise in strength training for general or athletic clients.", typicalEntryRoute: "Level 3 + specialist courses", salaryRange: "£25,000 - £55,000" },
      { title: "Online Fitness Coach", shortDescription: "Coach clients remotely via app, video, and check-ins.", typicalEntryRoute: "Build audience + offer", salaryRange: "£20,000 - £150,000" },
      { title: "Group Exercise Instructor", shortDescription: "Lead classes like spin, yoga, or HIIT.", typicalEntryRoute: "Discipline-specific qualification", salaryRange: "£18,000 - £40,000" },
    ],
    selfEmployable: true,
    soloMeta: {
      startupCostGBP: "£500 - £2,000 (qualification + insurance + basic kit)",
      timeToFirstClient: "1 - 3 weeks",
      monthOneIncome: "£400 - £1,800",
      yearOneIncome: "£18,000 - £35,000",
      scaledIncome: "£45,000 - £80,000+ with online coaching or studio",
      commonPitfalls: ["Trying to be all things to everyone instead of picking a niche", "Trading time for money forever - no online offer", "Renting expensive gym space too early"],
      firstFiveClients: ["Free trial sessions for friends + their network", "Gym floor work to meet members", "Instagram with one clear transformation story", "Local Facebook mum/dad groups", "Referral discounts for existing clients"],
      toolsAndCosts: ["Level 3 PT qual £1k", "Insurance £150/yr", "Basic kit £300"],
      legalBasics: ["Sole trader", "Public liability + professional indemnity", "REPs / CIMSPA registration"],
    },
  },
  {
    name: "Counsellor",
    category: "Sport, Fitness and Wellbeing",
    shortDescription: "Help clients work through emotional and mental challenges in confidential sessions.",
    whyItFits: "You're empathetic, a strong listener, and people already open up to you.",
    startingSalary: "£22,000 - £28,000",
    midSalary: "£30,000 - £45,000",
    seniorSalary: "£50,000 - £70,000",
    timeToEntry: "3 - 5 Years (training + practice hours)",
    degreeRequired: "Helpful, accreditation required",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Clinic, NHS, online, or private practice",
    stability: "Growing demand",
    weights: { people: 3, structure: 1, creativity: 1, income: -1, freedom: 0 },
    skillsNeeded: ["Empathy", "Listening", "Self-awareness", "Boundaries"],
    specialisations: [],
    selfEmployable: true,
  },
  {
    name: "Sports Massage Therapist",
    category: "Sport, Fitness and Wellbeing",
    shortDescription: "Treat clients with sports and remedial massage to recover and prevent injury.",
    whyItFits: "You like hands-on work, anatomy, and helping people feel better physically.",
    startingSalary: "£20,000 - £26,000",
    midSalary: "£28,000 - £40,000",
    seniorSalary: "£45,000 - £60,000",
    timeToEntry: "6 - 12 months",
    degreeRequired: "Not required (Level 3-5 qualification)",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Clinic, gym, mobile, or sports team",
    stability: "Steady, often self-employed",
    weights: { people: 2, freedom: 2, creativity: 0, income: 0, structure: 0 },
    skillsNeeded: ["Anatomy", "Manual technique", "Communication", "Stamina"],
    specialisations: [],
    selfEmployable: true,
    soloMeta: {
      startupCostGBP: "£1,000 - £3,000 (qualification + table + insurance)",
      timeToFirstClient: "2 - 4 weeks",
      monthOneIncome: "£400 - £1,500",
      yearOneIncome: "£18,000 - £30,000",
      scaledIncome: "£35,000 - £55,000+ with clinic or sports team contract",
      commonPitfalls: ["No clear niche (runners? rugby? office workers?)", "Not partnering with local PTs / physios", "Treating mates for free forever"],
      firstFiveClients: ["Free taster sessions at running clubs", "Partner with a local PT for referrals", "Instagram with mobility tips", "Office wellness day pop-ups", "Loyalty pack (buy 5 get 1 free)"],
      toolsAndCosts: ["Level 3/4 qual £800", "Table £150", "Insurance £100/yr"],
      legalBasics: ["Sole trader", "Professional indemnity insurance", "Membership of a body (CThA, FHT)"],
    },
  },

  // ---------------- Hospitality and Food ----------------
  {
    name: "Chef",
    category: "Hospitality and Food",
    shortDescription: "Cook and lead in professional kitchens - restaurants, hotels, or private settings.",
    whyItFits: "You like fast-paced work, creativity, and producing something tangible every day.",
    startingSalary: "£20,000 - £25,000 (commis)",
    midSalary: "£28,000 - £42,000 (chef de partie / sous)",
    seniorSalary: "£50,000 - £100,000+ (head / exec chef)",
    timeToEntry: "2 - 5 years",
    degreeRequired: "Not required",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Kitchen - hot, fast, evenings and weekends",
    stability: "Strong demand, high turnover industry",
    weights: { creativity: 2, structure: 1, people: 1, income: 0, freedom: 0 },
    skillsNeeded: ["Knife skills", "Speed", "Stamina", "Calm under pressure"],
    specialisations: [
      { title: "Line Cook", shortDescription: "Run a station in a busy professional kitchen.", typicalEntryRoute: "Direct entry + on-the-job training", salaryRange: "£20,000 - £30,000" },
      { title: "Sous Chef", shortDescription: "Second-in-command, supports the head chef.", typicalEntryRoute: "3-5 years kitchen experience", salaryRange: "£28,000 - £45,000" },
      { title: "Pastry Chef", shortDescription: "Specialise in desserts, breads, and patisserie.", typicalEntryRoute: "Pastry training + apprenticeship", salaryRange: "£22,000 - £45,000" },
      { title: "Private Chef", shortDescription: "Cook for individuals, families, or events.", typicalEntryRoute: "Industry experience + network", salaryRange: "£30,000 - £80,000" },
      { title: "Head Chef", shortDescription: "Run a kitchen - menu, team, costs.", typicalEntryRoute: "5+ years progression", salaryRange: "£35,000 - £75,000" },
    ],
    selfEmployable: true,
    soloMeta: {
      startupCostGBP: "£500 - £5,000 (private chef) or £15k+ (food truck / supper club)",
      timeToFirstClient: "2 - 8 weeks",
      monthOneIncome: "£600 - £2,500",
      yearOneIncome: "£25,000 - £45,000",
      scaledIncome: "£50,000 - £100,000+ with private clients, events, or own venue",
      commonPitfalls: ["Not pricing in shopping + travel time", "Skipping food hygiene paperwork", "Saying yes to everything instead of finding a niche"],
      firstFiveClients: ["Dinner parties for friends + their networks", "Instagram with plated dishes + behind the scenes", "Partner with a local wine shop for pairing nights", "Yacht / chalet agencies for season work", "Local supper club to build a list"],
      toolsAndCosts: ["Knife roll £400", "Insurance £150/yr", "Food hygiene L2 £20"],
      legalBasics: ["Sole trader or Ltd", "Public liability + product liability", "Food hygiene certificate", "Council food business registration"],
    },
  },
  {
    name: "Bartender",
    category: "Hospitality and Food",
    shortDescription: "Mix drinks and run service in bars, restaurants, or hotels.",
    whyItFits: "You're sociable, quick, and like fast-paced shifts.",
    startingSalary: "£18,000 - £22,000 (+ tips)",
    midSalary: "£24,000 - £32,000 (+ tips)",
    seniorSalary: "£35,000 - £50,000 (manager)",
    timeToEntry: "0 - 6 months",
    degreeRequired: "Not required",
    difficulty: "Low",
    competitionLevel: "Low",
    workEnvironment: "Bar - evenings, weekends, late nights",
    stability: "Steady, high churn",
    weights: { people: 3, freedom: 1, creativity: 1, income: -1, structure: 0 },
    skillsNeeded: ["Speed", "Memory", "Customer service", "Stamina"],
    specialisations: [],
  },
  {
    name: "Hotel Manager",
    category: "Hospitality and Food",
    shortDescription: "Run a hotel or part of one - guests, staff, revenue, operations.",
    whyItFits: "You're organised, people-focused, and good in a high-pressure service environment.",
    startingSalary: "£24,000 - £32,000 (asst manager)",
    midSalary: "£35,000 - £55,000",
    seniorSalary: "£60,000 - £100,000+ (GM)",
    timeToEntry: "3 - 7 years",
    degreeRequired: "Helpful",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Hotel - long, unsociable hours common",
    stability: "Tied to tourism cycles",
    weights: { people: 3, structure: 2, income: 1, creativity: 0, freedom: -1 },
    skillsNeeded: ["Leadership", "Customer service", "Commercial awareness", "Calm under pressure"],
    specialisations: [],
  },

  // ---------------- Retail and Service ----------------
  {
    name: "Retail Store Manager",
    category: "Sales, Retail and Customer Service",
    shortDescription: "Run a shop - team, sales, stock, and customer experience.",
    whyItFits: "You're organised, lead easily, and like a target-driven, customer-facing role.",
    startingSalary: "£24,000 - £32,000",
    midSalary: "£35,000 - £50,000",
    seniorSalary: "£55,000 - £80,000 (multi-site)",
    timeToEntry: "1 - 4 years",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Shop floor - weekends and evenings",
    stability: "Steady, retail-cycle dependent",
    weights: { people: 3, structure: 2, income: 0, creativity: 0, freedom: -1 },
    skillsNeeded: ["Leadership", "Sales", "Stock management", "Customer service"],
    specialisations: [],
  },
  {
    name: "Hairdresser",
    category: "Sales, Retail and Customer Service",
    shortDescription: "Cut, style, and colour hair for clients in a salon or as a freelancer.",
    whyItFits: "You're creative, hands-on, and good at building client relationships.",
    startingSalary: "£18,000 - £22,000",
    midSalary: "£25,000 - £35,000",
    seniorSalary: "£40,000 - £60,000+ (chair rental / salon owner)",
    timeToEntry: "2 - 3 years",
    degreeRequired: "Not required (NVQ Level 2/3)",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Salon, mobile, or freelance",
    stability: "Steady, builds with reputation",
    weights: { creativity: 2, people: 3, freedom: 1, income: -1, structure: 0 },
    skillsNeeded: ["Cutting", "Colouring", "Customer care", "Stamina"],
    specialisations: [
      { title: "Colourist", shortDescription: "Specialise in hair colour - balayage, highlights, corrections.", typicalEntryRoute: "Apprenticeship + advanced training", salaryRange: "£22,000 - £55,000" },
      { title: "Barber", shortDescription: "Specialise in cutting and grooming, often men's hair and beards.", typicalEntryRoute: "Barbering qualification or apprenticeship", salaryRange: "£20,000 - £45,000" },
    ],
    selfEmployable: true,
    soloMeta: {
      startupCostGBP: "£800 - £3,000 (mobile) or £10k+ (chair rental fit-out)",
      timeToFirstClient: "1 - 3 weeks",
      monthOneIncome: "£600 - £1,800",
      yearOneIncome: "£20,000 - £35,000",
      scaledIncome: "£40,000 - £70,000+ with own salon or strong booking app",
      commonPitfalls: ["Cash-only chaos and no proper booking system", "Not raising prices yearly", "No insurance on mobile work"],
      firstFiveClients: ["Friends + family for portfolio shots", "Instagram with before/after reels", "Local Facebook groups", "Chair rental in an existing salon part-time", "Referral discounts (£5 off next visit)"],
      toolsAndCosts: ["Kit £600", "Insurance £100/yr", "Booking app £20/mo"],
      legalBasics: ["Sole trader", "Public liability insurance", "PAT test electricals if mobile"],
    },
  },
  {
    name: "Beautician",
    category: "Sales, Retail and Customer Service",
    shortDescription: "Provide beauty treatments - facials, nails, waxing, lashes, and more.",
    whyItFits: "You're detail-oriented, creative, and enjoy making people feel good.",
    startingSalary: "£18,000 - £22,000",
    midSalary: "£24,000 - £32,000",
    seniorSalary: "£35,000 - £55,000 (own business)",
    timeToEntry: "1 - 2 years",
    degreeRequired: "Not required (NVQ qualifications)",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Salon, mobile, or home studio",
    stability: "Steady, often self-employed",
    weights: { creativity: 2, people: 3, freedom: 2, income: -1, structure: 0 },
    skillsNeeded: ["Treatment skills", "Customer care", "Hygiene standards", "Self-promotion"],
    specialisations: [],
    selfEmployable: true,
    soloMeta: {
      startupCostGBP: "£500 - £2,500 (mobile kit) or £8k+ (home studio)",
      timeToFirstClient: "1 - 3 weeks",
      monthOneIncome: "£500 - £1,600",
      yearOneIncome: "£18,000 - £32,000",
      scaledIncome: "£35,000 - £65,000+ with treatments like aesthetics or lash training",
      commonPitfalls: ["Skipping insurance on advanced treatments", "Undercharging compared to high street salons", "No deposit system - costly no-shows"],
      firstFiveClients: ["Friends + family for portfolio", "Instagram with treatment results", "Local Facebook groups", "Wedding fairs and bridal referrals", "Loyalty card (5th treatment free)"],
      toolsAndCosts: ["Kit £400-1500", "Insurance £100-300/yr depending on treatments"],
      legalBasics: ["Sole trader", "Treatment-specific insurance", "Local council registration for some treatments"],
    },
  },

  // ---------------- Skilled Trades ----------------
  {
    name: "Electrician",
    category: "Skilled Trades",
    shortDescription: "Install, maintain, and repair electrical systems in homes and businesses.",
    whyItFits: "You like practical work, problem solving, and earning a strong wage with your hands.",
    startingSalary: "£22,000 - £28,000 (apprentice)",
    midSalary: "£32,000 - £45,000",
    seniorSalary: "£50,000 - £80,000+ (self-employed)",
    timeToEntry: "3 - 4 years (apprenticeship)",
    degreeRequired: "Not required (Level 3 qualification)",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Sites, homes, businesses - some travel",
    stability: "Very strong, persistent shortage",
    weights: { structure: 2, freedom: 1, income: 2, people: 0, creativity: 0 },
    skillsNeeded: ["Practical skills", "Problem solving", "Maths", "Safety awareness"],
    specialisations: [
      { title: "Domestic Electrician", shortDescription: "Wire and maintain residential properties.", typicalEntryRoute: "Apprenticeship + Part P", salaryRange: "£25,000 - £55,000" },
      { title: "Industrial Electrician", shortDescription: "Work on factories and large-scale equipment.", typicalEntryRoute: "Industry experience + Level 3", salaryRange: "£30,000 - £65,000" },
    ],
    selfEmployable: true,
    soloMeta: {
      startupCostGBP: "£2,000 - £5,000 (van + tools + Part P registration)",
      timeToFirstClient: "2 - 6 weeks",
      monthOneIncome: "£800 - £2,500",
      yearOneIncome: "£28,000 - £45,000",
      scaledIncome: "£60,000 - £100,000+ with apprentices",
      commonPitfalls: ["Underquoting on first jobs to win work", "Skipping public liability insurance", "Not registering for Part P / NICEIC early enough"],
      firstFiveClients: ["Family + friends asking for small fixes", "Local Facebook community groups", "Checkatrade / MyBuilder profile", "Subcontracting for an established sparky", "Estate agents and small landlords nearby"],
      toolsAndCosts: ["Van £8k used", "Tool kit £1.5k", "Insurance £400/yr", "NICEIC ~£900/yr"],
      legalBasics: ["Sole trader or Ltd", "Public liability £2m+", "Part P registration", "VAT register at £90k"],
    },
  },
  {
    name: "Plumber",
    category: "Skilled Trades",
    shortDescription: "Install, fix, and maintain pipes, heating, and water systems.",
    whyItFits: "You like physical, practical work and solving real problems on the spot.",
    startingSalary: "£22,000 - £28,000 (apprentice)",
    midSalary: "£32,000 - £45,000",
    seniorSalary: "£50,000 - £80,000+ (self-employed)",
    timeToEntry: "2 - 4 years",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Homes, sites, businesses - travel between jobs",
    stability: "Very strong demand",
    weights: { structure: 2, freedom: 2, income: 2, people: 0, creativity: 0 },
    skillsNeeded: ["Practical skills", "Diagnostics", "Customer service", "Stamina"],
    specialisations: [
      { title: "Heating Engineer (Gas Safe)", shortDescription: "Install and service boilers and gas appliances.", typicalEntryRoute: "Plumbing + Gas Safe registration", salaryRange: "£32,000 - £65,000" },
    ],
    selfEmployable: true,
    soloMeta: {
      startupCostGBP: "£2,500 - £6,000 (van, tools, Gas Safe optional)",
      timeToFirstClient: "2 - 4 weeks",
      monthOneIncome: "£1,000 - £3,000",
      yearOneIncome: "£30,000 - £50,000",
      scaledIncome: "£70,000 - £120,000+ with team",
      commonPitfalls: ["Taking emergency call-outs at 3am for too cheap", "Not getting paid upfront for parts", "Skipping Gas Safe and turning down 40% of jobs"],
      firstFiveClients: ["Word of mouth via friends and family", "Local Facebook groups and Nextdoor", "Checkatrade / Rated People", "Subcontract for a local firm to build reputation", "Letting agents needing reliable plumbers"],
      toolsAndCosts: ["Van £8k", "Tools £2k", "Insurance £400/yr", "Gas Safe ~£400/yr"],
      legalBasics: ["Sole trader or Ltd", "Public liability £2m+", "Gas Safe (if doing gas)", "VAT register at £90k"],
    },
  },
  {
    name: "Carpenter",
    category: "Skilled Trades",
    shortDescription: "Cut, shape, and install wood for buildings, kitchens, furniture, and more.",
    whyItFits: "You're skilled with your hands, like making things, and notice detail.",
    startingSalary: "£20,000 - £26,000",
    midSalary: "£32,000 - £45,000",
    seniorSalary: "£50,000 - £80,000+ (self-employed)",
    timeToEntry: "2 - 4 years",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Sites or workshops",
    stability: "Very stable - skills shortage",
    weights: { structure: 1, freedom: 1, creativity: 2, income: 1, people: 0 },
    skillsNeeded: ["Measurement", "Hand tools", "Power tools", "Spatial thinking"],
    specialisations: [],
    selfEmployable: true,
    soloMeta: {
      startupCostGBP: "£1,500 - £4,000 (tools, van, basic insurance)",
      timeToFirstClient: "1 - 4 weeks",
      monthOneIncome: "£800 - £2,200",
      yearOneIncome: "£26,000 - £42,000",
      scaledIncome: "£55,000 - £90,000+ with bespoke kitchen / fit-out work",
      commonPitfalls: ["Quoting per day rather than per job and losing on speed gains", "No deposit taken before ordering materials", "Working only as a subcontractor and never building own brand"],
      firstFiveClients: ["Friends, family, neighbours", "Local builders needing a 2nd chippy", "Kitchen showrooms wanting fitters", "Checkatrade / MyBuilder", "Instagram with before/after photos"],
      toolsAndCosts: ["Tools £1.5k", "Van £6-10k", "Insurance £350/yr"],
      legalBasics: ["Sole trader or Ltd", "Public liability insurance", "CSCS card for sites"],
    },
  },
  {
    name: "Builder",
    category: "Skilled Trades",
    shortDescription: "Run or work on small to medium building projects - extensions, renovations, conversions.",
    whyItFits: "You're practical, like leading a job from start to finish, and want to be your own boss eventually.",
    startingSalary: "£22,000 - £30,000 (labourer/improver)",
    midSalary: "£35,000 - £55,000",
    seniorSalary: "£60,000 - £120,000+ (own firm)",
    timeToEntry: "2 - 5 years (apprenticeship or on-site progression)",
    degreeRequired: "Not required (CSCS card required)",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Sites, customers' homes, all weather",
    stability: "Strong - persistent skills shortage",
    weights: { freedom: 2, structure: 1, income: 1, creativity: 1, people: 0 },
    skillsNeeded: ["Practical know-how across trades", "Project management", "Quoting", "People management", "Reading drawings"],
    specialisations: [
      { title: "Extension / Loft Specialist", shortDescription: "Focus on house extensions and loft conversions for homeowners.", typicalEntryRoute: "Time-served builder + small team", salaryRange: "£40,000 - £100,000" },
      { title: "Renovation Builder", shortDescription: "Full house refurbs - structural work, layouts, finishes.", typicalEntryRoute: "Multi-trade experience + project management", salaryRange: "£40,000 - £120,000" },
      { title: "Site Manager", shortDescription: "Run large new-build sites for a contractor.", typicalEntryRoute: "Trades background + SMSTS + experience", salaryRange: "£45,000 - £80,000" },
    ],
    selfEmployable: true,
    soloMeta: {
      startupCostGBP: "£3,000 - £8,000 (tools, van, basic plant, insurance)",
      timeToFirstClient: "2 - 8 weeks",
      monthOneIncome: "£1,500 - £4,000",
      yearOneIncome: "£35,000 - £60,000",
      scaledIncome: "£80,000 - £200,000+ with a small team",
      commonPitfalls: ["Underquoting big jobs and bleeding money for months", "No staged payments - you fund the customer's project", "Hiring labour before you have steady work", "Poor written quotes leading to disputes"],
      firstFiveClients: ["Word of mouth from one well-finished job", "Local Facebook + community groups", "Architects passing on small projects", "Checkatrade / MyBuilder", "Estate agents needing pre-sale works"],
      toolsAndCosts: ["Hand + power tools £2-3k", "Van £8k+", "Public liability + employers' liability £400-800/yr"],
      legalBasics: ["Sole trader or Ltd", "Public liability £2m+ (£5m for larger jobs)", "CIS scheme registration", "Employers' liability if hiring labour", "Written contracts for jobs over £500"],
    },
  },
  {
    name: "Bricklayer",
    category: "Skilled Trades",
    shortDescription: "Build walls, structures, and chimneys with bricks, blocks, and stone.",
    whyItFits: "You like physical, repetitive precision work and seeing tangible progress every hour.",
    startingSalary: "£20,000 - £28,000 (apprentice/labourer)",
    midSalary: "£32,000 - £45,000",
    seniorSalary: "£50,000 - £80,000+ (self-employed/sub-contracting)",
    timeToEntry: "2 - 4 years (apprenticeship or fast-track course + site time)",
    degreeRequired: "Not required (CSCS card required)",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Sites, outdoors, all weather",
    stability: "Very stable - shortage of qualified bricklayers",
    weights: { freedom: 1, structure: 2, income: 1, creativity: 0, people: 0 },
    skillsNeeded: ["Trowel work", "Stamina", "Spirit-level accuracy", "Reading drawings", "Mortar mixing"],
    specialisations: [
      { title: "Heritage / Stonemason", shortDescription: "Restoration work on listed buildings and stone structures.", typicalEntryRoute: "Bricklaying base + heritage course or apprenticeship", salaryRange: "£30,000 - £60,000" },
      { title: "Sub-contract Brickie", shortDescription: "Price-work on new-build estates - higher earnings if fast.", typicalEntryRoute: "Time-served + CSCS + own transport", salaryRange: "£40,000 - £80,000" },
    ],
    selfEmployable: true,
    soloMeta: {
      startupCostGBP: "£800 - £2,500 (trowels, levels, mixer, PPE, van)",
      timeToFirstClient: "1 - 4 weeks (sub-contract work easier than direct)",
      monthOneIncome: "£1,500 - £3,500",
      yearOneIncome: "£30,000 - £50,000",
      scaledIncome: "£55,000 - £90,000+ (price-work / small gang)",
      commonPitfalls: ["Working price-work too long without protecting back/knees", "No deposit on materials and getting stung", "Sub-contracting only and never building direct customers"],
      firstFiveClients: ["Local builders needing a brickie", "Word of mouth from estate agents", "Checkatrade for garden walls / extensions", "House developers' price-work gangs", "Facebook trade groups"],
      toolsAndCosts: ["Trowels + levels £400", "Cement mixer £300", "Van £6-10k", "Insurance £300/yr"],
      legalBasics: ["Sole trader or Ltd", "Public liability insurance", "CSCS card", "CIS scheme registration"],
    },
  },
  {
    name: "Painter and Decorator",
    category: "Skilled Trades",
    shortDescription: "Prepare and paint interior and exterior surfaces - homes, commercial premises, and new builds.",
    whyItFits: "You're patient, neat, and like turning tired rooms into something people are pleased with.",
    startingSalary: "£18,000 - £25,000",
    midSalary: "£28,000 - £40,000",
    seniorSalary: "£45,000 - £70,000+ (self-employed/specialist)",
    timeToEntry: "0 - 2 years (short course + on-the-job, or apprenticeship)",
    degreeRequired: "Not required",
    difficulty: "Low",
    competitionLevel: "Low",
    workEnvironment: "Customers' homes, sites, indoors and out",
    stability: "Steady - constant residential demand",
    weights: { freedom: 2, creativity: 1, structure: 1, income: 0, people: 0 },
    skillsNeeded: ["Surface preparation", "Cutting in / steady hand", "Colour and product knowledge", "Time management", "Customer service"],
    specialisations: [
      { title: "Heritage / Specialist Decorator", shortDescription: "Wallpapering, hand-finished period work, high-end private clients.", typicalEntryRoute: "Time-served + portfolio of finished interiors", salaryRange: "£35,000 - £75,000" },
      { title: "Spray Finisher", shortDescription: "Spray-finishing kitchens, doors, commercial fit-outs - higher day rate.", typicalEntryRoute: "Spray course + invest in HVLP setup", salaryRange: "£35,000 - £70,000" },
    ],
    selfEmployable: true,
    soloMeta: {
      startupCostGBP: "£500 - £2,000 (brushes, rollers, sundries, dust sheets, ladders, basic van)",
      timeToFirstClient: "1 - 3 weeks",
      monthOneIncome: "£1,200 - £3,000",
      yearOneIncome: "£25,000 - £40,000",
      scaledIncome: "£45,000 - £70,000+ with a second pair of hands",
      commonPitfalls: ["Quoting per day instead of per room - customers dawdle on choices", "Skimping on prep and getting callbacks", "No deposit so customers cancel last minute"],
      firstFiveClients: ["Friends and neighbours wanting a feature wall", "Local Facebook recommendations", "Letting agents needing turnaround paints between tenants", "Checkatrade / MyBuilder", "Working under another decorator for overflow"],
      toolsAndCosts: ["Brushes + rollers £200", "Step ladder + access £300", "Van £5-8k", "Insurance £200/yr"],
      legalBasics: ["Sole trader or Ltd", "Public liability £2m+", "Dulux Select / Trustmark optional but raises trust"],
    },
  },
  {
    name: "Mechanic",
    category: "Skilled Trades",
    shortDescription: "Diagnose and fix problems with cars, vans, or commercial vehicles.",
    whyItFits: "You like vehicles, problem solving, and fixing things hands-on.",
    startingSalary: "£20,000 - £26,000",
    midSalary: "£28,000 - £40,000",
    seniorSalary: "£45,000 - £60,000",
    timeToEntry: "2 - 4 years",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Garage or workshop",
    stability: "Steady, evolving with EV transition",
    weights: { structure: 2, freedom: 1, income: 0, people: 0, creativity: 0 },
    skillsNeeded: ["Diagnostics", "Hand tools", "Electrics basics", "Patience"],
    specialisations: [],
    selfEmployable: true,
    soloMeta: {
      startupCostGBP: "£3,000 - £8,000 (mobile setup) or £15k+ (small workshop)",
      timeToFirstClient: "2 - 6 weeks",
      monthOneIncome: "£1,000 - £2,500",
      yearOneIncome: "£28,000 - £45,000",
      scaledIncome: "£50,000 - £85,000+ as mobile mechanic with niche (e.g. EVs, classics)",
      commonPitfalls: ["Buying too much diagnostic kit before having clients", "Not building a Google Business profile early", "Undercharging for diagnostics"],
      firstFiveClients: ["Local Facebook + Nextdoor posts", "Family + friends with small issues", "Used car dealers needing pre-sale checks", "Mobile servicing for office car parks", "Word of mouth from honest first jobs"],
      toolsAndCosts: ["Tools + diagnostics £3k", "Van £8k+", "Insurance £600/yr"],
      legalBasics: ["Sole trader or Ltd", "Motor trade insurance", "Waste oil disposal licence"],
    },
  },
  {
    name: "Gardener",
    category: "Skilled Trades",
    shortDescription: "Maintain and improve outdoor spaces - private gardens, grounds, and landscapes.",
    whyItFits: "You like working outdoors, hands-on practical work, and seeing visible results from your day.",
    startingSalary: "£18,000 - £24,000",
    midSalary: "£26,000 - £35,000",
    seniorSalary: "£40,000 - £60,000+ (self-employed/landscaper)",
    timeToEntry: "0 - 2 years",
    degreeRequired: "Not required (RHS optional)",
    difficulty: "Low",
    competitionLevel: "Low",
    workEnvironment: "Outdoors in all weather, customer gardens or estates",
    stability: "Steady - rises with seasons",
    weights: { freedom: 2, structure: 1, creativity: 1, people: 0, income: 0 },
    skillsNeeded: ["Plant knowledge", "Stamina", "Customer service", "Tool use"],
    specialisations: [
      { title: "Landscaper", shortDescription: "Design and build patios, decking, planting schemes, and full garden makeovers.", typicalEntryRoute: "Trade experience + landscaping course", salaryRange: "£28,000 - £55,000" },
      { title: "Groundskeeper", shortDescription: "Maintain large grounds - sports clubs, schools, estates.", typicalEntryRoute: "Greenkeeper apprenticeship or experience", salaryRange: "£22,000 - £35,000" },
    ],
    selfEmployable: true,
    soloMeta: {
      startupCostGBP: "£1,500 - £4,000 (mower, strimmer, basic tools, van/trailer)",
      timeToFirstClient: "1 - 4 weeks",
      monthOneIncome: "£500 - £1,800",
      yearOneIncome: "£20,000 - £35,000",
      scaledIncome: "£45,000 - £75,000+ with team or landscaping work",
      commonPitfalls: ["Quoting too low to win first jobs and getting stuck there", "No insurance until something gets damaged", "Cash-in-hand only - no record for mortgage applications"],
      firstFiveClients: ["Leaflet drop on 200 nearby houses", "Local Facebook + Nextdoor groups", "Friends, family and their neighbours", "Letting agents needing reliable garden upkeep", "Word of mouth from one tidy first job"],
      toolsAndCosts: ["Mower £400+", "Strimmer £200", "Van or trailer £4-8k", "Insurance £200/yr"],
      legalBasics: ["Sole trader or Ltd", "Public liability £2m+", "Waste carrier licence (if removing green waste)"],
    },
  },
  {
    name: "Driving Instructor",
    category: "Self-Employed and Business Owner",
    shortDescription: "Teach learner drivers to pass their test - usually self-employed under your own name or a franchise.",
    whyItFits: "You're patient, like meeting new people, and want flexible self-employed work.",
    startingSalary: "£18,000 - £25,000 (first year)",
    midSalary: "£28,000 - £40,000",
    seniorSalary: "£45,000 - £60,000+ (independent, full diary)",
    timeToEntry: "6 - 12 months (ADI Parts 1, 2, 3)",
    degreeRequired: "Not required (DVSA ADI qualification required)",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "In-car, mostly local area, flexible hours",
    stability: "Strong - long learner waiting lists post-pandemic",
    weights: { people: 2, freedom: 3, structure: 1, income: 0, creativity: 0 },
    skillsNeeded: ["Patience", "Clear communication", "Calm under pressure", "Self-management"],
    specialisations: [
      { title: "Independent Driving Instructor", shortDescription: "Run your own ADI business under your own brand.", typicalEntryRoute: "ADI qualification + 1-2 years building reputation", salaryRange: "£35,000 - £60,000" },
      { title: "Franchise Instructor", shortDescription: "Work under a national brand (RED, AA, BSM) with leads provided.", typicalEntryRoute: "ADI qualification + franchise sign-up", salaryRange: "£25,000 - £40,000" },
    ],
    selfEmployable: true,
    soloMeta: {
      startupCostGBP: "£3,000 - £8,000 (ADI training + dual-control car lease deposit)",
      timeToFirstClient: "1 - 4 weeks once qualified",
      monthOneIncome: "£800 - £2,500",
      yearOneIncome: "£20,000 - £35,000",
      scaledIncome: "£45,000 - £60,000+ as independent with full diary",
      commonPitfalls: ["Choosing a franchise too quickly to feel safe and losing 30% of income", "Underpricing per hour to win pupils", "No no-show policy - lost income from cancellations"],
      firstFiveClients: ["Local Facebook + community groups", "Friends and family with teenage learners", "Listing on Google Business + reviews", "Partnering with one local secondary school", "Word of mouth from one good test pass"],
      toolsAndCosts: ["Dual-control car lease £300-£500/month", "Insurance £1k-£1.8k/yr", "ADI badge £300/4yrs"],
      legalBasics: ["Sole trader or Ltd", "DVSA ADI registration", "Hire and reward insurance", "VAT register at £90k"],
    },
  },

  // ---------------- Construction and Infrastructure ----------------
  {
    name: "Construction Worker",
    category: "Engineering, Construction and Infrastructure",
    shortDescription: "Build and demolish on construction sites - hands-on, physical work.",
    whyItFits: "You're fit, like outdoor work, and want to see real results from your day.",
    startingSalary: "£20,000 - £28,000",
    midSalary: "£30,000 - £42,000",
    seniorSalary: "£45,000 - £65,000 (foreman)",
    timeToEntry: "0 - 1 year",
    degreeRequired: "Not required (CSCS card required)",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Construction sites, outdoor, all weather",
    stability: "Cyclical with construction industry",
    weights: { structure: 1, freedom: 1, income: 0, people: 0, creativity: 0 },
    skillsNeeded: ["Stamina", "Teamwork", "Safety awareness", "Practical skills"],
    specialisations: [
      { title: "Bricklayer", shortDescription: "Build walls, structures, and chimneys with bricks and blocks.", typicalEntryRoute: "Apprenticeship", salaryRange: "£25,000 - £55,000" },
      { title: "Site Supervisor", shortDescription: "Oversee day-to-day work on a section of a site.", typicalEntryRoute: "Trades experience + SSSTS/SMSTS", salaryRange: "£35,000 - £60,000" },
    ],
  },
  {
    name: "Quantity Surveyor",
    category: "Engineering, Construction and Infrastructure",
    shortDescription: "Manage costs, contracts, and budgets on construction projects.",
    whyItFits: "You're numbers-minded, organised, and interested in built environment.",
    startingSalary: "£26,000 - £35,000",
    midSalary: "£42,000 - £65,000",
    seniorSalary: "£70,000 - £110,000",
    timeToEntry: "3 - 5 Years (degree + chartership)",
    degreeRequired: "Yes (or apprenticeship route)",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office + site visits",
    stability: "Strong - shortage of QS in UK",
    weights: { structure: 3, income: 2, people: 1, creativity: 0, freedom: -1 },
    skillsNeeded: ["Numerical analysis", "Contracts", "Negotiation", "Attention to detail"],
    specialisations: [],
    selfEmployable: true,
  },

  // ---------------- Engineering and Manufacturing ----------------
  {
    name: "Mechanical Engineer",
    category: "Engineering, Construction and Infrastructure",
    shortDescription: "Design, build, and improve machines and mechanical systems.",
    whyItFits: "You like maths, physics, and turning ideas into working things.",
    startingSalary: "£26,000 - £35,000",
    midSalary: "£42,000 - £60,000",
    seniorSalary: "£65,000 - £95,000+",
    timeToEntry: "3 - 5 Years (degree + IEng / CEng route)",
    degreeRequired: "Yes (or apprenticeship)",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Office, factory, or site",
    stability: "Strong, broad industry demand",
    weights: { structure: 3, creativity: 1, income: 1, people: 0, freedom: 0 },
    skillsNeeded: ["Maths", "CAD", "Problem solving", "Attention to detail"],
    specialisations: [
      { title: "Automotive Engineer", shortDescription: "Design or improve vehicles and components.", typicalEntryRoute: "Engineering degree + grad scheme", salaryRange: "£28,000 - £80,000" },
      { title: "Aerospace Engineer", shortDescription: "Work on aircraft, satellites, and propulsion.", typicalEntryRoute: "Engineering degree + specialism", salaryRange: "£32,000 - £90,000" },
    ],
  },
  {
    name: "Civil Engineer",
    category: "Engineering, Construction and Infrastructure",
    shortDescription: "Design and oversee bridges, roads, water systems, and large structures.",
    whyItFits: "You like planning, want tangible results, and think long-term.",
    startingSalary: "£28,000 - £35,000",
    midSalary: "£42,000 - £60,000",
    seniorSalary: "£70,000 - £110,000+",
    timeToEntry: "5 - 7 years (incl. chartership)",
    degreeRequired: "Yes",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Office + site",
    stability: "Strong, infrastructure-led",
    weights: { structure: 3, creativity: 1, income: 1, people: 1, freedom: -1 },
    skillsNeeded: ["Maths", "CAD", "Project thinking", "Communication"],
    specialisations: [],
  },
  {
    name: "Factory Operative",
    category: "Engineering, Construction and Infrastructure",
    shortDescription: "Run machinery, assemble products, or manage quality in a manufacturing setting.",
    whyItFits: "You like routine, structured environments and steady, predictable work.",
    startingSalary: "£20,000 - £25,000",
    midSalary: "£26,000 - £32,000",
    seniorSalary: "£35,000 - £45,000 (team leader)",
    timeToEntry: "0 - 3 months",
    degreeRequired: "Not required",
    difficulty: "Low",
    competitionLevel: "Low",
    workEnvironment: "Factory, often shifts",
    stability: "Steady but tied to industry",
    weights: { structure: 2, people: 0, freedom: -1, income: -1, creativity: -1 },
    skillsNeeded: ["Reliability", "Stamina", "Attention to detail", "Teamwork"],
    specialisations: [],
  },

  // ---------------- Transport and Logistics ----------------
  {
    name: "HGV Driver",
    category: "Transport and Logistics",
    shortDescription: "Drive lorries to deliver goods across the country or internationally.",
    whyItFits: "You like driving, independence, and time on the road.",
    startingSalary: "£28,000 - £35,000",
    midSalary: "£36,000 - £48,000",
    seniorSalary: "£50,000 - £65,000+ (specialist / owner-driver)",
    timeToEntry: "3 - 6 months (licence)",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Cab, varied routes, long hours",
    stability: "Very strong demand",
    weights: { freedom: 2, structure: 1, income: 1, people: -1, creativity: -1 },
    skillsNeeded: ["Driving", "Patience", "Time management", "Stamina"],
    specialisations: [],
  },
  {
    name: "Delivery Driver",
    category: "Transport and Logistics",
    shortDescription: "Deliver parcels, food, or goods locally - often using a van or car.",
    whyItFits: "You like being out and about, working independently, with a clear daily target.",
    startingSalary: "£22,000 - £28,000",
    midSalary: "£28,000 - £35,000",
    seniorSalary: "£35,000 - £45,000",
    timeToEntry: "0 - 1 month",
    degreeRequired: "Not required (driving licence)",
    difficulty: "Low",
    competitionLevel: "Low",
    workEnvironment: "Van, lots of stops, all weather",
    stability: "Strong, e-commerce led",
    weights: { freedom: 2, structure: 1, people: 0, income: -1, creativity: -1 },
    skillsNeeded: ["Driving", "Time management", "Customer interaction", "Stamina"],
    specialisations: [],
  },
  {
    name: "Pilot",
    category: "Transport and Logistics",
    shortDescription: "Fly commercial, cargo, or private aircraft.",
    whyItFits: "You're disciplined, calm, and want a high-skill, high-stakes career.",
    startingSalary: "£24,000 - £40,000 (first officer)",
    midSalary: "£60,000 - £100,000",
    seniorSalary: "£120,000 - £200,000 (captain)",
    timeToEntry: "2 - 3 years training (£80k-£120k)",
    degreeRequired: "Not required (ATPL training)",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Cockpit, varied schedules, away from home",
    stability: "Cyclical, recovers post-disruption",
    weights: { structure: 3, income: 2, freedom: 1, people: 0, creativity: 0 },
    skillsNeeded: ["Discipline", "Calm under pressure", "Spatial awareness", "Decision making"],
    specialisations: [],
  },

  // ---------------- Public Sector and Emergency Services ----------------
  {
    name: "Police Officer",
    category: "Public Sector and Emergency Services",
    shortDescription: "Keep communities safe - patrol, investigate, respond to incidents.",
    whyItFits: "You want public service, can stay calm, and are physically and mentally robust.",
    startingSalary: "£28,000 - £33,000",
    midSalary: "£42,000 - £50,000 (sergeant / inspector)",
    seniorSalary: "£55,000 - £100,000+ (senior ranks)",
    timeToEntry: "2 - 3 years (PCDA / DHEP)",
    degreeRequired: "Increasingly expected (PCDA route)",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Streets, station, shifts including nights",
    stability: "Very strong",
    weights: { people: 2, structure: 3, income: 0, creativity: 0, freedom: -1 },
    skillsNeeded: ["Calm under pressure", "Communication", "Fitness", "Judgement"],
    specialisations: [],
  },
  {
    name: "Firefighter",
    category: "Public Sector and Emergency Services",
    shortDescription: "Respond to fires, road accidents, and other emergencies.",
    whyItFits: "You're physically fit, calm under pressure, and want hands-on public service.",
    startingSalary: "£28,000 - £30,000",
    midSalary: "£37,000 - £40,000",
    seniorSalary: "£45,000 - £80,000+ (officer ranks)",
    timeToEntry: "1 - 2 years (entry is competitive)",
    degreeRequired: "Not required",
    difficulty: "High",
    competitionLevel: "Extremely High",
    workEnvironment: "Station + emergency call-outs, shifts",
    stability: "Very strong, limited intake",
    weights: { people: 2, structure: 3, income: 0, creativity: 0, freedom: -1 },
    skillsNeeded: ["Fitness", "Teamwork", "Calm under pressure", "Practical skills"],
    specialisations: [],
  },
  {
    name: "Paramedic",
    category: "Public Sector and Emergency Services",
    shortDescription: "Provide emergency medical care at the scene and en route to hospital.",
    whyItFits: "You're calm under pressure, decisive, and want to help in critical moments.",
    startingSalary: "£28,000 - £35,000 (Band 5/6)",
    midSalary: "£37,000 - £48,000 (Band 6/7)",
    seniorSalary: "£50,000 - £65,000",
    timeToEntry: "3 years",
    degreeRequired: "Yes",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Ambulance, shifts including nights",
    stability: "Very strong demand",
    weights: { people: 3, structure: 2, creativity: 0, income: 0, freedom: -1 },
    skillsNeeded: ["Clinical judgement", "Calm under pressure", "Communication", "Stamina"],
    specialisations: [],
  },

  // ---------------- Sport and Performance ----------------
  {
    name: "Professional Footballer",
    category: "Sport, Fitness and Wellbeing",
    shortDescription: "Play football professionally for a club.",
    whyItFits: "You've trained from a young age, are exceptionally fit, and have proven talent.",
    startingSalary: "£18,000 - £40,000 (lower leagues)",
    midSalary: "£60,000 - £200,000",
    seniorSalary: "£500,000 - £15m+ (top flight)",
    timeToEntry: "10+ years (academy from young age)",
    degreeRequired: "Not required",
    difficulty: "High",
    competitionLevel: "Extremely High",
    workEnvironment: "Training ground + matches, public scrutiny",
    stability: "Short careers, high injury risk",
    weights: { freedom: 1, income: 1, people: 1, structure: 1, creativity: 1 },
    skillsNeeded: ["Talent", "Discipline", "Mental resilience", "Teamwork"],
    specialisations: [],
  },
  {
    name: "Sports Coach",
    category: "Sport, Fitness and Wellbeing",
    shortDescription: "Coach individuals or teams in a specific sport, from grassroots to elite.",
    whyItFits: "You love a sport deeply and like teaching and motivating others.",
    startingSalary: "£18,000 - £25,000",
    midSalary: "£28,000 - £45,000",
    seniorSalary: "£60,000 - £200,000+ (elite / pro)",
    timeToEntry: "1 - 5 years (NGB coaching badges)",
    degreeRequired: "Helpful (sport science) + coaching badges",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Pitches, gyms, academies; evenings/weekends",
    stability: "Variable, often part-time at lower levels",
    weights: { people: 3, freedom: 1, creativity: 1, income: -1, structure: 0 },
    skillsNeeded: ["Coaching", "Communication", "Motivation", "Sport knowledge"],
    specialisations: [],
    selfEmployable: true,
  },

  // ---------------- Cleaning, Facilities, and Maintenance ----------------
  {
    name: "Cleaner",
    category: "Sales, Retail and Customer Service",
    shortDescription: "Clean homes, offices, or commercial spaces - keeping places safe and presentable.",
    whyItFits: "You like steady, hands-on work where you can see results immediately.",
    startingSalary: "£18,000 - £22,000",
    midSalary: "£22,000 - £28,000",
    seniorSalary: "£28,000 - £38,000 (supervisor / own business)",
    timeToEntry: "0 - 1 month",
    degreeRequired: "Not required",
    difficulty: "Low",
    competitionLevel: "Low",
    workEnvironment: "Homes, offices, or commercial sites",
    stability: "Very strong demand",
    weights: { structure: 1, freedom: 1, people: 0, income: -2, creativity: -1 },
    skillsNeeded: ["Reliability", "Attention to detail", "Stamina", "Trustworthiness"],
    specialisations: [
      { title: "Domestic Cleaner", shortDescription: "Clean private homes, often regular weekly clients.", typicalEntryRoute: "Direct entry", salaryRange: "£18,000 - £30,000" },
      { title: "Commercial Cleaner", shortDescription: "Clean offices and businesses, often early or late shifts.", typicalEntryRoute: "Direct entry", salaryRange: "£18,000 - £26,000" },
      { title: "Specialist Deep Cleaner", shortDescription: "End-of-tenancy, post-construction, biohazard cleans.", typicalEntryRoute: "Training + equipment", salaryRange: "£22,000 - £40,000" },
      { title: "Housekeeping Operative", shortDescription: "Clean and prep rooms in hotels and accommodation.", typicalEntryRoute: "Direct entry", salaryRange: "£18,000 - £26,000" },
    ],
    selfEmployable: true,
    soloMeta: {
      startupCostGBP: "£200 - £800 (kit + insurance) or £3k+ (small team van setup)",
      timeToFirstClient: "1 - 2 weeks",
      monthOneIncome: "£600 - £2,000",
      yearOneIncome: "£20,000 - £35,000",
      scaledIncome: "£40,000 - £80,000+ with small team and commercial contracts",
      commonPitfalls: ["No contract / cancellation policy - clients ghosting", "Trying to do residential and commercial badly", "Not raising prices for long-term clients"],
      firstFiveClients: ["Leaflets to streets you'd cover anyway", "Local Facebook groups + Nextdoor", "Airbnb hosts needing turnover cleans", "Estate agents needing end-of-tenancy cleans", "Word of mouth via existing clients"],
      toolsAndCosts: ["Cleaning kit £150", "Insurance £100/yr", "Vacuum £200"],
      legalBasics: ["Sole trader or Ltd", "Public liability £2m+", "Treatment-specific COSHH awareness"],
    },
  },
  {
    name: "Facilities Manager",
    category: "Business, Finance and Legal",
    shortDescription: "Keep buildings running - cleaning, maintenance, security, contracts.",
    whyItFits: "You're organised, practical, and like making sure things just work.",
    startingSalary: "£26,000 - £35,000",
    midSalary: "£40,000 - £55,000",
    seniorSalary: "£60,000 - £85,000+",
    timeToEntry: "2 - 4 years",
    degreeRequired: "Helpful (BIFM/IWFM)",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office or site-based",
    stability: "Strong, broad industry need",
    weights: { structure: 3, people: 2, income: 1, creativity: 0, freedom: -1 },
    skillsNeeded: ["Organisation", "Negotiation", "Problem solving", "Vendor management"],
    specialisations: [],
  },

  // ---------------- Science and Research ----------------
  {
    name: "Research Scientist",
    category: "Education, Science and Research",
    shortDescription: "Run original research in a specialist scientific field.",
    whyItFits: "You love a subject deeply and like long-term, exploratory work.",
    startingSalary: "£28,000 - £38,000",
    midSalary: "£40,000 - £60,000",
    seniorSalary: "£70,000 - £120,000+",
    timeToEntry: "5 - 8 years (PhD route)",
    degreeRequired: "Yes (PhD usually)",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Lab, industry, or academia",
    stability: "Funding-dependent in academia",
    weights: { structure: 2, creativity: 2, income: 0, people: 0, freedom: 1 },
    skillsNeeded: ["Subject expertise", "Experimental design", "Writing", "Persistence"],
    specialisations: [],
  },

  // ---------------- Agriculture and Outdoor Work ----------------
  {
    name: "Farmer",
    category: "Skilled Trades",
    shortDescription: "Run or work on a farm - crops, livestock, machinery, business.",
    whyItFits: "You love outdoor work, animals or land, and a hands-on, varied life.",
    startingSalary: "£20,000 - £28,000 (farm worker)",
    midSalary: "£30,000 - £45,000 (manager)",
    seniorSalary: "£50,000 - £100,000+ (owner / large estate)",
    timeToEntry: "1 - 5 years",
    degreeRequired: "Helpful (agriculture)",
    difficulty: "High",
    competitionLevel: "Low",
    workEnvironment: "Outdoors, all weather, long days",
    stability: "Variable, weather and market exposed",
    weights: { freedom: 2, structure: 1, people: 0, income: -1, creativity: 0 },
    skillsNeeded: ["Practical skills", "Animal / crop knowledge", "Machinery", "Resilience"],
    specialisations: [],
    selfEmployable: true,
  },
  // ---------------- Alternative Paths ----------------
  {
    name: "Founder / Entrepreneur",
    category: "Self-Employed and Business Owner",
    shortDescription: "Start and run your own business from scratch.",
    whyItFits: "You're independent, take initiative, and willing to handle uncertainty.",
    startingSalary: "£0 - £15,000",
    midSalary: "£0 - £100,000 (very wide)",
    seniorSalary: "£0 - £millions (very wide)",
    timeToEntry: "Immediate, but 3 - 7 years to a real business",
    degreeRequired: "Not required",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Wherever you make it - long hours common",
    stability: "Unstable income, especially early on",
    weights: { freedom: 3, income: 2, creativity: 2, people: 1, structure: -2 },
    skillsNeeded: ["Self-motivation", "Sales", "Resilience", "Decision making"],
    isAlternativePath: true,
    specialisations: [],
  },
  {
    name: "Freelancer",
    category: "Self-Employed and Business Owner",
    shortDescription: "Sell a skill - design, writing, dev, marketing - directly to clients.",
    whyItFits: "You have a skill, like control over your time, and can manage client work.",
    startingSalary: "£15,000 - £30,000 (early)",
    midSalary: "£40,000 - £80,000",
    seniorSalary: "£90,000 - £200,000+",
    timeToEntry: "0 - 2 years (after building a skill)",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Anywhere - your own setup",
    stability: "Income varies month to month",
    weights: { freedom: 3, creativity: 2, income: 1, people: 0, structure: -2 },
    skillsNeeded: ["A real craft", "Self-discipline", "Sales", "Client management"],
    isAlternativePath: true,
    specialisations: [],
  },
  // ---------------- Batch 2 expansion ----------------
  {
    name: "AI Engineer",
    category: "Technology",
    shortDescription: "Build AI-powered features by integrating large language models, vector databases and APIs into real products.",
    whyItFits: "You like shipping working software and applying cutting-edge AI to real user problems, more than writing research papers.",
    startingSalary: "£40,000 - £65,000",
    midSalary: "£75,000 - £120,000",
    seniorSalary: "£130,000 - £220,000+",
    timeToEntry: "2 - 4 years",
    degreeRequired: "Helpful (CS, maths) but strong portfolios are accepted",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Office or remote, deep focus + cross-team collaboration",
    stability: "Booming - high demand for AI product builders",
    weights: { structure: 2, income: 3, creativity: 2, freedom: 1, people: 0 },
    skillsNeeded: ["Python or TypeScript", "LLM APIs and prompting", "RAG and vector DBs", "Systems design", "Evaluation and testing"],
    specialisations: [
      { title: "LLM Application Engineer", shortDescription: "Build chatbots, copilots and AI features on top of foundation models.", typicalEntryRoute: "Move from SWE + ship 2-3 LLM side-projects", salaryRange: "£60,000 - £150,000" },
      { title: "AI Product Engineer", shortDescription: "Own the full AI feature lifecycle - design, prototype, ship, evaluate.", typicalEntryRoute: "Strong product engineer + AI portfolio", salaryRange: "£70,000 - £170,000" },
    ],
    similarCareers: ["Machine Learning Engineer", "Software Engineer", "Data Scientist"],
    higherProgressionCareers: ["Machine Learning Engineer", "Technical Product Manager"],
    entryLevelAlternatives: ["Software Engineer", "Data Analyst"],
  },
  {
    name: "Machine Learning Engineer",
    category: "Technology",
    shortDescription: "Train, deploy and maintain ML models that power recommendations, predictions, NLP and vision.",
    whyItFits: "You enjoy maths, code and turning messy data into models that actually run reliably in production.",
    startingSalary: "£45,000 - £70,000",
    midSalary: "£80,000 - £130,000",
    seniorSalary: "£140,000 - £220,000+",
    timeToEntry: "3 - 5 Years",
    degreeRequired: "Usually required (CS, maths, stats or related)",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Office or remote, deep focus work",
    stability: "Strong - core hire at any company doing serious ML",
    weights: { structure: 3, income: 3, creativity: 1, freedom: 1, people: -1 },
    skillsNeeded: ["Python", "Maths and stats", "PyTorch or TensorFlow", "MLOps", "Data engineering basics"],
    specialisations: [
      { title: "MLOps Engineer", shortDescription: "Get ML models running reliably in production at scale.", typicalEntryRoute: "Move from SWE/DevOps + ML projects", salaryRange: "£60,000 - £140,000" },
      { title: "NLP Engineer", shortDescription: "Build systems that understand and generate language.", typicalEntryRoute: "MSc + NLP projects", salaryRange: "£60,000 - £150,000" },
      { title: "Applied Research Engineer", shortDescription: "Take cutting-edge papers and turn them into products.", typicalEntryRoute: "PhD or strong publications", salaryRange: "£80,000 - £220,000" },
    ],
    similarCareers: ["AI Engineer", "Data Scientist", "Data Engineer"],
    higherProgressionCareers: ["AI Engineer", "Research Scientist"],
    entryLevelAlternatives: ["Data Analyst", "Software Engineer"],
  },
  {
    name: "UX Researcher",
    category: "Design and Creative",
    shortDescription: "Talk to real users and figure out what they actually need from a product.",
    whyItFits: "You're curious about people and like asking 'why?' more than 'what?'",
    startingSalary: "£28,000 - £40,000",
    midSalary: "£45,000 - £70,000",
    seniorSalary: "£75,000 - £110,000",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Helpful (psychology, anthropology, HCI)",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Mix of interviews, workshops, and desk research",
    stability: "Growing field but cyclical with tech hiring",
    weights: { people: 2, creativity: 2, structure: 1, freedom: 1, income: 0 },
    skillsNeeded: ["Listening", "Synthesis", "Writing", "Interview skills"],
    specialisations: [
      { title: "Generative Researcher", shortDescription: "Run open-ended discovery to find new product opportunities.", typicalEntryRoute: "Research degree or junior research role", salaryRange: "£30,000 - £75,000" },
      { title: "Evaluative Researcher", shortDescription: "Test specific designs to make sure they actually work.", typicalEntryRoute: "UX agency or in-house junior role", salaryRange: "£28,000 - £70,000" },
    ],
  },
  {
    name: "Recruiter",
    category: "Business, Finance and Legal",
    shortDescription: "Match people with jobs - either in-house or running your own desk at an agency.",
    whyItFits: "You're persuasive, thrive on targets, and genuinely enjoy talking to people.",
    startingSalary: "£22,000 - £30,000 + commission",
    midSalary: "£40,000 - £80,000 OTE",
    seniorSalary: "£100,000 - £200,000+ OTE",
    timeToEntry: "0 - 1 year",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office (agency) or hybrid (in-house)",
    stability: "Cyclical with hiring market",
    weights: { people: 3, income: 3, freedom: 1, structure: 0, creativity: 0 },
    skillsNeeded: ["Resilience", "Listening", "Pitching", "Networking"],
    specialisations: [
      { title: "Tech Recruiter", shortDescription: "Place software engineers, designers, and data roles.", typicalEntryRoute: "Trainee at a tech recruitment agency", salaryRange: "£25,000 - £150,000 OTE" },
      { title: "Executive Search Consultant", shortDescription: "Headhunt senior leaders for big companies.", typicalEntryRoute: "Researcher -> consultant at a search firm", salaryRange: "£40,000 - £250,000 OTE" },
      { title: "In-house Talent Partner", shortDescription: "Hire for a single company, no commission, more strategy.", typicalEntryRoute: "Move from agency or HR background", salaryRange: "£40,000 - £90,000" },
    ],
  },
  {
    name: "Vet",
    category: "Healthcare and Care",
    shortDescription: "Diagnose and treat animals - companion, farm, or exotic.",
    whyItFits: "You love animals, can handle hard decisions, and have stamina for long study.",
    startingSalary: "£32,000 - £40,000",
    midSalary: "£45,000 - £65,000",
    seniorSalary: "£70,000 - £120,000+ (specialist/owner)",
    timeToEntry: "5 - 6 years (BVetMed/BVMS)",
    degreeRequired: "Required",
    difficulty: "High",
    competitionLevel: "Extremely High",
    workEnvironment: "Clinic, farm, or zoo - emotionally demanding",
    stability: "Very stable",
    weights: { people: 1, structure: 2, creativity: 0, freedom: 0, income: 1 },
    skillsNeeded: ["Empathy", "Decision making", "Stamina", "Communication with owners"],
    specialisations: [
      { title: "Equine Vet", shortDescription: "Specialise in horses - racing, sport, and stud work.", typicalEntryRoute: "Vet degree + equine internship", salaryRange: "£35,000 - £80,000" },
      { title: "Veterinary Surgeon (Surgery)", shortDescription: "Specialise in advanced surgical procedures.", typicalEntryRoute: "Vet degree + surgical residency", salaryRange: "£60,000 - £120,000" },
    ],
    selfEmployable: true,
  },
  {
    name: "Social Worker",
    category: "Public Sector and Emergency Services",
    shortDescription: "Support vulnerable children, families, or adults - often through council services.",
    whyItFits: "You care deeply, can hold difficult emotions, and want meaningful work.",
    startingSalary: "£28,000 - £35,000",
    midSalary: "£38,000 - £48,000",
    seniorSalary: "£50,000 - £70,000+ (team manager)",
    timeToEntry: "3 - 4 years (BA/MSc Social Work)",
    degreeRequired: "Required",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Office + home visits - emotionally heavy",
    stability: "Very stable - chronic UK shortage",
    weights: { people: 3, structure: 1, creativity: 0, freedom: 0, income: 0 },
    skillsNeeded: ["Empathy", "Boundaries", "Risk assessment", "Resilience"],
    specialisations: [
      { title: "Children's Social Worker", shortDescription: "Protect children at risk and support families.", typicalEntryRoute: "Social work degree + assessed year", salaryRange: "£32,000 - £55,000" },
      { title: "Mental Health Social Worker", shortDescription: "Support adults with mental health needs (often AMHP role).", typicalEntryRoute: "Social work degree + AMHP training", salaryRange: "£35,000 - £55,000" },
    ],
  },

  // ---------------- Batch 3 expansion: thin categories ----------------

  // Construction and Infrastructure
  {
    name: "Site Manager",
    category: "Engineering, Construction and Infrastructure",
    shortDescription: "Run a construction site day-to-day - workers, subcontractors, safety, and schedule.",
    whyItFits: "You're organised, decisive, and good at keeping a busy operation moving.",
    startingSalary: "£32,000 - £42,000",
    midSalary: "£45,000 - £65,000",
    seniorSalary: "£70,000 - £100,000+",
    timeToEntry: "3 - 6 years (often via trade or degree)",
    degreeRequired: "Construction degree helpful, not required",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "On-site, outdoors, all weather",
    stability: "Strong demand, project-based",
    weights: { people: 2, structure: 3, income: 2, freedom: 0, creativity: 0 },
    skillsNeeded: ["Leadership", "Planning", "Health and safety", "Problem solving"],
    specialisations: [
      { title: "Assistant Site Manager", shortDescription: "Support a senior site manager - first step into running a site.", typicalEntryRoute: "Trade background or construction degree", salaryRange: "£28,000 - £42,000" },
      { title: "Project Manager (Construction)", shortDescription: "Run multiple sites or large projects across phases.", typicalEntryRoute: "Site manager + APM / PRINCE2", salaryRange: "£50,000 - £90,000" },
    ],
  },
  {
    name: "Civil Engineering Technician",
    category: "Engineering, Construction and Infrastructure",
    shortDescription: "Support civil engineers on infrastructure projects - roads, bridges, water, rail.",
    whyItFits: "You like technical, hands-on work that keeps the country running.",
    startingSalary: "£22,000 - £30,000",
    midSalary: "£32,000 - £45,000",
    seniorSalary: "£48,000 - £65,000",
    timeToEntry: "1 - 3 years (apprenticeship or HNC)",
    degreeRequired: "Not required - HNC/HND or apprenticeship",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Mix of office and site",
    stability: "Strong long-term infrastructure demand",
    weights: { structure: 2, income: 1, creativity: 0, people: 0, freedom: 0 },
    skillsNeeded: ["CAD", "Maths", "Site awareness", "Technical drawing"],
    specialisations: [
      { title: "CAD Technician", shortDescription: "Produce detailed drawings for civil projects.", typicalEntryRoute: "HNC + AutoCAD/Revit skills", salaryRange: "£24,000 - £45,000" },
      { title: "Setting Out Engineer", shortDescription: "Translate designs into precise positions on site.", typicalEntryRoute: "Apprenticeship or technician role", salaryRange: "£30,000 - £55,000" },
    ],
  },
  {
    name: "Scaffolder",
    category: "Engineering, Construction and Infrastructure",
    shortDescription: "Erect and dismantle scaffolding for construction and maintenance projects.",
    whyItFits: "You're physical, comfortable at heights, and want to earn well without a degree.",
    startingSalary: "£22,000 - £28,000",
    midSalary: "£32,000 - £45,000",
    seniorSalary: "£50,000 - £70,000 (advanced/supervisor)",
    timeToEntry: "1 - 3 years (CISRS card)",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Outdoors, all sites and weather",
    stability: "Steady demand on construction projects",
    weights: { freedom: 1, income: 1, structure: 1, people: 0, creativity: -1 },
    skillsNeeded: ["Physical fitness", "Head for heights", "Teamwork", "Safety awareness"],
    specialisations: [
      { title: "Advanced Scaffolder", shortDescription: "Complex scaffolding designs and supervision on big sites.", typicalEntryRoute: "CISRS Advanced after 2-3 years", salaryRange: "£40,000 - £60,000" },
    ],
  },

  // Science and Research
  {
    name: "Laboratory Technician",
    category: "Education, Science and Research",
    shortDescription: "Run experiments, prepare samples, and maintain lab equipment for research or industry.",
    whyItFits: "You're methodical, careful, and enjoy structured scientific work.",
    startingSalary: "£20,000 - £26,000",
    midSalary: "£28,000 - £38,000",
    seniorSalary: "£40,000 - £55,000",
    timeToEntry: "1 - 2 years",
    degreeRequired: "Not required - science A-levels or BTEC",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Lab, often shift-based",
    stability: "Steady across pharma, NHS, food, manufacturing",
    weights: { structure: 3, creativity: 0, income: 0, people: 0, freedom: -1 },
    skillsNeeded: ["Lab procedures", "Attention to detail", "Record keeping", "Following protocols"],
    specialisations: [
      { title: "Biomedical Scientist (NHS)", shortDescription: "Test patient samples to support diagnosis - regulated role.", typicalEntryRoute: "Accredited biomed science degree + HCPC", salaryRange: "£28,000 - £55,000" },
      { title: "QC Analyst", shortDescription: "Test products in pharma, food, or chemicals against spec.", typicalEntryRoute: "Science degree or HND", salaryRange: "£26,000 - £45,000" },
    ],
  },
  {
    name: "Environmental Scientist",
    category: "Education, Science and Research",
    shortDescription: "Study and protect ecosystems, water, air, and soil - often advising on developments.",
    whyItFits: "You care about the planet and want technical work with real-world impact.",
    startingSalary: "£24,000 - £30,000",
    midSalary: "£35,000 - £50,000",
    seniorSalary: "£55,000 - £75,000",
    timeToEntry: "1 - 3 years post-degree",
    degreeRequired: "Environmental / earth science degree typical",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Mix of office, lab, and fieldwork",
    stability: "Growing - net zero, ESG drive demand",
    weights: { structure: 2, creativity: 1, freedom: 1, people: 0, income: 0 },
    skillsNeeded: ["Data analysis", "Field sampling", "Report writing", "Regulatory knowledge"],
    specialisations: [
      { title: "Ecologist", shortDescription: "Survey wildlife and habitats, often for planning applications.", typicalEntryRoute: "Ecology degree + CIEEM membership", salaryRange: "£24,000 - £55,000" },
      { title: "Sustainability Consultant", shortDescription: "Advise companies on reducing carbon and meeting ESG targets.", typicalEntryRoute: "Env. degree + consultancy entry role", salaryRange: "£30,000 - £70,000" },
    ],
  },

  // Agriculture and Outdoor Work
  {
    name: "Forestry Worker",
    category: "Skilled Trades",
    shortDescription: "Plant, manage, and harvest trees - working in woodlands and forests.",
    whyItFits: "You want outdoor, physical work and care about land and nature.",
    startingSalary: "£20,000 - £26,000",
    midSalary: "£28,000 - £38,000",
    seniorSalary: "£40,000 - £55,000",
    timeToEntry: "0 - 2 years",
    degreeRequired: "Not required - NPTC tickets matter",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Outdoors, remote, all weather",
    stability: "Steady - growing with rewilding and net zero",
    weights: { freedom: 2, structure: 1, people: -1, creativity: 0, income: -1 },
    skillsNeeded: ["Chainsaw skills", "Stamina", "Map reading", "Safety awareness"],
    specialisations: [
      { title: "Arborist / Tree Surgeon", shortDescription: "Climb, prune, and fell trees in urban and rural settings.", typicalEntryRoute: "NPTC chainsaw + climbing tickets", salaryRange: "£24,000 - £45,000" },
      { title: "Forest Manager", shortDescription: "Plan and manage commercial woodland operations.", typicalEntryRoute: "Forestry degree + ICF route", salaryRange: "£32,000 - £55,000" },
    ],
  },

  // Sport and Performance
  {
    name: "Sports Therapist",
    category: "Sport, Fitness and Wellbeing",
    shortDescription: "Prevent and treat sports injuries with massage, rehab, and exercise plans.",
    whyItFits: "You want hands-on healthcare in a sporting environment.",
    startingSalary: "£22,000 - £28,000",
    midSalary: "£30,000 - £45,000",
    seniorSalary: "£50,000 - £75,000 (private practice)",
    timeToEntry: "3 - 4 years (degree)",
    degreeRequired: "BSc Sports Therapy",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Clinic, club, or pitchside",
    stability: "Steady - private clinics + sports clubs",
    weights: { people: 3, structure: 2, creativity: 0, freedom: 1, income: 0 },
    skillsNeeded: ["Anatomy", "Manual therapy", "Communication", "Empathy"],
    specialisations: [
      { title: "Pitchside Therapist", shortDescription: "Provide immediate care at sports events.", typicalEntryRoute: "Degree + ITMMP / pitchside qualifications", salaryRange: "£24,000 - £45,000" },
    ],
    selfEmployable: true,
  },

  // Cleaning, Facilities, and Maintenance
  {
    name: "Maintenance Engineer",
    category: "Skilled Trades",
    shortDescription: "Keep machinery, buildings, or equipment running - planned and reactive maintenance.",
    whyItFits: "You like fixing things, working with your hands, and solving practical problems.",
    startingSalary: "£24,000 - £32,000",
    midSalary: "£35,000 - £48,000",
    seniorSalary: "£50,000 - £70,000",
    timeToEntry: "2 - 4 years (apprenticeship)",
    degreeRequired: "Not required - apprenticeship typical",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Factory, building, or mobile",
    stability: "Strong - critical role across industries",
    weights: { structure: 2, freedom: 1, income: 1, creativity: 0, people: -1 },
    skillsNeeded: ["Mechanical skills", "Electrical knowledge", "Diagnostics", "Problem solving"],
    specialisations: [
      { title: "Multi-Skilled Engineer", shortDescription: "Cover both mechanical and electrical maintenance in industry.", typicalEntryRoute: "Apprenticeship + 17th/18th edition", salaryRange: "£32,000 - £55,000" },
      { title: "Building Services Engineer", shortDescription: "Maintain HVAC, lifts, and plant in commercial buildings.", typicalEntryRoute: "Trade background + BMS training", salaryRange: "£30,000 - £55,000" },
    ],
  },

  // Healthcare and Care (more)
  {
    name: "Healthcare Assistant",
    category: "Healthcare and Care",
    shortDescription: "Support nurses and patients in hospitals, care homes, or community settings.",
    whyItFits: "You're caring, patient, and want a meaningful job that helps people directly.",
    startingSalary: "£22,000 - £25,000",
    midSalary: "£24,000 - £28,000",
    seniorSalary: "£28,000 - £33,000",
    timeToEntry: "0 - 6 months",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Hospital, care home, or community; shifts",
    stability: "Very strong NHS / care demand",
    weights: { people: 3, structure: 2, creativity: 0, income: -2, freedom: -1 },
    skillsNeeded: ["Empathy", "Stamina", "Personal care", "Teamwork"],
    specialisations: [
      { title: "Nursing Associate", shortDescription: "Bridge role between HCA and registered nurse.", typicalEntryRoute: "2-year apprenticeship via NHS", salaryRange: "£25,000 - £30,000" },
      { title: "Maternity Support Worker", shortDescription: "Support midwives and new mums on maternity wards.", typicalEntryRoute: "HCA experience + maternity training", salaryRange: "£22,000 - £28,000" },
    ],
  },
  {
    name: "Dental Nurse",
    category: "Healthcare and Care",
    shortDescription: "Assist dentists during treatment, prepare instruments, and care for patients.",
    whyItFits: "You're calm, organised, and good at putting nervous people at ease.",
    startingSalary: "£20,000 - £25,000",
    midSalary: "£26,000 - £32,000",
    seniorSalary: "£33,000 - £40,000",
    timeToEntry: "1 - 2 years (GDC registration)",
    degreeRequired: "Not required - NEBDN diploma",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Dental practice, regular hours",
    stability: "Steady - strong UK demand",
    weights: { people: 2, structure: 3, creativity: 0, freedom: -1, income: -1 },
    skillsNeeded: ["Infection control", "Patient care", "Manual dexterity", "Calm under pressure"],
    specialisations: [
      { title: "Orthodontic Nurse", shortDescription: "Specialise in supporting orthodontic treatments.", typicalEntryRoute: "Qualified DN + orthodontic certificate", salaryRange: "£26,000 - £35,000" },
    ],
  },

  // Public Sector / Emergency Services (more)
  {
    name: "Civil Servant (Generalist)",
    category: "Public Sector and Emergency Services",
    shortDescription: "Work in government departments shaping and delivering public policy and services.",
    whyItFits: "You want stable, varied work with public purpose and clear progression.",
    startingSalary: "£25,000 - £32,000",
    midSalary: "£40,000 - £60,000",
    seniorSalary: "£70,000 - £100,000+ (SCS)",
    timeToEntry: "1 - 3 years (often via grad scheme)",
    degreeRequired: "Often expected, not strict",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Office or hybrid, often London or hub cities",
    stability: "Very strong - excellent pension",
    weights: { structure: 3, people: 2, income: 1, creativity: 0, freedom: 0 },
    skillsNeeded: ["Written communication", "Analysis", "Stakeholder management", "Discretion"],
    specialisations: [
      { title: "Policy Adviser", shortDescription: "Develop and analyse government policy options.", typicalEntryRoute: "Fast Stream or direct policy entry", salaryRange: "£32,000 - £75,000" },
      { title: "Operational Delivery", shortDescription: "Run public-facing services like benefits, passports, or visas.", typicalEntryRoute: "EO/HEO entry + internal progression", salaryRange: "£25,000 - £50,000" },
    ],
  },
  {
    name: "Prison Officer",
    category: "Public Sector and Emergency Services",
    shortDescription: "Maintain order in prisons and support rehabilitation of offenders.",
    whyItFits: "You're calm under pressure, fair, and want a stable public-service role.",
    startingSalary: "£30,000 - £35,000",
    midSalary: "£36,000 - £42,000",
    seniorSalary: "£45,000 - £60,000 (manager)",
    timeToEntry: "0 - 12 months",
    degreeRequired: "Not required",
    difficulty: "High",
    competitionLevel: "Low",
    workEnvironment: "Prison, shifts including nights",
    stability: "Very stable, strong pension",
    weights: { structure: 3, people: 2, income: 1, creativity: -1, freedom: -1 },
    skillsNeeded: ["De-escalation", "Resilience", "Communication", "Decision making"],
    specialisations: [
      { title: "Custodial Manager", shortDescription: "Lead a team of officers and run a wing.", typicalEntryRoute: "Internal promotion after 3-5 years", salaryRange: "£40,000 - £55,000" },
    ],
  },

  // Engineering and Manufacturing (more)
  {
    name: "Electrical Engineer",
    category: "Engineering, Construction and Infrastructure",
    shortDescription: "Design and maintain electrical systems - from circuits to power grids.",
    whyItFits: "You're technical, methodical, and want a future-proof career.",
    startingSalary: "£28,000 - £36,000",
    midSalary: "£40,000 - £58,000",
    seniorSalary: "£65,000 - £95,000",
    timeToEntry: "3 - 5 Years",
    degreeRequired: "Electrical engineering degree typical",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Office, lab, or site",
    stability: "Very strong - net zero, EVs, infrastructure",
    weights: { structure: 3, income: 2, creativity: 1, people: 0, freedom: 0 },
    skillsNeeded: ["Circuit analysis", "Maths", "CAD", "Standards knowledge"],
    specialisations: [
      { title: "Power Systems Engineer", shortDescription: "Design and maintain electricity transmission and distribution.", typicalEntryRoute: "EE degree + grad scheme (NG, SSE)", salaryRange: "£35,000 - £80,000" },
      { title: "Controls Engineer", shortDescription: "Program PLCs and automation systems in factories.", typicalEntryRoute: "EE / mechatronics degree + PLC training", salaryRange: "£32,000 - £70,000" },
    ],
  },

  // Education and Training (more)

  // Hospitality and Food (more)
  {
    name: "Pastry Chef",
    category: "Hospitality and Food",
    shortDescription: "Create pastries, breads, and desserts in restaurants, hotels, or bakeries.",
    whyItFits: "You're patient, precise, and love creative work with your hands.",
    startingSalary: "£20,000 - £25,000",
    midSalary: "£28,000 - £38,000",
    seniorSalary: "£42,000 - £65,000",
    timeToEntry: "2 - 4 years",
    degreeRequired: "Not required - patisserie diploma helps",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Kitchen, very early starts",
    stability: "Steady - strong demand at all levels",
    weights: { creativity: 2, structure: 2, freedom: 0, people: 0, income: -1 },
    skillsNeeded: ["Precision", "Stamina", "Creativity", "Time management"],
    specialisations: [
      { title: "Baker", shortDescription: "Specialise in breads and viennoiserie - often own bakery.", typicalEntryRoute: "Bakery apprenticeship or self-taught", salaryRange: "£22,000 - £45,000" },
      { title: "Head Pastry Chef", shortDescription: "Lead the pastry section in a fine-dining kitchen.", typicalEntryRoute: "8-10 years through kitchen brigade", salaryRange: "£40,000 - £65,000" },
    ],
    selfEmployable: true,
  },

  // Marketing and Media (more)
  {
    name: "SEO Specialist",
    category: "Marketing and Media",
    shortDescription: "Help websites rank higher on Google through technical, content, and link strategy.",
    whyItFits: "You're analytical, curious, and like work that mixes data with creativity.",
    startingSalary: "£22,000 - £30,000",
    midSalary: "£35,000 - £55,000",
    seniorSalary: "£60,000 - £90,000",
    timeToEntry: "1 - 2 years",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office, remote, or agency",
    stability: "Steady - core to digital marketing",
    weights: { structure: 2, creativity: 1, income: 1, freedom: 1, people: 0 },
    skillsNeeded: ["Keyword research", "Analytics", "Content strategy", "Technical basics"],
    specialisations: [
      { title: "Technical SEO", shortDescription: "Focus on site speed, crawling, and indexing for large sites.", typicalEntryRoute: "SEO + light dev knowledge", salaryRange: "£35,000 - £75,000" },
      { title: "Content / Editorial SEO", shortDescription: "Plan and oversee content that ranks and converts.", typicalEntryRoute: "Writing background + SEO upskill", salaryRange: "£28,000 - £60,000" },
    ],
    selfEmployable: true,
    soloMeta: {
      startupCostGBP: "£200 - £1,500 (laptop, tools, portfolio site)",
      timeToFirstClient: "3 - 8 weeks",
      monthOneIncome: "£0 - £2,000",
      yearOneIncome: "£20,000 - £50,000",
      scaledIncome: "£60,000 - £150,000+ with retainers, productised audits, or own SaaS",
      commonPitfalls: ["Promising rankings - SEO has no guarantees and Google updates kill promises", "Charging hourly for retainers - clients see SEO as 'why am I paying for this month'", "No reporting dashboard - clients churn when they can't see what you did", "Trying to rank for everything - one niche pays much better than generalist work"],
      firstFiveClients: ["Run a free SEO audit for 5 small businesses in your chosen niche - 1-2 will hire you", "Post weekly LinkedIn breakdowns of brands' SEO mistakes - inbound leads compound", "Pick a niche (DTC ecom, B2B SaaS, local services) and pitch 30 founders directly", "Subcontract for an established SEO agency on overflow content briefs", "Build your own niche site and rank it - the strongest possible case study"],
      toolsAndCosts: ["Ahrefs or Semrush £100/month", "Screaming Frog £200/year", "Google Search Console + Analytics free", "Looker Studio free", "Domain + portfolio site £15/month"],
      legalBasics: ["Sole trader or Ltd", "Professional indemnity insurance £1m+", "Written retainer contracts with 60-day notice", "GDPR: client analytics access via delegated permissions"],
    },
  },

  // Skilled Trades (more)
  {
    name: "Heating and Gas Engineer",
    category: "Skilled Trades",
    shortDescription: "Install, service, and repair gas boilers and heating systems in homes and businesses.",
    whyItFits: "You want a hands-on trade with strong earnings and self-employment potential.",
    startingSalary: "£24,000 - £32,000",
    midSalary: "£35,000 - £50,000",
    seniorSalary: "£55,000 - £80,000+ (self-employed)",
    timeToEntry: "2 - 4 years (apprenticeship + Gas Safe)",
    degreeRequired: "Not required - Gas Safe registration essential",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Customer homes, vans, all weather",
    stability: "Very strong - shortage of engineers",
    weights: { freedom: 2, income: 2, structure: 1, people: 1, creativity: 0 },
    skillsNeeded: ["Diagnostics", "Customer skills", "Safety awareness", "Problem solving"],
    specialisations: [
      { title: "Heat Pump Installer", shortDescription: "Install renewable heat pumps - growing area with grants.", typicalEntryRoute: "Plumbing/heating background + MCS training", salaryRange: "£35,000 - £65,000" },
    ],
    selfEmployable: true,
  },
  {
    name: "Welder",
    category: "Skilled Trades",
    shortDescription: "Join metal parts using heat - in manufacturing, construction, or oil and gas.",
    whyItFits: "You like skilled, focused, hands-on work with clear progression.",
    startingSalary: "£22,000 - £30,000",
    midSalary: "£32,000 - £45,000",
    seniorSalary: "£50,000 - £80,000 (coded / offshore)",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Not required - NVQ + tickets",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "Workshop, site, or offshore",
    stability: "Strong - chronic UK shortage",
    weights: { structure: 2, income: 2, freedom: 1, people: -1, creativity: 0 },
    skillsNeeded: ["Hand-eye coordination", "Patience", "Reading drawings", "Safety"],
    specialisations: [
      { title: "Coded Welder", shortDescription: "Welds tested and approved for high-pressure or critical work.", typicalEntryRoute: "Coding tests in TIG/MIG/MMA", salaryRange: "£35,000 - £70,000" },
      { title: "Pipe Welder (Offshore)", shortDescription: "High-paying offshore work on rigs and platforms.", typicalEntryRoute: "Coded + offshore survival training", salaryRange: "£50,000 - £100,000" },
    ],
    selfEmployable: true,
  },

  // Transport and Logistics (more)
  {
    name: "Logistics Coordinator",
    category: "Transport and Logistics",
    shortDescription: "Plan and track movement of goods between suppliers, warehouses, and customers.",
    whyItFits: "You're organised, calm under pressure, and like solving real-time puzzles.",
    startingSalary: "£22,000 - £28,000",
    midSalary: "£30,000 - £42,000",
    seniorSalary: "£48,000 - £65,000",
    timeToEntry: "0 - 2 years",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office, warehouse-adjacent",
    stability: "Strong - core to every supply chain",
    weights: { structure: 3, people: 1, income: 0, creativity: 0, freedom: 0 },
    skillsNeeded: ["Organisation", "Spreadsheets", "Communication", "Problem solving"],
    specialisations: [
      { title: "Supply Chain Analyst", shortDescription: "Use data to optimise stock, demand, and supplier performance.", typicalEntryRoute: "Logistics or analytics background", salaryRange: "£30,000 - £55,000" },
      { title: "Freight Forwarder", shortDescription: "Coordinate international shipping by air, sea, or road.", typicalEntryRoute: "Entry role + BIFA training", salaryRange: "£25,000 - £50,000" },
    ],
  },
  {
    name: "Train Driver",
    category: "Transport and Logistics",
    shortDescription: "Drive passenger or freight trains across the UK rail network.",
    whyItFits: "You want a stable, well-paid role with structure and clear shifts.",
    startingSalary: "£32,000 - £40,000 (trainee)",
    midSalary: "£50,000 - £65,000",
    seniorSalary: "£65,000 - £75,000+",
    timeToEntry: "12 - 18 months training",
    degreeRequired: "Not required - very competitive entry",
    difficulty: "Medium",
    competitionLevel: "Extremely High",
    workEnvironment: "Cab; shifts including nights / weekends",
    stability: "Very stable, strong pension",
    weights: { structure: 3, income: 2, freedom: -1, people: -1, creativity: -1 },
    skillsNeeded: ["Concentration", "Calm temperament", "Reliability", "Following procedures"],
    specialisations: [
      { title: "Freight Train Driver", shortDescription: "Drive freight services - often more nights, fewer passengers.", typicalEntryRoute: "Apply to freight operators (DB, GBRf)", salaryRange: "£45,000 - £65,000" },
    ],
  },

  // Business and Operations (more)
  {
    name: "Mobile Mechanic",
    category: "Self-Employed and Business Owner",
    shortDescription: "Drive to customers' homes or workplaces and fix their cars where they parked them.",
    whyItFits: "You like working with your hands, fixing real problems, and being your own boss. No workshop overheads, no rent.",
    startingSalary: "£25,000 - £35,000",
    midSalary: "£35,000 - £55,000",
    seniorSalary: "£55,000 - £80,000",
    timeToEntry: "Immediate if qualified, 2 - 4 years to train from scratch",
    degreeRequired: "Not required - Level 2/3 vehicle maintenance or apprenticeship route",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Driveways, car parks, roadside - in all weather",
    stability: "Steady demand; recession-resistant since people fix old cars when they can't afford new ones",
    weights: { freedom: 3, income: 1, structure: -1, creativity: 0, people: 1 },
    skillsNeeded: ["Diagnostic skills", "Customer communication", "Pricing on the spot", "Time management", "Logistics"],
    specialisations: [
      { title: "EV / Hybrid Specialist", shortDescription: "Service and repair electric and hybrid vehicles - the highest-margin niche.", typicalEntryRoute: "Standard mechanic + IMI EV qualification (Level 2-4)", salaryRange: "£35,000 - £85,000" },
      { title: "Classic Car Specialist", shortDescription: "Mobile servicing for owners of pre-1990s vehicles - loyal, premium clientele.", typicalEntryRoute: "Standard qualification + niche reputation", salaryRange: "£30,000 - £65,000" },
    ],
    selfEmploymentOnly: true,
    soloMeta: {
      startupCostGBP: "£3,000 - £10,000",
      timeToFirstClient: "1 - 4 weeks",
      monthOneIncome: "£800 - £2,500",
      yearOneIncome: "£25,000 - £42,000",
      scaledIncome: "£45,000 - £80,000+",
      commonPitfalls: [
        "Saying yes to jobs you can't finish on a driveway (engine-out work)",
        "Not charging a call-out fee - lose money on no-shows",
        "Buying parts on personal card, losing track of job profitability",
        "Working evenings to please customers, burning out by month 6"
      ],
      firstFiveClients: [
        "Run a £25 'pre-MOT mobile check' offer on Facebook Marketplace - converts to repair work 60% of the time",
        "Get on Google Maps with photos of completed jobs - 'mobile mechanic [town]' is high intent",
        "Partner with one used-car dealer for prep/MOT work - steady weekly income",
        "Drop cards at 5 local taxi/private hire ranks - drivers need fast, reliable repairs",
        "Offer free vehicle health checks at one school car park (with permission) - parents talk"
      ],
      toolsAndCosts: [
        "Mobile tool kit + diagnostic scanner: £2,000 - £6,000",
        "Van: £3,000 - £15,000",
        "Public liability + tools-in-transit insurance: £400 - £900/year",
        "Diagnostic data subscription (Autodata): £600/year",
        "Trade parts account (Euro Car Parts, GSF): free, 30-40% trade discount"
      ],
      legalBasics: [
        "Register self-employed with HMRC and get your UTR",
        "Motor trade insurance needed if you drive customer cars (much more than standard van insurance)",
        "Waste oil and tyres must go via licensed collectors - keep Duty of Care receipts",
        "MOT testing requires VOSA cert + approved test centre - not viable as a mobile-only operator",
        "VAT mandatory at £90,000 turnover"
      ],
    },
  },
  {
    name: "Handyman / Property Maintenance",
    category: "Self-Employed and Business Owner",
    shortDescription: "Tackle the small jobs every household needs - shelving, flat-pack, leaks, doors, painting, fence panels.",
    whyItFits: "You're practical, like variety, and don't want to specialise in one trade. Demand is endless and the barrier to entry is low.",
    startingSalary: "£22,000 - £32,000",
    midSalary: "£32,000 - £45,000",
    seniorSalary: "£45,000 - £65,000",
    timeToEntry: "Immediate - no formal qualifications required",
    degreeRequired: "Not required",
    difficulty: "Low",
    competitionLevel: "Moderate",
    workEnvironment: "Customer homes, mostly indoors, some outdoor",
    stability: "Strong - every household needs small jobs done",
    weights: { freedom: 3, structure: -1, income: 0, creativity: 0, people: 2 },
    skillsNeeded: ["Practical problem-solving", "Multiple trade basics", "Customer rapport", "Pricing per job", "Time-blocking your day"],
    specialisations: [
      { title: "Property Maintenance for Landlords", shortDescription: "Recurring contracts maintaining rental properties - the highest-stability handyman model.", typicalEntryRoute: "Build relationships with 3-5 local letting agents", salaryRange: "£28,000 - £55,000" },
      { title: "Disability / Mobility Adaptations", shortDescription: "Install grab rails, ramps, stair rails and other home adaptations for older or disabled homeowners.", typicalEntryRoute: "Standard handyman + approved contractor lists with local councils", salaryRange: "£30,000 - £60,000" },
    ],
    selfEmploymentOnly: true,
    soloMeta: {
      startupCostGBP: "£500 - £2,500",
      timeToFirstClient: "1 - 2 weeks",
      monthOneIncome: "£800 - £2,500",
      yearOneIncome: "£22,000 - £38,000",
      scaledIncome: "£40,000 - £65,000+",
      commonPitfalls: [
        "Charging by the hour - you'll never out-earn an employed wage doing this",
        "Saying yes to large jobs (kitchens, bathrooms) you should sub out",
        "No call-out minimum - drive 30 minutes for a £40 job and lose money",
        "Doing free 'while I'm here' jobs - kills your day rate over a year"
      ],
      firstFiveClients: [
        "Post in 5 local Facebook community groups with photos of 3 finished small jobs",
        "Sign up to TaskRabbit, MyBuilder and Bark to win first 5 reviews",
        "Drop 200 leaflets through one estate's doors with a clear price list ('Hang a TV £45, fit a shelf £25')",
        "Build a Google Business Profile - 'handyman near me' is high-intent",
        "Tell 3 letting agents you're available for tenant-fault repairs - steady weekly bookings"
      ],
      toolsAndCosts: [
        "Core hand and power tool kit: £400 - £1,200",
        "Van or large estate car: £2,000 - £8,000 (start with car if needed)",
        "Public liability insurance: £100 - £250/year",
        "Mobile card reader (SumUp): £30 + 1.69% per transaction",
        "Workwear, ladders, basic safety kit: £200 - £400"
      ],
      legalBasics: [
        "Register self-employed with HMRC and get your UTR",
        "Public liability insurance £1m+ is the practical minimum",
        "Do NOT do gas, notifiable electrical, or structural work without the right qualifications",
        "Keep records for 6 years for HMRC",
        "VAT becomes mandatory at £90,000 turnover - rare at this scale"
      ],
    },
  },
  {
    name: "Dog Walker / Pet Sitter",
    category: "Self-Employed and Business Owner",
    shortDescription: "Walk dogs and look after pets while their owners work or travel - in your area, on your own schedule.",
    whyItFits: "You love animals, like being outside, and want a low-barrier business with steady, recurring income.",
    startingSalary: "£15,000 - £25,000",
    midSalary: "£25,000 - £40,000",
    seniorSalary: "£40,000 - £70,000 (with home boarding or staff)",
    timeToEntry: "Immediate",
    degreeRequired: "Not required",
    difficulty: "Low",
    competitionLevel: "Moderate",
    workEnvironment: "Outdoors, in all weather; some at-home boarding",
    stability: "Recurring weekday income from dog owners who work; growing market",
    weights: { freedom: 3, people: 1, structure: 0, creativity: -1, income: -1 },
    skillsNeeded: ["Confident dog handling", "Reliability", "Local knowledge", "Customer communication", "Safety awareness"],
    specialisations: [
      { title: "Home Boarding / Doggy Day Care", shortDescription: "Look after dogs in your home overnight - much higher income but needs council licensing.", typicalEntryRoute: "Standard dog walking + Animal Welfare Licence from local council", salaryRange: "£25,000 - £60,000" },
      { title: "Specialist Dog Trainer", shortDescription: "Move from walking into 1-to-1 behavioural training - higher fees, less weather exposure.", typicalEntryRoute: "IMDT, COAPE or similar accredited training qualification", salaryRange: "£25,000 - £55,000" },
    ],
    selfEmploymentOnly: true,
    soloMeta: {
      startupCostGBP: "£200 - £1,000",
      timeToFirstClient: "1 - 3 weeks",
      monthOneIncome: "£300 - £1,200",
      yearOneIncome: "£15,000 - £28,000",
      scaledIncome: "£35,000 - £60,000+ (with home boarding or hiring walkers)",
      commonPitfalls: [
        "Walking too many dogs at once without insurance for it - one bite incident ends your business",
        "No registration with the council for home boarding - illegal and uninsurable",
        "Driving 20 minutes between walks - density is everything in this business",
        "Discounting for friends/family forever - you can never raise prices later"
      ],
      firstFiveClients: [
        "Post in 3 local 'dog owner' Facebook groups offering one free trial walk",
        "Drop leaflets through 100 doors in one walkable area you know well",
        "Build a Google Business Profile with photos of dogs you've walked (with owner permission)",
        "Partner with one local vet, groomer or pet shop - put leaflets at their counter",
        "Sign up to Tailster and Rover to seed first 3-5 reviews quickly"
      ],
      toolsAndCosts: [
        "Slip leads, harnesses, poo bags, water bowls: £60 - £150",
        "Pet first aid kit: £30 - £60",
        "Public liability + care/custody insurance: £150 - £300/year (Cliverton, Pet Business Insurance)",
        "Reliable car if covering wider area: £1,000 - £8,000",
        "Booking software (Pet Sitter Plus, Time to Pet): £20 - £40/month (skip year one)"
      ],
      legalBasics: [
        "Register self-employed with HMRC and get your UTR",
        "Home boarding/day care needs an Animal Welfare Licence from your council (since 2018)",
        "Pet sitting insurance is industry standard - £1m public liability + care/custody/control cover",
        "DBS check is not required but increasingly asked for by clients",
        "GDPR: client info must be securely stored and deleted on request"
      ],
    },
  },
  {
    name: "Private Tutor",
    category: "Self-Employed and Business Owner",
    shortDescription: "Teach school subjects, exam prep or specialist skills 1-to-1, in person or online.",
    whyItFits: "You're patient, knowledgeable in a subject, and want flexible work that pays well per hour.",
    startingSalary: "£20,000 - £35,000",
    midSalary: "£35,000 - £55,000",
    seniorSalary: "£55,000 - £100,000+ (premium / 11+ / Oxbridge specialists)",
    timeToEntry: "Immediate",
    degreeRequired: "Not legally required - degree in subject helps; QTS for school-aged",
    difficulty: "Low",
    competitionLevel: "Moderate",
    workEnvironment: "Online, students' homes, libraries, your home",
    stability: "Strong recurring income during term time; quieter in summer",
    weights: { freedom: 2, people: 2, structure: 1, creativity: 1, income: 1 },
    skillsNeeded: ["Subject mastery", "Patience", "Lesson planning", "Tracking student progress", "Parent communication"],
    specialisations: [
      { title: "11+ / Grammar School Prep", shortDescription: "High-demand niche - parents pay £40-80/hour for proven results.", typicalEntryRoute: "Strong subject knowledge + track record of pupils passing", salaryRange: "£30,000 - £80,000" },
      { title: "GCSE / A-Level Specialist", shortDescription: "Focus on one or two subjects you know inside out - referrals compound.", typicalEntryRoute: "Degree in subject + first few good results", salaryRange: "£25,000 - £60,000" },
      { title: "Online English Tutor (TEFL)", shortDescription: "Teach English online to students in Asia, Europe, Latin America via global platforms.", typicalEntryRoute: "TEFL/TESOL qualification (£200-400) + headset + webcam", salaryRange: "£18,000 - £40,000" },
    ],
    selfEmploymentOnly: true,
    soloMeta: {
      startupCostGBP: "£100 - £600",
      timeToFirstClient: "1 - 3 weeks",
      monthOneIncome: "£300 - £1,500",
      yearOneIncome: "£18,000 - £35,000",
      scaledIncome: "£40,000 - £80,000+",
      commonPitfalls: [
        "Charging £20/hour because that's what teaching agencies pay - you're not an agency",
        "No cancellation policy - parents drop sessions last minute, you eat the loss",
        "Travelling 40+ minutes for one hour of work - online or local-only is the only sustainable model",
        "Refusing to drop weak students - one struggling pupil takes the energy of three good ones"
      ],
      firstFiveClients: [
        "Sign up to Tutorful, MyTutor and Superprof and complete first 5 sessions to get reviews",
        "Post in 3 local school parent Facebook groups offering a free 30-minute trial lesson",
        "Drop leaflets at 5 local primary or secondary school gates (with permission) at pick-up",
        "Tell every teacher friend or ex-colleague you tutor - referrals are the best lead source",
        "Build a Google Business Profile - 'maths tutor [town]' converts when you have reviews"
      ],
      toolsAndCosts: [
        "Webcam, headset, decent laptop (you likely own these): £0 - £400",
        "Whiteboard tool (BitPaper, Miro): free - £8/month",
        "Resources / past papers / textbooks: £100 - £400",
        "Public liability + professional indemnity insurance: £80 - £200/year",
        "Booking and payment software (Calendly + Stripe): £15/month + 1.5% on payments"
      ],
      legalBasics: [
        "Register self-employed with HMRC and get your UTR",
        "DBS check (Enhanced) is not legally required for solo tutors but parents will ask - £40-65 yourself or via tutoring platform",
        "Public liability insurance £1m+ is industry standard",
        "Safeguarding: lessons with under-18s should be recorded or with parent in earshot/online room",
        "GDPR: pupil progress notes must be securely stored"
      ],
    },
  },
  {
    name: "Mobile Beautician",
    category: "Self-Employed and Business Owner",
    shortDescription: "Travel to clients' homes for treatments - lashes, nails, brows, waxing, facials. No salon rent.",
    whyItFits: "You're hands-on, presentation-focused, and want low overheads with the freedom to set your hours and prices.",
    startingSalary: "£18,000 - £28,000",
    midSalary: "£28,000 - £45,000",
    seniorSalary: "£45,000 - £75,000",
    timeToEntry: "6 months - 2 years to qualify",
    degreeRequired: "Not required - VTCT/NVQ Level 2/3 in beauty therapy expected by insurers",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Clients' homes, car-based logistics",
    stability: "Strong recurring revenue (lashes 3 weekly, nails 2-3 weekly)",
    weights: { freedom: 2, creativity: 2, people: 2, income: 0, structure: 0 },
    skillsNeeded: ["Treatment skills", "Sanitation/hygiene", "Client rapport", "Marketing on Instagram", "Time management between bookings"],
    specialisations: [
      { title: "Lash Technician", shortDescription: "Classic, hybrid and volume lash extensions - high-margin recurring service.", typicalEntryRoute: "Lash training course (£300-1,500) + insurance", salaryRange: "£20,000 - £55,000" },
      { title: "Mobile Nail Technician", shortDescription: "Manicures, pedicures, gel and BIAB at clients' homes.", typicalEntryRoute: "VTCT Level 2/3 nail services or accredited course", salaryRange: "£18,000 - £40,000" },
      { title: "Spray Tan Specialist", shortDescription: "Mobile spray tans - peak demand around weddings, holidays and Christmas.", typicalEntryRoute: "1-day course + tent + machine (£500-1,200)", salaryRange: "£15,000 - £35,000" },
    ],
    selfEmploymentOnly: true,
    soloMeta: {
      startupCostGBP: "£800 - £4,000",
      timeToFirstClient: "1 - 4 weeks",
      monthOneIncome: "£500 - £2,000",
      yearOneIncome: "£20,000 - £35,000",
      scaledIncome: "£35,000 - £65,000+",
      commonPitfalls: [
        "Underpricing to fill the diary - you'll burn out at low rates",
        "Travelling 30+ minutes between clients - eats your day, not your fee",
        "Skipping insurance because clients 'are friends' - one allergic reaction ends you",
        "Using cheap products that ruin work and brand - clients judge longevity"
      ],
      firstFiveClients: [
        "Build a treatment portfolio on Instagram (your account = your shopfront) and tag your town in every post",
        "Run a £20 treatment offer to first 5 clients in exchange for review + tagged photo",
        "Sign up to Treatwell or Booksy to be discoverable + manage bookings",
        "Partner with one local hairdresser or salon to be their mobile recommendation",
        "Drop leaflets at 5 local gyms, dance studios and cafes where your target clients hang out"
      ],
      toolsAndCosts: [
        "Treatment kit specific to your service: £300 - £2,000",
        "Public liability + treatment insurance (ABT, BABTAC, Salon Gold): £80 - £200/year",
        "Reliable car: £1,500 - £8,000",
        "Booking software (Fresha free, Booksy ~£25/month): free - £30/month",
        "Sterilisation kit + couch + lighting: £200 - £700"
      ],
      legalBasics: [
        "Register self-employed with HMRC and get your UTR",
        "Treatment insurance is essential AND requires accredited qualifications (don't fake CPDs)",
        "Some treatments (microneedling, microblading) need council registration as 'special treatments'",
        "GDPR: keep client consultation forms securely (paper or encrypted)",
        "Patch tests required before first lash/tint/colour appointment - keep dated records"
      ],
    },
  },
  {
    name: "Freelance Social Media Manager",
    category: "Self-Employed and Business Owner",
    shortDescription: "Run Instagram, TikTok and LinkedIn for small businesses - content, scheduling, community, growth.",
    whyItFits: "You're chronically online, can write punchy copy, and want to work from anywhere with recurring monthly retainers.",
    startingSalary: "£20,000 - £35,000",
    midSalary: "£35,000 - £60,000",
    seniorSalary: "£60,000 - £120,000+ (with productised services or agency)",
    timeToEntry: "1 - 3 months to build first portfolio",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Fully remote",
    stability: "Steady once you have 3-5 retainer clients",
    weights: { freedom: 3, creativity: 2, people: 1, income: 1, structure: 0 },
    skillsNeeded: ["Short-form copywriting", "Trend awareness", "Basic design (Canva)", "Analytics (native + Sprout/Later)", "Client communication"],
    specialisations: [
      { title: "TikTok / Short-Form Specialist", shortDescription: "Focus on viral short-form video for one industry (fashion, fitness, B2B SaaS).", typicalEntryRoute: "Build your own niche TikTok to 10k+ followers as proof", salaryRange: "£30,000 - £90,000" },
      { title: "LinkedIn Ghostwriter", shortDescription: "Write LinkedIn content for founders and execs - £2-5k/month per client.", typicalEntryRoute: "Build your own LinkedIn audience first as proof of skill", salaryRange: "£40,000 - £120,000" },
    ],
    selfEmploymentOnly: true,
    soloMeta: {
      startupCostGBP: "£200 - £1,500",
      timeToFirstClient: "2 - 8 weeks",
      monthOneIncome: "£0 - £2,000",
      yearOneIncome: "£18,000 - £45,000",
      scaledIncome: "£40,000 - £100,000+",
      commonPitfalls: [
        "Charging £15/hour to do everything - the most underpaid corner of freelancing",
        "Promising follower growth - follower counts are vanity, lose clients in month 3",
        "Taking 8 small clients on £400/month each instead of 3 on £1,500 - admin kills you",
        "Posting and ghosting - clients pay for community management too, not just scheduling"
      ],
      firstFiveClients: [
        "Build YOUR OWN account in your target niche to 5k followers as live proof",
        "Productise one offer ('TikTok content engine, £1,500/month, 12 posts + 3 reels') and DM 30 brands in your niche",
        "Cold-email 50 small businesses with personalised content audits - 2-3 will book a call",
        "Reach out to 10 marketing agencies offering overflow social media capacity",
        "Run a free 7-day social audit for one local business to seed first case study"
      ],
      toolsAndCosts: [
        "Canva Pro: £11/month",
        "Later, Buffer or Metricool (scheduling): £15 - £45/month",
        "Capcut or InShot (video editing): free",
        "Notion or Trello for content calendar: free - £10/month",
        "Loom (for client updates): free"
      ],
      legalBasics: [
        "Register self-employed with HMRC and get your UTR",
        "Use written contracts: minimum 3-month engagement, monthly retainer, kill fee for early exit",
        "Get account access via delegated permissions (Meta Business, LinkedIn) - never shared logins",
        "GDPR: never store client login credentials in plain text",
        "VAT mandatory at £90,000 turnover"
      ],
    },
  },
  {
    name: "Freelance Copywriter",
    category: "Self-Employed and Business Owner",
    shortDescription: "Write words that sell - websites, emails, ads, landing pages, sales scripts - for businesses that need them.",
    whyItFits: "You can write clearly and persuasively, and want a fully remote business that pays well per hour with low overheads.",
    startingSalary: "£24,000 - £40,000",
    midSalary: "£40,000 - £75,000",
    seniorSalary: "£75,000 - £150,000+",
    timeToEntry: "1 - 3 months to first paid project",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Fully remote",
    stability: "Highly stable with 3-5 retainer clients",
    weights: { freedom: 3, creativity: 3, income: 1, people: 0, structure: 0 },
    skillsNeeded: ["Persuasive writing", "Research", "Customer-voice interviewing", "Direct response principles", "SEO basics"],
    specialisations: [
      { title: "Email Copywriter (B2C / Ecom)", shortDescription: "Write welcome flows, abandoned cart, and broadcast emails for ecommerce brands - £2-8k/month per client.", typicalEntryRoute: "Build a portfolio of 3-5 spec email flows and pitch DTC brands directly", salaryRange: "£40,000 - £120,000" },
      { title: "B2B / SaaS Copywriter", shortDescription: "Landing pages, case studies and white papers for tech companies - the highest-paid niche.", typicalEntryRoute: "Pick one B2B niche, write 2-3 strong sample pages, pitch heads of marketing", salaryRange: "£50,000 - £150,000" },
    ],
    selfEmploymentOnly: true,
    soloMeta: {
      startupCostGBP: "£100 - £800",
      timeToFirstClient: "2 - 8 weeks",
      monthOneIncome: "£0 - £2,500",
      yearOneIncome: "£20,000 - £50,000",
      scaledIncome: "£50,000 - £150,000+",
      commonPitfalls: [
        "Charging by the word or by the hour - top copywriters charge per project based on outcome",
        "Working with anyone who'll pay - low-budget clients consume disproportionate energy",
        "Unlimited revisions - 2 rounds maximum is industry standard",
        "Skipping the customer research call - thin copy that doesn't convert, no testimonials"
      ],
      firstFiveClients: [
        "Pick ONE niche (DTC skincare, B2B SaaS, fitness coaches) and write 3 spec pieces in that niche",
        "Cold-email 50 founders/marketing leads with a personalised, specific suggestion - 2-3 reply",
        "Post 1 weekly LinkedIn breakdown of a brand's copy - proves your eye, attracts inbound",
        "Reach out to 10 marketing agencies offering white-label overflow copy",
        "Sign up to Contently or Compose.ly for first 2-3 paid bylines while you prospect"
      ],
      toolsAndCosts: [
        "Google Docs / Notion: free",
        "Grammarly Pro: £12/month",
        "Loom (client kickoff videos): free",
        "Calendly + Stripe (or GoCardless): £15/month + 1.5% per payment",
        "Optional: Hemingway, Wordtune, ChatGPT Plus: £15-20/month each"
      ],
      legalBasics: [
        "Register self-employed with HMRC and get your UTR",
        "Use written contracts: scope, deliverables, 50% deposit, 2 revision rounds, kill fee",
        "Copyright transfers to client only on final payment - state this explicitly",
        "Don't sign exclusivity / non-compete clauses without a retainer fee attached",
        "VAT mandatory at £90,000 turnover"
      ],
    },
  },
  {
    name: "Etsy / Online Shop Owner",
    category: "Self-Employed and Business Owner",
    shortDescription: "Make and sell physical or digital products to a global audience via Etsy, Shopify or both.",
    whyItFits: "You're creative, want a product business not a service, and don't mind handling fulfilment, customer service and Etsy SEO.",
    startingSalary: "£5,000 - £20,000 (year one)",
    midSalary: "£20,000 - £45,000",
    seniorSalary: "£45,000 - £150,000+ (top 1% of sellers)",
    timeToEntry: "Immediate to launch; 6 - 18 months to meaningful income",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Extremely High",
    workEnvironment: "Home-based, mix of making and admin",
    stability: "Variable - dependent on Etsy algorithm changes and seasonal demand",
    weights: { freedom: 3, creativity: 3, income: 0, people: -1, structure: 0 },
    skillsNeeded: ["Product photography", "Etsy SEO", "Customer service", "Inventory and shipping", "Costing and margin"],
    specialisations: [
      { title: "Digital Product Seller", shortDescription: "Printables, templates, presets, SVGs - zero shipping, infinite scale, high margin.", typicalEntryRoute: "Pick a niche (wedding, education, planning) and list 30+ products", salaryRange: "£10,000 - £80,000" },
      { title: "Print on Demand", shortDescription: "Design once, sell on Etsy, fulfil through Printify or Printful - no inventory.", typicalEntryRoute: "Design skills + Printify/Printful integration + 50+ listings", salaryRange: "£8,000 - £50,000" },
      { title: "Handmade Maker", shortDescription: "Jewellery, candles, ceramics, art - lower volume, higher price, loyal customers.", typicalEntryRoute: "Hone craft + photography + Etsy listing optimisation", salaryRange: "£8,000 - £40,000" },
    ],
    selfEmploymentOnly: true,
    soloMeta: {
      startupCostGBP: "£100 - £2,000",
      timeToFirstClient: "1 day to first sale (often) - 3-6 months for meaningful income",
      monthOneIncome: "£0 - £400",
      yearOneIncome: "£3,000 - £20,000",
      scaledIncome: "£25,000 - £100,000+",
      commonPitfalls: [
        "Underpricing to compete - Etsy is full of sellers losing money to win the buy box",
        "Listing 5 products and waiting - the algorithm rewards 30-100+ listings",
        "Bad photography - the single biggest reason listings don't convert",
        "Ignoring customs, VAT and import duties when selling to EU/US - hidden cost",
        "Building only on Etsy with no email list - one algorithm change wipes your income"
      ],
      firstFiveClients: [
        "Research 10 successful sellers in your niche and reverse-engineer their listings (titles, tags, photos)",
        "Launch with 30+ listings - Etsy SEO favours active shops with depth",
        "Run small Etsy Ads (£3-5/day) on your best 5 listings to seed sales velocity",
        "Build an email list from day one (Klaviyo or Flodesk) - your only owned channel",
        "Promote on Pinterest with rich pins - free traffic, plays well with Etsy product images"
      ],
      toolsAndCosts: [
        "Etsy listing fee: £0.16 per listing (4-month renewal) + 6.5% transaction fee",
        "Camera or phone with good light: £0 - £600",
        "Canva Pro for product photos / mockups: £11/month",
        "EverBee or Marmalead (Etsy SEO research): £15-30/month",
        "Shopify (when you outgrow Etsy): £25/month"
      ],
      legalBasics: [
        "Register self-employed with HMRC and get your UTR",
        "VAT mandatory at £90,000 turnover - selling to EU triggers IOSS thresholds (€150)",
        "Distance Selling Regulations: 14-day cancellation right for UK consumers",
        "Trading Standards: clear, accurate descriptions; no false health/efficacy claims",
        "Trademark check before naming a shop or product line - removals are brutal"
      ],
    },
  },
  {
    name: "Content Creator",
    category: "Self-Employed and Business Owner",
    shortDescription: "Build an audience around a topic you love and earn from ads, sponsorships, products and affiliates.",
    whyItFits: "You're comfortable on camera, can stick at one thing for years without immediate reward, and want to build something that compounds.",
    startingSalary: "£0 - £8,000 (year one - usually unpaid time)",
    midSalary: "£25,000 - £80,000",
    seniorSalary: "£80,000 - £500,000+ (top 1% of creators)",
    timeToEntry: "12 - 36 months to meaningful income",
    degreeRequired: "Not required",
    difficulty: "High",
    competitionLevel: "Extremely High",
    workEnvironment: "Home-based; filming, editing, admin",
    stability: "Very low for the first 1-2 years; strong once an audience is built",
    weights: { freedom: 3, creativity: 3, people: 0, income: 0, structure: -2 },
    skillsNeeded: ["Storytelling", "On-camera presence", "Video editing or hiring an editor", "Niche expertise", "Algorithm understanding", "Resilience"],
    specialisations: [
      { title: "Long-Form YouTube Educator", shortDescription: "Tutorials, breakdowns, deep-dives - high-trust audiences, premium ad rates, strong sponsor fit.", typicalEntryRoute: "Pick a clear niche, post 1-2 videos weekly for 12-24 months minimum", salaryRange: "£20,000 - £200,000" },
      { title: "Short-Form Creator (TikTok / Reels)", shortDescription: "Faster audience growth, lower ad rates, monetise via brand deals and your own products.", typicalEntryRoute: "Post 1-3 short-form videos daily for 6-12 months", salaryRange: "£15,000 - £150,000" },
      { title: "Newsletter Operator", shortDescription: "Build an email audience around a niche topic and monetise via sponsorships and paid tiers.", typicalEntryRoute: "Pick a niche, ship weekly for 12+ months, grow via Twitter/LinkedIn/SEO", salaryRange: "£15,000 - £200,000" },
    ],
    selfEmploymentOnly: true,
    soloMeta: {
      startupCostGBP: "£200 - £3,000",
      timeToFirstClient: "6 - 18 months to first sponsor or ad payout",
      monthOneIncome: "£0",
      yearOneIncome: "£0 - £8,000",
      scaledIncome: "£40,000 - £300,000+",
      commonPitfalls: [
        "Quitting at month 6 - 90% of would-be creators stop right before traction",
        "Chasing trends instead of niching - generic content gets lost",
        "Over-investing in gear before audience - phone + light is enough for 12 months",
        "Single income stream (ad revenue only) - sponsors, products, affiliates, courses are where money is",
        "Treating it as a hobby in your evenings forever - it's a business or it isn't"
      ],
      firstFiveClients: [
        "Pick ONE niche so narrow it sounds boring (the algorithm rewards this)",
        "Publish a posting schedule you can sustain for 12 months minimum (e.g. 1 long-form/week)",
        "Build an email list from video 1 (ConvertKit free tier) - the only audience you own",
        "At 5,000 subscribers / followers, reach out to 20 brands in your niche with a one-page sponsor deck",
        "Launch one product or service for your audience by month 12 - sponsorships alone aren't enough"
      ],
      toolsAndCosts: [
        "Phone or basic camera + ring light: £0 - £400",
        "Mic (Rode VideoMic, Shure MV7): £80 - £400",
        "Editing software (DaVinci Resolve free, Adobe Premiere £50/month)",
        "Thumbnail design (Canva Pro): £11/month",
        "Email platform (ConvertKit, Beehiiv): free up to 1,000 subs"
      ],
      legalBasics: [
        "Register self-employed with HMRC and get your UTR",
        "ASA rules: paid promotion must be marked #ad in posts - fines and account bans for hiding it",
        "GDPR: email list and any data must be securely stored",
        "Music licensing: use Epidemic Sound or YouTube Audio Library only - copyright strikes kill channels",
        "VAT mandatory at £90,000 turnover - happens fast once sponsorships scale"
      ],
    },
  },
  {
    name: "Online Coach",
    category: "Self-Employed and Business Owner",
    shortDescription: "Help clients reach a specific outcome - fitness, business, mindset, career - through 1-to-1 calls and accountability.",
    whyItFits: "You've got real expertise or experience to share, you're a strong communicator, and want a high-margin remote business.",
    startingSalary: "£15,000 - £35,000 (year one)",
    midSalary: "£35,000 - £80,000",
    seniorSalary: "£80,000 - £250,000+ (with group programmes)",
    timeToEntry: "1 - 6 months to first paying client",
    degreeRequired: "Not legally required (depends on niche)",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Fully remote",
    stability: "Variable - retainer-based once you have group programmes",
    weights: { freedom: 3, people: 3, creativity: 1, income: 1, structure: 0 },
    skillsNeeded: ["Niche expertise", "Active listening", "Behaviour change frameworks", "Marketing yourself", "Sales calls"],
    specialisations: [
      { title: "Career / Business Coach", shortDescription: "Help professionals or founders solve specific problems - usually £150-400/hour.", typicalEntryRoute: "Strong CV in your domain + 3-5 transformation case studies", salaryRange: "£30,000 - £150,000" },
      { title: "Online Fitness / Nutrition Coach", shortDescription: "Hybrid coaching - calls + check-ins + a custom plan delivered via app.", typicalEntryRoute: "PT or nutrition qualification + 3-5 client wins documented", salaryRange: "£20,000 - £80,000" },
      { title: "Group Programme Operator", shortDescription: "Run a 6-12 week cohort programme for 10-30 people at a time - the highest-leverage model.", typicalEntryRoute: "First sell 1-to-1, then package the system", salaryRange: "£50,000 - £250,000" },
    ],
    selfEmploymentOnly: true,
    soloMeta: {
      startupCostGBP: "£100 - £1,500",
      timeToFirstClient: "2 - 12 weeks",
      monthOneIncome: "£0 - £2,000",
      yearOneIncome: "£15,000 - £40,000",
      scaledIncome: "£50,000 - £200,000+",
      commonPitfalls: [
        "No qualifications and offering medical/mental-health advice - illegal and dangerous in some niches",
        "Selling 'unlimited messaging' on a £200/month package - your client owns your evenings",
        "Discounting to close sales - you train your client to expect more discounts",
        "Charging by the hour instead of by outcome - the cap is your hours in the day",
        "Building only on social media with no email list - one ban wipes your business"
      ],
      firstFiveClients: [
        "Pick a niche so narrow it scares you - 'helping ex-teachers transition into tech' beats 'career coaching'",
        "Get 3 free testimonials from your network in exchange for results - your only credibility year one",
        "Post 1 case study per week on LinkedIn or your podcast/YouTube - leads compound",
        "Run a free workshop or webinar to one targeted audience monthly - convert 10-15% to discovery calls",
        "Use a 30-minute discovery call with a clear, simple offer at the end - not a pitch deck"
      ],
      toolsAndCosts: [
        "Calendly + Zoom + Stripe: £15-30/month total",
        "Coaching CRM (Paperbell, Practice, CoachAccountable): £25-100/month",
        "Email platform (ConvertKit, MailerLite): free up to 1,000 subs",
        "Notion or Trello for client portals: free",
        "Public liability + professional indemnity insurance: £150-400/year"
      ],
      legalBasics: [
        "Register self-employed with HMRC and get your UTR",
        "Coaching is unregulated - but you cannot diagnose, treat or claim to cure mental/physical conditions",
        "Therapist/counsellor titles ARE protected - don't use them without accreditation",
        "Use a coaching agreement covering scope, refunds, IP and data",
        "GDPR: client info must be securely stored, and deleted on request"
      ],
    },
  },
  {
    name: "Digital Product Creator",
    category: "Self-Employed and Business Owner",
    shortDescription: "Build and sell digital products - courses, templates, ebooks, software - to an audience you grow online.",
    whyItFits: "You can create something useful once and sell it many times. High margins, fully remote, infinitely scalable.",
    startingSalary: "£0 - £15,000 (year one)",
    midSalary: "£25,000 - £80,000",
    seniorSalary: "£80,000 - £400,000+",
    timeToEntry: "3 - 12 months to first product live + audience",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Fully remote, home-based",
    stability: "Strong once audience and 2-3 products exist; very low at the start",
    weights: { freedom: 3, creativity: 2, income: 1, structure: 0, people: -1 },
    skillsNeeded: ["Niche expertise", "Audience building", "Product packaging", "Sales pages and landing pages", "Email marketing"],
    specialisations: [
      { title: "Notion / Template Operator", shortDescription: "Sell low-priced repeatable templates and prompt packs - high volume, low ticket.", typicalEntryRoute: "Pick a niche, list on Gumroad/Etsy/Notion Marketplace", salaryRange: "£10,000 - £80,000" },
      { title: "Template / Notion Templates / SaaS Operator", shortDescription: "Sell low-priced repeatable digital tools - templates, prompt packs, presets.", typicalEntryRoute: "Pick a niche, list on Gumroad/Etsy/Notion Marketplace", salaryRange: "£10,000 - £100,000" },
      { title: "Membership / Community Operator", shortDescription: "Recurring monthly subscription for ongoing value - community, content, calls.", typicalEntryRoute: "Build audience first, then launch with 20-50 founding members", salaryRange: "£25,000 - £200,000" },
    ],
    selfEmploymentOnly: true,
    soloMeta: {
      startupCostGBP: "£100 - £2,500",
      timeToFirstClient: "3 - 9 months",
      monthOneIncome: "£0 - £500",
      yearOneIncome: "£3,000 - £20,000",
      scaledIncome: "£40,000 - £300,000+",
      commonPitfalls: [
        "Building the product before the audience - you'll launch to crickets",
        "Pricing too low ('£19 course') - hard to make a living on small ticket sizes alone",
        "Selling on social media only - email list is the only owned channel that compounds",
        "One-and-done launches - launch the same product 4 times a year minimum",
        "No refund policy clarity - chargebacks from Stripe destroy your account standing"
      ],
      firstFiveClients: [
        "Pick a niche specific enough to define your customer in 8 words",
        "Build email list to 500 subs BEFORE launching anything (lead magnet + content)",
        "Pre-sell to validate: open beta cohort at 50% off in exchange for testimonials",
        "Use launch sequences (5-7 emails over 5 days) - drip selling rarely works for new creators",
        "After launch one, raise the price 30% and launch again to a fresh cohort"
      ],
      toolsAndCosts: [
        "Email platform (ConvertKit, MailerLite, Beehiiv): free up to 1k subs",
        "Course/checkout platform (Podia, Teachable, Gumroad, Stan): free - £80/month",
        "Loom or Riverside (for video lessons): free - £15/month",
        "Notion or Circle (community): free - £40/month",
        "Stripe for payments: 1.5% + 20p per UK transaction"
      ],
      legalBasics: [
        "Register self-employed with HMRC and get your UTR",
        "Distance Selling Regulations: 14-day refund right unless customer waives in writing for digital downloads",
        "Trading Standards: clear, accurate descriptions; no exaggerated income/results claims",
        "GDPR: explicit consent for marketing emails, easy unsubscribe in every email",
        "VAT mandatory at £90,000 turnover; EU VAT rules apply selling digital products to EU consumers (Stripe Tax can handle this)"
      ],
    },
  },
  {
    name: "Virtual Assistant",
    category: "Self-Employed and Business Owner",
    shortDescription: "Handle inbox, calendar, admin, research and project tasks for busy founders and execs - all remote.",
    whyItFits: "You're organised, reliable, calm under pressure, and want a fully remote business with steady monthly retainers.",
    startingSalary: "£18,000 - £30,000",
    midSalary: "£30,000 - £55,000",
    seniorSalary: "£55,000 - £100,000+ (specialist VAs and agencies)",
    timeToEntry: "1 - 8 weeks",
    degreeRequired: "Not required",
    difficulty: "Low",
    competitionLevel: "High",
    workEnvironment: "Fully remote",
    stability: "Strong once 2-3 retainer clients are in place",
    weights: { freedom: 2, structure: 2, people: 2, income: 0, creativity: 0 },
    skillsNeeded: ["Inbox and calendar management", "Tools fluency (Notion, Asana, Slack)", "Discretion", "Process documentation", "Clear written communication"],
    specialisations: [
      { title: "Executive Assistant for Founders", shortDescription: "Inbox, calendar, travel, light project work for one founder - £2-5k/month per client.", typicalEntryRoute: "EA experience or strong admin background + 1-2 testimonials", salaryRange: "£30,000 - £80,000" },
      { title: "Specialist VA (Podcast, Course, Launch)", shortDescription: "Handle one specific workflow - podcast production, course launch ops, customer support - end to end.", typicalEntryRoute: "Pick a niche workflow + build SOPs + portfolio", salaryRange: "£25,000 - £75,000" },
    ],
    selfEmploymentOnly: true,
    soloMeta: {
      startupCostGBP: "£100 - £600",
      timeToFirstClient: "1 - 6 weeks",
      monthOneIncome: "£300 - £2,000",
      yearOneIncome: "£18,000 - £35,000",
      scaledIncome: "£40,000 - £75,000+",
      commonPitfalls: [
        "Charging £8-15/hour to compete with overseas VAs - race to the bottom",
        "Saying yes to everything - you become a jack of all trades, master of none",
        "Taking 8 small clients on £200/month each instead of 3 on £1,200 - admin nightmare",
        "Not specialising - generalist VAs are the lowest-paid corner of the market"
      ],
      firstFiveClients: [
        "Pick ONE niche (founders, course creators, podcasters) and ONE workflow you'll own",
        "List on VA-specific platforms (Time Etc, Belay, Boldly) for first 1-2 reviews",
        "Cold-email 50 founders/creators in your niche with a clear, productised offer",
        "Reach out to 10 marketing agencies and consultancies offering overflow ops",
        "Post on LinkedIn weekly about your niche - inbound from founders works here"
      ],
      toolsAndCosts: [
        "Notion, Slack, Asana - usually client-provided",
        "Loom (client updates): free",
        "Calendly + Zoom: £15/month",
        "Password manager (1Password, Bitwarden): £3-5/month",
        "Public liability + professional indemnity insurance: £100-300/year"
      ],
      legalBasics: [
        "Register self-employed with HMRC and get your UTR",
        "Use written contracts: scope, hours included per month, what triggers extra billing, kill fee",
        "GDPR: never store client passwords in plain text - use a password manager with delegated access",
        "Sign NDAs only AFTER reading them; don't agree to broad non-competes without retainer",
        "VAT mandatory at £90,000 turnover"
      ],
    },
  },
  // ---------------- Batch 3: Structured expansion ----------------
  // Tech & Digital
  {
    name: "Front-End Developer",
    category: "Technology",
    shortDescription: "Build the part of websites and apps users see and click - the visual, interactive layer.",
    whyItFits: "You like turning designs into smooth, fast, accessible interfaces and care about the small details users notice.",
    startingSalary: "£28,000 - £40,000",
    midSalary: "£50,000 - £75,000",
    seniorSalary: "£80,000 - £120,000+",
    timeToEntry: "6 - 18 months (self-taught) or 1 - 3 years (degree)",
    degreeRequired: "Helpful but not required",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Office or remote, mostly desk-based",
    stability: "Strong demand across every industry with a website or app",
    weights: { creativity: 2, structure: 2, freedom: 1, income: 1, people: -1 },
    skillsNeeded: ["HTML, CSS, JavaScript", "React or similar framework", "Responsive design", "Accessibility", "Git"],
    specialisations: [
      { title: "React Developer", shortDescription: "Specialise in React-based web apps - the most in-demand frontend stack.", typicalEntryRoute: "Self-taught + portfolio of 3-5 React projects", salaryRange: "£32,000 - £100,000" },
      { title: "Web Performance Engineer", shortDescription: "Make sites lightning-fast on slow networks and old devices.", typicalEntryRoute: "Senior frontend + perf specialism", salaryRange: "£60,000 - £130,000" },
    ],
    selfEmployable: true,
    similarCareers: ["UI Designer", "Full-Stack Developer", "Mobile App Developer"],
    higherProgressionCareers: ["Full-Stack Developer", "Software Engineer"],
    entryLevelAlternatives: ["UI Designer"],
  },
  {
    name: "Back-End Developer",
    category: "Technology",
    shortDescription: "Build the servers, databases and APIs that power apps behind the scenes.",
    whyItFits: "You like solving logic puzzles, working with data and building reliable systems users never see but always rely on.",
    startingSalary: "£32,000 - £45,000",
    midSalary: "£55,000 - £85,000",
    seniorSalary: "£90,000 - £140,000+",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Helpful (CS) but bootcamps + portfolio also work",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Office or remote, deep focus work",
    stability: "Very strong - core hire at any tech-enabled business",
    weights: { structure: 3, income: 2, creativity: 1, freedom: 1, people: -2 },
    skillsNeeded: ["Node, Python, Go or Java", "SQL and databases", "API design", "System design", "Testing"],
    specialisations: [
      { title: "API Engineer", shortDescription: "Design and build the APIs other engineers and clients depend on.", typicalEntryRoute: "Backend role + REST/GraphQL specialism", salaryRange: "£45,000 - £120,000" },
      { title: "Platform Engineer", shortDescription: "Build the internal tools and infra other developers use.", typicalEntryRoute: "Senior backend + infra/DevOps experience", salaryRange: "£70,000 - £140,000" },
    ],
    similarCareers: ["Full-Stack Developer", "DevOps Engineer", "Software Engineer"],
    higherProgressionCareers: ["Software Engineer", "Cloud Engineer"],
    entryLevelAlternatives: ["Front-End Developer"],
  },
  {
    name: "Full-Stack Developer",
    category: "Technology",
    shortDescription: "Work across both frontend and backend - the whole stack, end to end.",
    whyItFits: "You like building complete features yourself rather than handing work between teams.",
    startingSalary: "£32,000 - £45,000",
    midSalary: "£55,000 - £85,000",
    seniorSalary: "£90,000 - £140,000+",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Helpful but not required",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Office or remote, deep focus work",
    stability: "Strong - particularly valuable at startups and scale-ups",
    weights: { structure: 2, creativity: 2, income: 2, freedom: 1, people: -1 },
    skillsNeeded: ["Frontend (React)", "Backend (Node/Python)", "Databases", "Deployment", "Git"],
    specialisations: [
      { title: "T-shaped Engineer", shortDescription: "Strong across the stack with one deep specialism.", typicalEntryRoute: "Build full-stack projects + pick one area to go deep", salaryRange: "£45,000 - £130,000" },
    ],
    selfEmployable: true,
    similarCareers: ["Front-End Developer", "Back-End Developer", "Software Engineer"],
    higherProgressionCareers: ["Software Engineer", "Technical Product Manager"],
    entryLevelAlternatives: ["Front-End Developer"],
  },
  {
    name: "Mobile App Developer",
    category: "Technology",
    shortDescription: "Build iOS and Android apps used on phones and tablets every day.",
    whyItFits: "You want to ship things people use in their pocket and care about smooth, native-feeling experiences.",
    startingSalary: "£30,000 - £42,000",
    midSalary: "£50,000 - £80,000",
    seniorSalary: "£85,000 - £130,000",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Helpful but not required",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Office or remote, mostly desk-based",
    stability: "Strong demand from every consumer brand",
    weights: { structure: 2, creativity: 2, income: 2, freedom: 1, people: -1 },
    skillsNeeded: ["Swift or Kotlin", "React Native or Flutter (cross-platform)", "Mobile UX", "App store deployment", "Performance"],
    specialisations: [
      { title: "iOS Developer", shortDescription: "Build native iPhone and iPad apps with Swift.", typicalEntryRoute: "Self-taught + 2-3 published apps", salaryRange: "£30,000 - £120,000" },
      { title: "Android Developer", shortDescription: "Build native Android apps with Kotlin.", typicalEntryRoute: "Self-taught + 2-3 published apps", salaryRange: "£30,000 - £110,000" },
    ],
    selfEmployable: true,
    similarCareers: ["Front-End Developer", "Game Developer", "Software Engineer"],
    higherProgressionCareers: ["Software Engineer", "Technical Product Manager"],
    entryLevelAlternatives: ["Front-End Developer"],
  },
  {
    name: "Game Developer",
    category: "Technology",
    shortDescription: "Build video games for console, PC, mobile or VR - from indie passion projects to AAA studios.",
    whyItFits: "You're obsessed with games, love building interactive worlds and accept the trade-off of long hours for creative work.",
    startingSalary: "£24,000 - £35,000",
    midSalary: "£40,000 - £65,000",
    seniorSalary: "£70,000 - £110,000+",
    timeToEntry: "2 - 5 years",
    degreeRequired: "Helpful (games dev or CS) but portfolio matters more",
    difficulty: "High",
    competitionLevel: "Extremely High",
    workEnvironment: "Studio or remote, often crunch periods",
    stability: "Volatile - studio closures are common; strong skills travel",
    weights: { creativity: 3, structure: 2, freedom: 1, income: 0, people: -1 },
    skillsNeeded: ["C++, C# or Unity/Unreal", "Maths and physics", "Game design", "Version control", "Optimisation"],
    specialisations: [
      { title: "Gameplay Programmer", shortDescription: "Build the systems players actually interact with - combat, movement, AI.", typicalEntryRoute: "Games degree + 2-3 finished demo projects", salaryRange: "£28,000 - £90,000" },
      { title: "Engine / Tools Programmer", shortDescription: "Build the engine and tooling the rest of the studio uses.", typicalEntryRoute: "CS degree + low-level programming", salaryRange: "£40,000 - £120,000" },
    ],
    similarCareers: ["Mobile App Developer", "Software Engineer", "3D Artist"],
    higherProgressionCareers: ["Software Engineer"],
    entryLevelAlternatives: ["Front-End Developer", "3D Artist"],
  },
  {
    name: "Data Scientist",
    category: "Technology",
    shortDescription: "Use stats, ML and code to find patterns in data and answer business questions.",
    whyItFits: "You love digging through data, building models and explaining what the numbers really mean.",
    startingSalary: "£35,000 - £50,000",
    midSalary: "£60,000 - £95,000",
    seniorSalary: "£100,000 - £160,000+",
    timeToEntry: "2 - 5 years",
    degreeRequired: "Usually required (stats, maths, CS or PhD)",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Office or remote, mix of analysis and stakeholder work",
    stability: "Strong - every data-rich company hires data scientists",
    weights: { structure: 3, creativity: 2, income: 2, people: 0, freedom: 1 },
    skillsNeeded: ["Python or R", "SQL", "Statistics", "ML basics", "Data storytelling"],
    specialisations: [
      { title: "Product Data Scientist", shortDescription: "Use data to drive product decisions and experimentation.", typicalEntryRoute: "Stats/CS degree + product company", salaryRange: "£50,000 - £130,000" },
      { title: "Research Data Scientist", shortDescription: "Push the boundaries of ML and applied stats.", typicalEntryRoute: "PhD + publications", salaryRange: "£70,000 - £180,000" },
    ],
    similarCareers: ["Machine Learning Engineer", "Data Analyst", "Data Engineer"],
    higherProgressionCareers: ["Machine Learning Engineer", "AI Engineer"],
    entryLevelAlternatives: ["Data Analyst"],
  },
  {
    name: "Data Engineer",
    category: "Technology",
    shortDescription: "Build the pipelines, warehouses and infrastructure that move data around a business reliably.",
    whyItFits: "You like solid engineering work, care about data quality and want to be the foundation analysts and DS rely on.",
    startingSalary: "£35,000 - £50,000",
    midSalary: "£60,000 - £95,000",
    seniorSalary: "£100,000 - £150,000+",
    timeToEntry: "2 - 4 years",
    degreeRequired: "Helpful (CS or related) but not strictly required",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Office or remote, deep focus work",
    stability: "Very strong - every company moving past spreadsheets needs them",
    weights: { structure: 3, income: 2, creativity: 1, freedom: 1, people: -1 },
    skillsNeeded: ["SQL", "Python", "Cloud (AWS/GCP/Azure)", "dbt or Airflow", "Data modelling"],
    specialisations: [
      { title: "Analytics Engineer", shortDescription: "Sit between data engineers and analysts - own the modelling layer.", typicalEntryRoute: "Analyst + dbt + SQL specialism", salaryRange: "£45,000 - £100,000" },
    ],
    similarCareers: ["Back-End Developer", "Data Analyst", "Cloud Engineer"],
    higherProgressionCareers: ["Cloud Engineer", "Machine Learning Engineer"],
    entryLevelAlternatives: ["Data Analyst", "Back-End Developer"],
  },
  {
    name: "DevOps Engineer",
    category: "Technology",
    shortDescription: "Automate deployment, infrastructure and reliability so software ships safely and runs smoothly.",
    whyItFits: "You like systems thinking, automation and being the person who keeps things running rather than building features.",
    startingSalary: "£40,000 - £55,000",
    midSalary: "£65,000 - £100,000",
    seniorSalary: "£105,000 - £160,000+",
    timeToEntry: "2 - 5 years (often via dev or sysadmin route)",
    degreeRequired: "Not required - certifications and experience matter more",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Office or remote, on-call rotation common",
    stability: "Very strong - every company shipping software needs DevOps",
    weights: { structure: 3, income: 2, freedom: 1, creativity: 0, people: -1 },
    skillsNeeded: ["Linux", "Docker and Kubernetes", "CI/CD", "Cloud (AWS/GCP/Azure)", "Scripting"],
    specialisations: [
      { title: "Site Reliability Engineer", shortDescription: "Keep large-scale systems up and fast - blend of dev + ops.", typicalEntryRoute: "Senior DevOps or backend + reliability specialism", salaryRange: "£70,000 - £160,000" },
    ],
    similarCareers: ["Cloud Engineer", "Back-End Developer", "Software Engineer"],
    higherProgressionCareers: ["Cloud Engineer", "Software Engineer"],
    entryLevelAlternatives: ["IT Support Technician", "Back-End Developer"],
  },
  {
    name: "Cloud Engineer",
    category: "Technology",
    shortDescription: "Design, build and run systems on AWS, Azure or Google Cloud at scale.",
    whyItFits: "You like big-picture infrastructure, cost optimisation and being the person who designs how things actually run in production.",
    startingSalary: "£40,000 - £55,000",
    midSalary: "£65,000 - £105,000",
    seniorSalary: "£110,000 - £170,000+",
    timeToEntry: "2 - 5 years",
    degreeRequired: "Not required - cloud certifications carry weight",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Office or remote, cross-team work",
    stability: "Very strong - cloud spend keeps growing",
    weights: { structure: 3, income: 3, creativity: 1, freedom: 1, people: 0 },
    skillsNeeded: ["AWS, Azure or GCP", "Terraform or IaC", "Networking", "Security basics", "Cost optimisation"],
    specialisations: [
      { title: "Cloud Architect", shortDescription: "Design cloud systems for whole organisations.", typicalEntryRoute: "Senior cloud engineer + architecture certifications", salaryRange: "£90,000 - £180,000" },
    ],
    similarCareers: ["DevOps Engineer", "Back-End Developer", "Data Engineer"],
    higherProgressionCareers: ["Software Engineer"],
    entryLevelAlternatives: ["IT Support Technician", "DevOps Engineer"],
  },
  {
    name: "Penetration Tester",
    category: "Technology",
    shortDescription: "Get paid to legally hack into systems and find vulnerabilities before criminals do.",
    whyItFits: "You're curious about how systems break, enjoy puzzles and want a clearly defined attacker mindset.",
    startingSalary: "£30,000 - £45,000",
    midSalary: "£50,000 - £80,000",
    seniorSalary: "£85,000 - £130,000+",
    timeToEntry: "1 - 3 years (often via certifications + home lab)",
    degreeRequired: "Not required - OSCP and similar certs matter most",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Office, remote, or client-site engagements",
    stability: "Very strong - cyber spend keeps growing",
    weights: { structure: 2, creativity: 2, income: 2, freedom: 2, people: -1 },
    skillsNeeded: ["Networking", "Linux", "Web app security", "Scripting (Python, Bash)", "Report writing"],
    specialisations: [
      { title: "Web App Pen Tester", shortDescription: "Find bugs in web applications and APIs.", typicalEntryRoute: "OSCP + bug bounty experience", salaryRange: "£40,000 - £110,000" },
      { title: "Red Team Operator", shortDescription: "Simulate full attacker campaigns against an organisation.", typicalEntryRoute: "Senior pen tester + OSCE/CRTO certs", salaryRange: "£70,000 - £140,000" },
    ],
    selfEmployable: true,
    similarCareers: ["Cybersecurity Analyst", "Back-End Developer"],
    higherProgressionCareers: ["Cybersecurity Analyst"],
    entryLevelAlternatives: ["IT Support Technician", "Cybersecurity Analyst"],
  },
  {
    name: "UI Designer",
    category: "Design and Creative",
    shortDescription: "Design the look, feel and visual interactions of digital products - the surface users actually see.",
    whyItFits: "You love typography, layout and pixel-level craft and want to design beautiful, usable interfaces.",
    startingSalary: "£26,000 - £38,000",
    midSalary: "£42,000 - £65,000",
    seniorSalary: "£70,000 - £100,000+",
    timeToEntry: "1 - 2 years (portfolio-led)",
    degreeRequired: "Not required - portfolio is everything",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Office or remote, collaborative with PMs and engineers",
    stability: "Cyclical with tech hiring",
    weights: { creativity: 3, structure: 1, freedom: 1, income: 0, people: 0 },
    skillsNeeded: ["Figma", "Visual hierarchy", "Typography", "Design systems", "Prototyping"],
    specialisations: [
      { title: "Product UI Designer", shortDescription: "Design the visual layer of SaaS and web products.", typicalEntryRoute: "Portfolio + junior product role", salaryRange: "£30,000 - £85,000" },
    ],
    selfEmployable: true,
    similarCareers: ["UX Designer", "Graphic Designer", "Brand Designer"],
    higherProgressionCareers: ["UX Designer", "Art Director"],
    entryLevelAlternatives: ["Graphic Designer"],
  },
  {
    name: "Technical Product Manager",
    category: "Technology",
    shortDescription: "PM technical products like APIs, platforms and developer tools - sits at engineering/product boundary.",
    whyItFits: "You enjoy product thinking but want to go deeper on technical trade-offs than a generalist PM does.",
    startingSalary: "£45,000 - £65,000",
    midSalary: "£75,000 - £110,000",
    seniorSalary: "£120,000 - £180,000+",
    timeToEntry: "3 - 6 years (usually via engineering)",
    degreeRequired: "Helpful (CS) - engineering background most common path",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Office or remote, lots of cross-team work",
    stability: "Strong demand at infra, dev tools and platform companies",
    weights: { structure: 3, income: 3, creativity: 1, people: 1, freedom: 1 },
    skillsNeeded: ["Technical literacy", "Product strategy", "API design", "Stakeholder management", "Roadmapping"],
    specialisations: [
      { title: "Platform PM", shortDescription: "PM internal platforms used by other engineers.", typicalEntryRoute: "Senior engineer + platform role", salaryRange: "£80,000 - £170,000" },
    ],
    similarCareers: ["Product Manager", "AI Engineer", "Software Engineer"],
    higherProgressionCareers: ["Product Manager"],
    entryLevelAlternatives: ["Product Manager"],
  },
  // Creative
  {
    name: "Brand Designer",
    category: "Design and Creative",
    shortDescription: "Design logos, identity systems and visual languages that define how brands look and feel.",
    whyItFits: "You love big-picture visual thinking, typography and shaping how a company is seen in the world.",
    startingSalary: "£26,000 - £35,000",
    midSalary: "£40,000 - £60,000",
    seniorSalary: "£65,000 - £95,000+",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Not required - portfolio matters most",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Studio, agency, in-house or freelance",
    stability: "Cyclical with marketing budgets",
    weights: { creativity: 3, freedom: 1, structure: 0, income: 0, people: 0 },
    skillsNeeded: ["Typography", "Logo design", "Brand strategy basics", "Adobe / Figma", "Pitching ideas"],
    specialisations: [],
    selfEmployable: true,
    similarCareers: ["Graphic Designer", "UI Designer", "Art Director"],
    higherProgressionCareers: ["Art Director", "Creative Director"],
    entryLevelAlternatives: ["Graphic Designer"],
  },
  {
    name: "Motion Graphics Designer",
    category: "Design and Creative",
    shortDescription: "Animate type, logos and graphics for ads, social, broadcast and product UI.",
    whyItFits: "You love designing AND animating, and want work that lives somewhere between graphic design and film.",
    startingSalary: "£25,000 - £35,000",
    midSalary: "£40,000 - £60,000",
    seniorSalary: "£65,000 - £95,000",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Not required - showreel matters most",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Studio, agency or freelance",
    stability: "Strong demand from social-first brands",
    weights: { creativity: 3, freedom: 2, structure: 0, income: 0, people: -1 },
    skillsNeeded: ["After Effects", "Cinema 4D basics", "Typography", "Sound design basics", "Storyboarding"],
    specialisations: [],
    selfEmployable: true,
    similarCareers: ["Animator", "Graphic Designer", "Video Editor"],
    higherProgressionCareers: ["Art Director", "Creative Director"],
    entryLevelAlternatives: ["Graphic Designer", "Video Editor"],
  },
  {
    name: "Animator",
    category: "Design and Creative",
    shortDescription: "Bring characters and stories to life for film, TV, games and online video.",
    whyItFits: "You're patient, obsessed with movement and want to make things move beautifully on screen.",
    startingSalary: "£22,000 - £32,000",
    midSalary: "£38,000 - £60,000",
    seniorSalary: "£65,000 - £100,000+",
    timeToEntry: "2 - 4 years",
    degreeRequired: "Helpful (animation degree) but showreel decides",
    difficulty: "High",
    competitionLevel: "Extremely High",
    workEnvironment: "Studio or remote, project-based",
    stability: "Project-by-project, but strong skills travel",
    weights: { creativity: 3, structure: 1, freedom: 1, income: 0, people: -1 },
    skillsNeeded: ["12 principles of animation", "Maya or Blender", "Storytelling", "Acting reference", "Patience"],
    specialisations: [
      { title: "2D Animator", shortDescription: "Hand-drawn or vector animation for film, TV or social.", typicalEntryRoute: "Animation degree + showreel", salaryRange: "£24,000 - £70,000" },
      { title: "3D Character Animator", shortDescription: "Animate characters for games and film.", typicalEntryRoute: "Animation degree + 3D specialism", salaryRange: "£28,000 - £100,000" },
    ],
    similarCareers: ["Motion Graphics Designer", "3D Artist", "Game Developer"],
    higherProgressionCareers: ["Art Director", "Creative Director"],
    entryLevelAlternatives: ["Motion Graphics Designer"],
  },
  {
    name: "3D Artist",
    category: "Design and Creative",
    shortDescription: "Model, texture and light 3D assets for games, film, product viz and AR/VR.",
    whyItFits: "You like making things in 3D, learning complex software and building polished visual work.",
    startingSalary: "£24,000 - £35,000",
    midSalary: "£40,000 - £65,000",
    seniorSalary: "£70,000 - £110,000+",
    timeToEntry: "2 - 4 years",
    degreeRequired: "Not required - portfolio is everything",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Studio or remote, project-based",
    stability: "Strong in games, film and product visualisation",
    weights: { creativity: 3, structure: 2, freedom: 1, income: 0, people: -1 },
    skillsNeeded: ["Blender, Maya or 3ds Max", "Texturing (Substance)", "Lighting", "Composition", "Render engines"],
    specialisations: [
      { title: "Environment Artist", shortDescription: "Build worlds and environments for games and film.", typicalEntryRoute: "Portfolio + junior studio role", salaryRange: "£26,000 - £80,000" },
      { title: "Character Artist", shortDescription: "Sculpt and texture characters for games and film.", typicalEntryRoute: "Portfolio + ZBrush specialism", salaryRange: "£28,000 - £90,000" },
    ],
    selfEmployable: true,
    similarCareers: ["Animator", "Game Developer", "Illustrator"],
    higherProgressionCareers: ["Art Director"],
    entryLevelAlternatives: ["Graphic Designer"],
  },
  {
    name: "Illustrator",
    category: "Design and Creative",
    shortDescription: "Create custom illustration for editorial, brand, books, products and packaging.",
    whyItFits: "You have a distinctive style, love drawing and want to make a living from your visual voice.",
    startingSalary: "£18,000 - £28,000",
    midSalary: "£30,000 - £55,000",
    seniorSalary: "£60,000 - £100,000+ (top names much higher)",
    timeToEntry: "2 - 5 years to build a name",
    degreeRequired: "Not required - portfolio decides",
    difficulty: "Medium",
    competitionLevel: "Extremely High",
    workEnvironment: "Mostly freelance or in-house brand teams",
    stability: "Variable - reputation and clients build slowly",
    weights: { creativity: 3, freedom: 3, structure: -1, income: -1, people: -1 },
    skillsNeeded: ["Drawing", "Style development", "Procreate / Photoshop", "Client management", "Pricing"],
    specialisations: [
      { title: "Editorial Illustrator", shortDescription: "Illustrate articles for newspapers, magazines and online.", typicalEntryRoute: "Portfolio + cold pitch art directors", salaryRange: "£18,000 - £60,000" },
      { title: "Brand Illustrator", shortDescription: "Build illustration systems for brands, products and ads.", typicalEntryRoute: "Portfolio + agency or in-house", salaryRange: "£25,000 - £80,000" },
    ],
    selfEmployable: true,
    similarCareers: ["Graphic Designer", "Brand Designer", "3D Artist"],
    higherProgressionCareers: ["Art Director"],
    entryLevelAlternatives: ["Graphic Designer"],
  },
  {
    name: "Commercial Photographer",
    category: "Design and Creative",
    shortDescription: "Shoot product, fashion, portraits, food or interiors for brands, magazines and ads.",
    whyItFits: "You love light, composition and the craft of photography - and want to turn it into a business.",
    startingSalary: "£18,000 - £30,000",
    midSalary: "£35,000 - £60,000",
    seniorSalary: "£70,000 - £150,000+ (top commercial)",
    timeToEntry: "2 - 5 years to build clients",
    degreeRequired: "Not required - portfolio matters",
    difficulty: "Medium",
    competitionLevel: "Extremely High",
    workEnvironment: "Studio + on-location; mostly freelance",
    stability: "Project-based; retainers possible",
    weights: { creativity: 3, freedom: 3, structure: 0, income: 0, people: 1 },
    skillsNeeded: ["Lighting", "Camera craft", "Retouching (Capture One, Lightroom)", "Client management", "Portfolio building"],
    specialisations: [
      { title: "Product Photographer", shortDescription: "Shoot product imagery for ecommerce and brands.", typicalEntryRoute: "Build portfolio + cold outreach to brands", salaryRange: "£20,000 - £100,000" },
      { title: "Portrait / Editorial Photographer", shortDescription: "Shoot people for editorial, fashion or branding.", typicalEntryRoute: "Assist established photographer + build book", salaryRange: "£25,000 - £150,000" },
    ],
    selfEmployable: true,
    similarCareers: ["Video Editor", "Content Creator", "Illustrator"],
    higherProgressionCareers: ["Art Director", "Creative Director"],
    entryLevelAlternatives: ["Content Creator"],
  },
  {
    name: "Copywriter",
    category: "Marketing and Media",
    shortDescription: "Write words that sell - for ads, websites, emails, products and brand campaigns.",
    whyItFits: "You can write clearly under pressure, understand persuasion and like the discipline of words that have a job to do.",
    startingSalary: "£24,000 - £32,000",
    midSalary: "£38,000 - £60,000",
    seniorSalary: "£65,000 - £100,000+",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Not required - portfolio matters most",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Agency or in-house, office or remote",
    stability: "Cyclical with marketing budgets",
    weights: { creativity: 3, structure: 1, people: 0, income: 0, freedom: 1 },
    skillsNeeded: ["Persuasive writing", "Research", "Brand voice", "Briefs", "Editing"],
    specialisations: [
      { title: "Brand Copywriter", shortDescription: "Shape voice, taglines and brand campaigns.", typicalEntryRoute: "Agency junior copywriter role + spec ad portfolio", salaryRange: "£28,000 - £85,000" },
      { title: "Conversion Copywriter", shortDescription: "Write landing pages and emails optimised to convert.", typicalEntryRoute: "Direct response training + portfolio of measured wins", salaryRange: "£35,000 - £100,000" },
    ],
    similarCareers: ["Freelance Copywriter", "Marketing Executive", "Brand Manager"],
    higherProgressionCareers: ["Art Director", "Creative Director"],
    entryLevelAlternatives: ["Marketing Executive"],
  },
  {
    name: "Art Director",
    category: "Design and Creative",
    shortDescription: "Lead the visual direction of campaigns, products or publications - shaping look, feel and quality.",
    whyItFits: "You have strong taste, can direct other designers and want to own the creative output, not just produce it.",
    startingSalary: "£40,000 - £55,000",
    midSalary: "£60,000 - £85,000",
    seniorSalary: "£90,000 - £140,000+",
    timeToEntry: "5 - 10 years (usually senior designer first)",
    degreeRequired: "Not required - portfolio + leadership track record",
    difficulty: "High",
    competitionLevel: "High",
    workEnvironment: "Agency, studio or in-house; lots of meetings",
    stability: "Cyclical with marketing budgets",
    weights: { creativity: 3, structure: 1, income: 2, people: 1, freedom: 0 },
    skillsNeeded: ["Visual taste", "Design leadership", "Concept development", "Briefing", "Presenting work"],
    specialisations: [],
    similarCareers: ["Brand Designer", "Graphic Designer", "Creative Director"],
    higherProgressionCareers: ["Creative Director"],
    entryLevelAlternatives: ["Graphic Designer", "Brand Designer"],
  },
  {
    name: "Creative Director",
    category: "Design and Creative",
    shortDescription: "Set the overall creative vision for an agency, brand or studio - the most senior creative role.",
    whyItFits: "You're a creative leader who can set vision, win pitches, hire well and ship award-winning work.",
    startingSalary: "£60,000 - £85,000",
    midSalary: "£90,000 - £130,000",
    seniorSalary: "£140,000 - £250,000+",
    timeToEntry: "10 - 15 years (long career build)",
    degreeRequired: "Not required - body of work decides",
    difficulty: "High",
    competitionLevel: "Extremely High",
    workEnvironment: "Agency, in-house or independent; lots of pitching",
    stability: "Senior leadership - market sensitive",
    weights: { creativity: 3, income: 2, people: 2, structure: 0, freedom: 1 },
    skillsNeeded: ["Vision", "Storytelling", "Hiring and managing creatives", "Client / leadership presence", "Business of creative work"],
    specialisations: [],
    similarCareers: ["Art Director", "Brand Designer"],
    higherProgressionCareers: [],
    entryLevelAlternatives: ["Art Director"],
  },
  // Business & Finance
  {
    name: "Investment Banker",
    category: "Business, Finance and Legal",
    shortDescription: "Advise companies on raising capital, M&A and IPOs - long hours, high pay, high pressure.",
    whyItFits: "You're highly driven, comfortable with intense hours and motivated by money, status and complex deals.",
    startingSalary: "£55,000 - £75,000 + bonus",
    midSalary: "£100,000 - £200,000 + bonus",
    seniorSalary: "£300,000 - £1,000,000+ (MD level)",
    timeToEntry: "1 - 3 years (very competitive grad schemes)",
    degreeRequired: "Yes (top university preferred)",
    difficulty: "High",
    competitionLevel: "Extremely High",
    workEnvironment: "Office, very long hours (70-100/week common at junior level)",
    stability: "Cyclical with markets - up-or-out culture",
    weights: { income: 3, structure: 2, people: 1, creativity: 0, freedom: -2 },
    skillsNeeded: ["Financial modelling", "Excel", "Valuation", "Stamina", "Attention to detail"],
    specialisations: [
      { title: "M&A Analyst", shortDescription: "Work on mergers and acquisitions deals.", typicalEntryRoute: "Top university + spring weeks + grad scheme", salaryRange: "£55,000 - £200,000" },
      { title: "Equity Capital Markets", shortDescription: "Help companies go public via IPOs.", typicalEntryRoute: "Bank grad scheme + ECM rotation", salaryRange: "£55,000 - £250,000" },
    ],
    similarCareers: ["Financial Analyst", "Management Consultant"],
    higherProgressionCareers: [],
    entryLevelAlternatives: ["Financial Analyst"],
  },
  {
    name: "Business Analyst",
    category: "Business, Finance and Legal",
    shortDescription: "Sit between business and tech - gather requirements, analyse processes and shape solutions.",
    whyItFits: "You like making sense of how things work, talking to people across teams and shaping practical solutions.",
    startingSalary: "£28,000 - £40,000",
    midSalary: "£45,000 - £70,000",
    seniorSalary: "£75,000 - £110,000+",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Helpful but not required",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office or hybrid, very stakeholder-heavy",
    stability: "Strong - every transformation programme needs BAs",
    weights: { structure: 3, people: 2, income: 1, creativity: 0, freedom: 0 },
    skillsNeeded: ["Requirements gathering", "Process mapping", "Stakeholder management", "SQL basics", "Documentation"],
    specialisations: [
      { title: "IT Business Analyst", shortDescription: "Translate business needs into technical requirements.", typicalEntryRoute: "Grad role + agile/Scrum certifications", salaryRange: "£32,000 - £85,000" },
    ],
    similarCareers: ["Project Manager", "Product Manager", "Data Analyst"],
    higherProgressionCareers: ["Product Manager", "Project Manager"],
    entryLevelAlternatives: ["Customer Service Advisor"],
  },
  {
    name: "E-commerce Manager",
    category: "Business, Finance and Legal",
    shortDescription: "Own the online sales channel for a brand - traffic, conversion, range and revenue.",
    whyItFits: "You're commercial, like data and want to own a P&L from day one.",
    startingSalary: "£30,000 - £42,000",
    midSalary: "£48,000 - £75,000",
    seniorSalary: "£80,000 - £130,000+",
    timeToEntry: "2 - 5 years",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Office or hybrid, often retail/D2C",
    stability: "Strong demand from D2C and retail",
    weights: { structure: 2, income: 2, people: 1, creativity: 1, freedom: 1 },
    skillsNeeded: ["Shopify or similar", "Analytics (GA4)", "Performance marketing basics", "CRO", "Merchandising"],
    specialisations: [
      { title: "D2C E-commerce Manager", shortDescription: "Run the online store for a direct-to-consumer brand.", typicalEntryRoute: "Marketing exec or merchandising role + D2C specialism", salaryRange: "£35,000 - £100,000" },
    ],
    selfEmployable: true,
    similarCareers: ["Marketing Executive", "Brand Manager", "Founder / Entrepreneur"],
    higherProgressionCareers: ["Founder / Entrepreneur", "Brand Manager"],
    entryLevelAlternatives: ["Marketing Executive"],
  },
  // Health & Science
  {
    name: "Surgeon",
    category: "Healthcare and Care",
    shortDescription: "Perform surgery to treat injury, disease and deformity - one of the most demanding medical specialties.",
    whyItFits: "You're academically strong, calm under pressure and willing to commit to a long, intense training pathway.",
    startingSalary: "£36,000 - £45,000 (FY1)",
    midSalary: "£60,000 - £100,000 (registrar)",
    seniorSalary: "£100,000 - £200,000+ (consultant + private)",
    timeToEntry: "12 - 16 years",
    degreeRequired: "Yes (medicine + surgical training)",
    difficulty: "High",
    competitionLevel: "Extremely High",
    workEnvironment: "Hospital, theatre, on-call rotations",
    stability: "Very strong",
    weights: { structure: 3, income: 3, people: 2, creativity: 1, freedom: -1 },
    skillsNeeded: ["Clinical knowledge", "Manual dexterity", "Stamina", "Decision making", "Teamwork"],
    specialisations: [
      { title: "General Surgeon", shortDescription: "Broad surgical practice across abdomen, breast and trauma.", typicalEntryRoute: "Medicine + core surgical training + specialty training", salaryRange: "£60,000 - £180,000" },
      { title: "Orthopaedic Surgeon", shortDescription: "Operate on bones, joints, ligaments and tendons.", typicalEntryRoute: "Medicine + orthopaedic specialty training", salaryRange: "£70,000 - £250,000" },
    ],
    similarCareers: ["Doctor"],
    higherProgressionCareers: [],
    entryLevelAlternatives: ["Doctor", "Nurse"],
  },
  {
    name: "Psychologist",
    category: "Healthcare and Care",
    shortDescription: "Use psychology to help people with mental health, behaviour, learning or performance.",
    whyItFits: "You're curious about how people think and behave and want to help them through evidence-based methods.",
    startingSalary: "£28,000 - £38,000 (assistant psychologist)",
    midSalary: "£45,000 - £65,000 (Band 7-8a)",
    seniorSalary: "£70,000 - £110,000+ (consultant / private)",
    timeToEntry: "6 - 8 years",
    degreeRequired: "Yes (BPS-accredited degree + doctorate for clinical)",
    difficulty: "High",
    competitionLevel: "Extremely High",
    workEnvironment: "NHS clinic, schools, prisons, private practice",
    stability: "Strong demand; clinical training places very competitive",
    weights: { people: 3, structure: 2, creativity: 1, income: 0, freedom: 0 },
    skillsNeeded: ["Active listening", "Research methods", "CBT or other modalities", "Report writing", "Empathy"],
    specialisations: [
      { title: "Clinical Psychologist", shortDescription: "Treat mental health conditions in NHS or private practice.", typicalEntryRoute: "Psychology degree + assistant psych + DClinPsy doctorate", salaryRange: "£45,000 - £100,000" },
      { title: "Educational Psychologist", shortDescription: "Support children and young people in education settings.", typicalEntryRoute: "Psych degree + DEdPsy doctorate", salaryRange: "£42,000 - £80,000" },
    ],
    similarCareers: ["Counsellor", "Social Worker"],
    higherProgressionCareers: [],
    entryLevelAlternatives: ["Counsellor", "Healthcare Assistant"],
  },
  {
    name: "Occupational Therapist",
    category: "Healthcare and Care",
    shortDescription: "Help people recover or adapt the daily activities they need or want to do after illness, injury or disability.",
    whyItFits: "You're practical, empathetic and want hands-on healthcare work that improves day-to-day life.",
    startingSalary: "£28,000 - £35,000",
    midSalary: "£37,000 - £48,000",
    seniorSalary: "£50,000 - £70,000+",
    timeToEntry: "3 - 4 years",
    degreeRequired: "Yes (HCPC-approved OT degree)",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Hospital, community, care home, school",
    stability: "Strong demand across NHS and private",
    weights: { people: 3, structure: 2, creativity: 1, income: 0, freedom: 0 },
    skillsNeeded: ["Assessment", "Communication", "Problem solving", "Patience", "Goal setting"],
    specialisations: [],
    similarCareers: ["Physiotherapist", "Nurse"],
    higherProgressionCareers: [],
    entryLevelAlternatives: ["Healthcare Assistant"],
  },
  {
    name: "Radiographer",
    category: "Healthcare and Care",
    shortDescription: "Use imaging like X-ray, MRI and CT to diagnose, or radiotherapy to treat cancer.",
    whyItFits: "You like science and tech, want patient contact but prefer focused, technical work to long ward shifts.",
    startingSalary: "£28,000 - £35,000",
    midSalary: "£37,000 - £48,000",
    seniorSalary: "£50,000 - £75,000+",
    timeToEntry: "3 years",
    degreeRequired: "Yes (HCPC-approved radiography degree)",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Hospital imaging or radiotherapy department",
    stability: "Strong - chronic shortage in UK",
    weights: { structure: 3, people: 2, income: 1, creativity: 0, freedom: 0 },
    skillsNeeded: ["Imaging tech", "Anatomy", "Patient care", "Radiation safety", "Attention to detail"],
    specialisations: [
      { title: "Diagnostic Radiographer", shortDescription: "Capture imaging used for diagnosis (X-ray, MRI, CT).", typicalEntryRoute: "Diagnostic radiography degree", salaryRange: "£28,000 - £55,000" },
      { title: "Therapeutic Radiographer", shortDescription: "Plan and deliver radiotherapy for cancer patients.", typicalEntryRoute: "Therapeutic radiography degree", salaryRange: "£28,000 - £55,000" },
    ],
    similarCareers: ["Nurse", "Physiotherapist"],
    higherProgressionCareers: [],
    entryLevelAlternatives: ["Healthcare Assistant"],
  },
  {
    name: "Nutritionist",
    category: "Healthcare and Care",
    shortDescription: "Advise people on food, diet and nutrition - in clinics, sports, public health or private practice.",
    whyItFits: "You're interested in health, food and habits and want a science-led role that can flex into self-employment.",
    startingSalary: "£22,000 - £30,000",
    midSalary: "£32,000 - £45,000",
    seniorSalary: "£50,000 - £80,000+ (private/clinics)",
    timeToEntry: "3 - 5 Years",
    degreeRequired: "Yes (AfN or BANT-accredited course recommended)",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Clinic, gym, public health setting or private practice",
    stability: "Solid - growing private market",
    weights: { people: 2, freedom: 2, creativity: 1, structure: 1, income: 0 },
    skillsNeeded: ["Nutrition science", "Behaviour change", "Listening", "Meal planning", "Marketing yourself"],
    specialisations: [],
    selfEmployable: true,
    similarCareers: ["Online Coach", "Personal Trainer", "Counsellor"],
    higherProgressionCareers: [],
    entryLevelAlternatives: ["Personal Trainer"],
  },
  {
    name: "Forensic Scientist",
    category: "Education, Science and Research",
    shortDescription: "Analyse evidence from crime scenes - DNA, drugs, fibres, digital traces - to support investigations.",
    whyItFits: "You like detail, methodical lab work and the idea of contributing to criminal justice without being on the front line.",
    startingSalary: "£22,000 - £30,000",
    midSalary: "£32,000 - £45,000",
    seniorSalary: "£50,000 - £75,000+",
    timeToEntry: "3 - 5 Years",
    degreeRequired: "Yes (forensic science or related)",
    difficulty: "High",
    competitionLevel: "Extremely High",
    workEnvironment: "Lab and occasional crime scene visits",
    stability: "Limited number of roles - mostly private contractors and police labs",
    weights: { structure: 3, creativity: 0, income: 0, people: -1, freedom: -1 },
    skillsNeeded: ["Lab technique", "Attention to detail", "Report writing", "Court testimony", "Chain of custody discipline"],
    specialisations: [
      { title: "DNA Analyst", shortDescription: "Process DNA evidence for criminal cases.", typicalEntryRoute: "Forensic science degree + lab role", salaryRange: "£25,000 - £55,000" },
      { title: "Digital Forensics Investigator", shortDescription: "Recover and analyse evidence from phones, computers and cloud services.", typicalEntryRoute: "CS or digital forensics degree + certs", salaryRange: "£30,000 - £75,000" },
    ],
    similarCareers: ["Laboratory Technician", "Cybersecurity Analyst"],
    higherProgressionCareers: [],
    entryLevelAlternatives: ["Laboratory Technician"],
  },
  {
    name: "Chemist",
    category: "Education, Science and Research",
    shortDescription: "Research, develop and analyse chemicals and materials - in pharma, energy, food or manufacturing.",
    whyItFits: "You love chemistry and want a science career inside industry rather than academia.",
    startingSalary: "£24,000 - £32,000",
    midSalary: "£35,000 - £55,000",
    seniorSalary: "£60,000 - £95,000+",
    timeToEntry: "3 - 5 Years",
    degreeRequired: "Yes (chemistry degree, often with MSc/PhD)",
    difficulty: "High",
    competitionLevel: "Moderate",
    workEnvironment: "Lab and office, regulated industries",
    stability: "Strong in pharma, energy and materials",
    weights: { structure: 3, creativity: 1, income: 1, people: 0, freedom: 0 },
    skillsNeeded: ["Lab technique", "Analytical instruments", "Statistics", "Health and safety", "Report writing"],
    specialisations: [
      { title: "Analytical Chemist", shortDescription: "Test composition and quality of materials and products.", typicalEntryRoute: "Chemistry degree + QC/QA role", salaryRange: "£26,000 - £65,000" },
      { title: "Medicinal Chemist", shortDescription: "Design and synthesise new drug molecules.", typicalEntryRoute: "Chem degree + PhD + pharma role", salaryRange: "£40,000 - £100,000" },
    ],
    similarCareers: ["Research Scientist", "Laboratory Technician"],
    higherProgressionCareers: ["Research Scientist"],
    entryLevelAlternatives: ["Laboratory Technician"],
  },
  // Skilled Trades
  {
    name: "HVAC Technician",
    category: "Skilled Trades",
    shortDescription: "Install, service and repair heating, ventilation, air-con and refrigeration systems.",
    whyItFits: "You like practical work, want a trade with strong demand and don't mind being on the road and on roofs.",
    startingSalary: "£22,000 - £30,000 (apprentice/junior)",
    midSalary: "£32,000 - £45,000",
    seniorSalary: "£50,000 - £75,000+ (or self-employed)",
    timeToEntry: "2 - 4 years (apprenticeship + F-Gas)",
    degreeRequired: "Not required - apprenticeship route",
    difficulty: "Medium",
    competitionLevel: "Low",
    workEnvironment: "On-site at commercial and domestic premises",
    stability: "Strong - demand growing with cooling and heat pump rollout",
    weights: { structure: 2, income: 2, freedom: 2, people: 0, creativity: 0 },
    skillsNeeded: ["Refrigeration cycle", "Electrical basics", "Pipework", "F-Gas certification", "Fault finding"],
    specialisations: [
      { title: "Commercial Air-Con Engineer", shortDescription: "Service and install commercial AC and chillers.", typicalEntryRoute: "Apprenticeship + F-Gas + on-the-job", salaryRange: "£30,000 - £65,000" },
      { title: "Heat Pump Installer", shortDescription: "Install air- and ground-source heat pumps for homes and businesses.", typicalEntryRoute: "Plumbing/heating background + heat pump training (e.g. MCS)", salaryRange: "£32,000 - £70,000" },
    ],
    selfEmployable: true,
    similarCareers: ["Heating and Gas Engineer", "Plumber", "Electrician"],
    higherProgressionCareers: [],
    entryLevelAlternatives: ["Maintenance Engineer"],
  },
  // Modern / Internet Careers
  {
    name: "Course Creator",
    category: "Self-Employed and Business Owner",
    shortDescription: "Build and sell online courses around a clear transformation - the highest-margin digital product.",
    whyItFits: "You have hard-won expertise, can teach clearly and want to package it once and sell it many times.",
    startingSalary: "£0 - £10,000 (year one)",
    midSalary: "£25,000 - £80,000",
    seniorSalary: "£100,000 - £400,000+",
    timeToEntry: "3 - 12 months to first launch",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "High",
    workEnvironment: "Fully remote, home-based",
    stability: "Strong once 2-3 launches in - very low at the start",
    weights: { freedom: 3, creativity: 2, income: 2, structure: 0, people: 0 },
    skillsNeeded: ["Niche expertise", "Audience building", "Curriculum design", "Sales pages", "Email marketing"],
    specialisations: [
      { title: "Cohort Course Creator", shortDescription: "Live group cohorts at a premium price - higher margins, more energy.", typicalEntryRoute: "Validate with a small cohort first", salaryRange: "£30,000 - £300,000" },
      { title: "Self-Paced Course Creator", shortDescription: "Evergreen courses sold on autopilot with funnels and ads.", typicalEntryRoute: "Build audience + run paid traffic", salaryRange: "£20,000 - £400,000" },
    ],
    selfEmploymentOnly: true,
    similarCareers: ["Online Coach", "Digital Product Creator", "Content Creator"],
    higherProgressionCareers: ["Digital Product Creator"],
    entryLevelAlternatives: ["Online Coach", "Private Tutor"],
    soloMeta: {
      startupCostGBP: "£200 - £2,000",
      timeToFirstClient: "3 - 6 months",
      monthOneIncome: "£0 - £500",
      yearOneIncome: "£3,000 - £40,000",
      scaledIncome: "£60,000 - £400,000+",
      commonPitfalls: [
        "Building a course before validating with a paid pilot cohort",
        "Pricing at £49 instead of £499 - small ticket sizes burn you out",
        "Selling on social only - no email list = no compounding asset",
        "Single launch and walk away - launch the same course 3-4 times a year",
        "No clear outcome - 'learn marketing' beats nothing, but 'add £5k MRR in 90 days' sells",
      ],
      firstFiveClients: [
        "Pick one painful, expensive problem you have credibility solving",
        "Run a £200-500 paid pilot with 5-10 people via your network",
        "Document the process and turn it into a repeatable curriculum",
        "Build a simple landing page + 2-week launch email sequence",
        "Run the launch, gather case studies, raise the price next time",
      ],
      toolsAndCosts: [
        "Course platform (Teachable, Kajabi, Podia): £30-100/month",
        "Email platform (ConvertKit, Beehiiv): free up to 1,000 subs",
        "Stripe for payments",
        "Loom + Zoom for delivery",
        "Canva Pro for assets: £11/month",
      ],
      legalBasics: [
        "Register self-employed with HMRC and get your UTR",
        "T&Cs and refund policy on the sales page (Stripe expects it)",
        "GDPR for email lists and student data",
        "VAT mandatory at £90,000 turnover - hits fast on big launches",
        "No medical/financial claims unless you're qualified to make them",
      ],
    },
  },
  {
    name: "Affiliate Marketer",
    category: "Self-Employed and Business Owner",
    shortDescription: "Earn commission by recommending other people's products via content, email or paid traffic.",
    whyItFits: "You like marketing, content and SEO and want a business with no inventory, support or fulfilment.",
    startingSalary: "£0 - £8,000 (year one - usually unpaid time)",
    midSalary: "£25,000 - £80,000",
    seniorSalary: "£100,000 - £500,000+ (top operators)",
    timeToEntry: "6 - 18 months to meaningful income",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Extremely High",
    workEnvironment: "Fully remote",
    stability: "Volatile - one Google update can wipe a site",
    weights: { freedom: 3, creativity: 1, income: 1, structure: -1, people: -1 },
    skillsNeeded: ["SEO or paid ads", "Content writing", "Conversion optimisation", "Niche research", "Disclosure rules"],
    specialisations: [
      { title: "SEO Affiliate Site Owner", shortDescription: "Build content sites that rank in Google for buyer-intent terms.", typicalEntryRoute: "Pick a niche, write 50-100 articles over 12-18 months", salaryRange: "£10,000 - £200,000" },
      { title: "Paid Affiliate Marketer", shortDescription: "Run paid ads to affiliate offers, scaling profitable campaigns.", typicalEntryRoute: "Learn ad platforms + start with low-cost niches", salaryRange: "£20,000 - £500,000" },
    ],
    selfEmploymentOnly: true,
    similarCareers: ["Content Creator", "SEO Specialist", "Etsy / Online Shop Owner"],
    higherProgressionCareers: ["Founder / Entrepreneur"],
    entryLevelAlternatives: ["Content Creator"],
    soloMeta: {
      startupCostGBP: "£100 - £1,000",
      timeToFirstClient: "3 - 12 months to first commissions",
      monthOneIncome: "£0",
      yearOneIncome: "£0 - £8,000",
      scaledIncome: "£40,000 - £300,000+",
      commonPitfalls: [
        "Building on a single platform you don't own (one ban = zero income)",
        "Picking ultra-competitive niches with thin commission",
        "Hiding affiliate disclosure - illegal in the UK and breaks Amazon/Google rules",
        "No email list - all traffic is rented",
        "Quitting at month 9 - SEO compounds in months 12-24",
      ],
      firstFiveClients: [
        "Pick a niche with high-intent keywords and decent commission rates",
        "Set up a basic site (WordPress or static) and a tracking spreadsheet",
        "Publish 20 buyer-intent articles ('best X for Y') in 90 days",
        "Sign up to relevant affiliate programmes (Amazon, ShareASale, niche-specific)",
        "Build an email list from day one and pitch deals weekly",
      ],
      toolsAndCosts: [
        "Domain + hosting: £100/year",
        "WordPress + theme: free or £100 one-off",
        "Keyword tools (Ahrefs, Semrush): £100/month or free alternatives",
        "Email platform (Beehiiv, ConvertKit): free up to 1,000 subs",
        "Tracking sheet or affiliate dashboard",
      ],
      legalBasics: [
        "Register self-employed with HMRC and get your UTR",
        "ASA + CAP rules: clear affiliate disclosure on every page and post",
        "Amazon Associates: strict rules - one breach = permanent ban",
        "GDPR: cookie banner + email list compliance",
        "VAT mandatory at £90,000 turnover",
      ],
    },
  },
  {
    name: "Amazon FBA Seller",
    category: "Self-Employed and Business Owner",
    shortDescription: "Source or create products and sell them on Amazon - they handle storage, shipping and returns.",
    whyItFits: "You're entrepreneurial, want to sell physical products and like the idea of leveraging Amazon's traffic.",
    startingSalary: "£0 - £15,000 (year one - mostly reinvested)",
    midSalary: "£30,000 - £80,000 net",
    seniorSalary: "£100,000 - £500,000+ net (or 6-7 figure exits)",
    timeToEntry: "3 - 9 months to first sale",
    degreeRequired: "Not required",
    difficulty: "High",
    competitionLevel: "Extremely High",
    workEnvironment: "Mostly remote + occasional supplier/warehouse trips",
    stability: "Volatile - account suspensions and competition risk",
    weights: { freedom: 2, structure: 2, income: 2, creativity: 1, people: -1 },
    skillsNeeded: ["Product research", "Sourcing (Alibaba)", "Amazon SEO", "PPC ads", "Cashflow management"],
    specialisations: [
      { title: "Private Label Seller", shortDescription: "Source generic products and sell under your own brand.", typicalEntryRoute: "Research + sourcing trip / Alibaba + £3-10k starting capital", salaryRange: "£20,000 - £300,000" },
      { title: "Wholesale FBA Seller", shortDescription: "Buy branded stock from distributors and resell on Amazon.", typicalEntryRoute: "Open accounts with brand distributors + repricer software", salaryRange: "£20,000 - £200,000" },
    ],
    selfEmploymentOnly: true,
    similarCareers: ["Etsy / Online Shop Owner", "Founder / Entrepreneur", "E-commerce Manager"],
    higherProgressionCareers: ["Founder / Entrepreneur"],
    entryLevelAlternatives: ["Etsy / Online Shop Owner"],
    soloMeta: {
      startupCostGBP: "£3,000 - £15,000",
      timeToFirstClient: "2 - 6 months to first sale",
      monthOneIncome: "£0 - £1,000",
      yearOneIncome: "£0 - £30,000 (mostly reinvested in stock)",
      scaledIncome: "£50,000 - £400,000+",
      commonPitfalls: [
        "Picking a saturated product with razor-thin margins",
        "Underestimating PPC ad spend - it eats early profit",
        "No backup supplier - one factory issue can wipe Q4",
        "Account suspension with no Plan B sales channel",
        "Treating gross revenue as profit - Amazon fees are 30-40%",
      ],
      firstFiveClients: [
        "Research products with Helium 10 or Jungle Scout - target £15-50 price, low review count",
        "Source via Alibaba, get 3 quotes, order samples",
        "Set up Amazon Seller Central + brand registry",
        "Launch with PPC + a small giveaway campaign for early reviews",
        "Reinvest profits into stock and ads for 12 months",
      ],
      toolsAndCosts: [
        "Initial inventory: £2,000 - £10,000",
        "Helium 10 / Jungle Scout: £30-100/month",
        "Amazon Seller account: £25/month + 15% referral fee",
        "Product photos: £200-500",
        "PPC budget: £10-50/day to start",
      ],
      legalBasics: [
        "Register as a sole trader or limited company with HMRC",
        "VAT registered from day one if importing (UK low-value threshold rules)",
        "Product safety: CE/UKCA marking, age warnings, packaging compliance",
        "Trademark your brand before scaling - hijackers will copy you",
        "Amazon ToS: read it - account terminations are appealable but slow",
      ],
    },
  },
  {
    name: "Dropshipping Business Owner",
    category: "Self-Employed and Business Owner",
    shortDescription: "Run a Shopify store where suppliers ship orders directly to customers - you focus on marketing.",
    whyItFits: "You want to test ecommerce ideas with low capital and aren't precious about owning physical inventory.",
    startingSalary: "£0 - £10,000 (year one - mostly reinvested in ads)",
    midSalary: "£25,000 - £70,000 net",
    seniorSalary: "£100,000 - £500,000+ net (top operators)",
    timeToEntry: "1 - 6 months to first sale",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Extremely High",
    workEnvironment: "Fully remote",
    stability: "Volatile - relies entirely on paid ads and supplier reliability",
    weights: { freedom: 3, income: 1, creativity: 1, structure: -1, people: -1 },
    skillsNeeded: ["Shopify", "Facebook / TikTok ads", "Product research", "Customer service basics", "Cashflow"],
    specialisations: [
      { title: "One-Product Store Operator", shortDescription: "Single-product Shopify store driven by paid ads.", typicalEntryRoute: "Shopify + ad creative + £500-2,000 ad budget", salaryRange: "£10,000 - £200,000" },
      { title: "Print on Demand Operator", shortDescription: "Sell custom-printed apparel, mugs and homewares with no inventory.", typicalEntryRoute: "Shopify + Printful/Printify + niche audience", salaryRange: "£8,000 - £100,000" },
    ],
    selfEmploymentOnly: true,
    similarCareers: ["Amazon FBA Seller", "Etsy / Online Shop Owner", "E-commerce Manager"],
    higherProgressionCareers: ["E-commerce Manager", "Founder / Entrepreneur"],
    entryLevelAlternatives: ["Etsy / Online Shop Owner"],
    soloMeta: {
      startupCostGBP: "£500 - £3,000",
      timeToFirstClient: "1 - 3 months",
      monthOneIncome: "£0 - £2,000",
      yearOneIncome: "£0 - £20,000 net",
      scaledIncome: "£40,000 - £400,000+",
      commonPitfalls: [
        "Selling generic AliExpress products at saturated price points",
        "Burning ad spend before testing creatives properly",
        "Long shipping times destroying customer trust",
        "Ignoring chargebacks and complaints until Stripe freezes the account",
        "Not understanding margin - £30 sale can leave £2 after ads, supplier and refunds",
      ],
      firstFiveClients: [
        "Pick a niche where you understand the customer (not 'everyone')",
        "Find a reliable supplier - ideally UK or EU for shipping speed",
        "Build a clean Shopify store with strong product photography",
        "Test 3-5 ad creatives with £200-500 to find a winner",
        "Scale spend on the winning creative; reinvest profit weekly",
      ],
      toolsAndCosts: [
        "Shopify: £25-79/month",
        "Theme + apps: £100-300 one-off",
        "Ad spend: £20-100/day",
        "Product samples: £50-200",
        "Customer support tool (Gorgias, Help Scout): £20-50/month",
      ],
      legalBasics: [
        "Register self-employed or limited company with HMRC",
        "Distance Selling Regulations: 14-day cancellation right",
        "VAT mandatory at £90,000 turnover; EU sales trigger IOSS at €150",
        "Trading Standards: accurate descriptions, no false health claims",
        "Stripe + PayPal: keep dispute rate under 1% or accounts get frozen",
      ],
    },
  },
  {
    name: "Community Manager",
    category: "Marketing and Media",
    shortDescription: "Build, grow and look after online communities for brands - on Discord, Slack, forums or social.",
    whyItFits: "You're sociable online, love a niche audience and want a role that mixes content, support and events.",
    startingSalary: "£24,000 - £32,000",
    midSalary: "£35,000 - £55,000",
    seniorSalary: "£60,000 - £90,000+",
    timeToEntry: "1 - 3 years",
    degreeRequired: "Not required",
    difficulty: "Medium",
    competitionLevel: "Moderate",
    workEnvironment: "Mostly remote, very online",
    stability: "Strong demand from creator-led brands and SaaS",
    weights: { people: 3, creativity: 2, freedom: 1, structure: 1, income: 0 },
    skillsNeeded: ["Writing", "Empathy", "Moderation", "Events", "Analytics"],
    specialisations: [
      { title: "SaaS Community Manager", shortDescription: "Run user communities for software products.", typicalEntryRoute: "Support or marketing role + community specialism", salaryRange: "£30,000 - £75,000" },
      { title: "Creator Community Manager", shortDescription: "Run communities and memberships for creators and educators.", typicalEntryRoute: "VA / ops background + creator world experience", salaryRange: "£28,000 - £65,000" },
    ],
    selfEmployable: true,
    similarCareers: ["Social Media Manager", "Customer Service Advisor"],
    higherProgressionCareers: ["Brand Manager", "Marketing Executive"],
    entryLevelAlternatives: ["Customer Service Advisor", "Social Media Manager"],
  },
  // ---------------- 2026 Expansion (144 careers, 13 new/expanded categories) ----------------
  ...CAREER_EXPANSION_SEEDS,
  ...CAREER_EXPANSION_V2_SEEDS,
];



// =============================================================
// Scoring + Career generation
// =============================================================

function scoreToLabel(score: number): MatchLabel {
  if (score >= 75) return "Strong match";
  if (score >= 60) return "Good fit";
  return "Worth exploring";
}

function applyWeights(weights: Weights, traits: TraitScores): number {
  let score = 50; // base
  (Object.entries(weights) as [TraitKey, number][]).forEach(([trait, weight]) => {
    const t = (traits as unknown as Record<string, number>)[trait] ?? 50;
    // (t - 50) so neutral traits add 0; weight scaled so realistic spread
    score += (t - 50) * weight * 0.18;
  });
  return score;
}

/**
 * Map of dropped duplicate seed IDs -> canonical (first-occurrence) seed ID.
 * Built once at module load. Used by `resolveCareerId` so any saved
 * `selected_career_id` that referenced a duplicate still resolves to the
 * canonical career after deduplication.
 */
const duplicateIdRedirects: Map<number, number> = (() => {
  const firstSeenIdByName = new Map<string, number>();
  const redirects = new Map<number, number>();
  careerSeeds.forEach((seed, idx) => {
    const id = idx + 1;
    const existing = firstSeenIdByName.get(seed.name);
    if (existing === undefined) {
      firstSeenIdByName.set(seed.name, id);
    } else {
      redirects.set(id, existing);
    }
  });
  return redirects;
})();

/** Resolves any (possibly stale) career id to its canonical id. */
export function resolveCareerId(id: number): number {
  return duplicateIdRedirects.get(id) ?? id;
}

/** Diagnostics: list of merged duplicates. Useful for admin / audits. */
export function getDuplicateRedirects(): Array<{ from: number; to: number; name: string }> {
  return Array.from(duplicateIdRedirects.entries()).map(([from, to]) => ({
    from,
    to,
    name: careerSeeds[from - 1]?.name ?? "(unknown)",
  }));
}

export const generateCareers = (traits: TraitScores): Career[] => {
  const seenNames = new Set<string>();
  return careerSeeds
    .map((seed, idx): Career | null => {
      // Dedupe by name, keeping first occurrence. IDs preserve original
      // index so canonical careers retain stable IDs across the dedupe.
      if (seenNames.has(seed.name)) return null;
      seenNames.add(seed.name);

      let score = applyWeights(seed.weights, traits);

      // Tiny deterministic jitter so identical-weight roles don't tie
      const jitter = ((seed.name.charCodeAt(0) + seed.name.length) % 5) - 2;
      score += jitter * 0.4;

      // Cap at 88 (spec: never above 90), floor at 45 (spec: lowest band)
      score = Math.min(88, Math.max(45, Math.round(score)));

      // Build legacy stepByStep from the new fields so existing UI still works
      const stepByStep = seed.selfEmploymentOnly
        ? [
            `Decide who your first 5 paying clients will realistically be (be specific - "dog owners in [your town]" not "everyone").`,
            `Register as self-employed with HMRC and get your UTR. Set up a separate bank account.`,
            `Get the kit, insurance, and any qualifications/licences you actually need to start - no more.`,
            `Build the simplest version of your offer (one-page site or Insta + price list) and put it in front of those 5 people.`,
            `Land your first paying job, deliver well, and ask for a review or referral. Repeat.`,
          ]
        : [
            `Look up ${seed.name} roles on LinkedIn or Indeed and read 5 real job ads`,
            `Talk to someone already working as a ${seed.name} - even a 15-minute call helps`,
            `Find one beginner course or qualification used by people in this role`,
            `Build one small piece of evidence you've explored this (project, shadowing, short course)`,
            `Apply to one entry-level role or related opportunity within the next month`,
          ];

      return {
        id: idx + 1,
        name: seed.name,
        category: seed.category,
        shortDescription: seed.shortDescription,
        whyItFits: seed.whyItFits,
        matchPercentage: score,
        matchLabel: scoreToLabel(score),
        stepByStep,
        skillsNeeded: seed.skillsNeeded,
        incomePotential: `${seed.startingSalary} - rising to ${seed.seniorSalary}`,
        risks: `Difficulty: ${seed.difficulty}. Competition: ${seed.competitionLevel}. ${seed.stability}.`,
        startingSalary: seed.startingSalary,
        midSalary: seed.midSalary,
        seniorSalary: seed.seniorSalary,
        timeToEntry: seed.timeToEntry,
        degreeRequired: seed.degreeRequired,
        difficulty: seed.difficulty,
        competitionLevel: seed.competitionLevel,
        workEnvironment: seed.workEnvironment,
        stability: seed.stability,
        specialisations: seed.specialisations,
        isAlternativePath: seed.isAlternativePath,
        selfEmployable: seed.selfEmployable,
        selfEmploymentOnly: seed.selfEmploymentOnly,
        soloMeta: seed.soloMeta,
        traitWeights: seed.weights,
        similarCareers: seed.similarCareers,
        higherProgressionCareers: seed.higherProgressionCareers,
        entryLevelAlternatives: seed.entryLevelAlternatives,
      };
    })
    .filter((c): c is Career => c !== null)
    .sort((a, b) => b.matchPercentage - a.matchPercentage);
};
